import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import '@/App.css';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import html2canvas from 'html2canvas';
import { FileDown, FileText, Code, Image, Palette, Building2, Trees, Battery, Undo, ChevronUp, ChevronDown, Upload, Lock, LogOut, Settings, Briefcase, Workflow, Mail, Menu, UserPlus, Activity, ShieldCheck, Calculator, Wrench, AlertTriangle, Scale, Handshake, FileStack } from 'lucide-react';
import { EnPowerLogoText } from './components/EnPowerLogo';
// Freiflächen PV (EN:POWER × ILOS)
import { CoverPageStatic } from './components/sections/CoverPageStatic';
import { ContentPage2Static } from './components/sections/ContentPage2Static';
import { ContentPage3Static } from './components/sections/ContentPage3Static';
import { ContentPage4Static } from './components/sections/ContentPage4Static';
// Dach PV (EN:POWER)
import { CoverPageDach } from './components/sections/dach/CoverPageDach';
import { ContentPage2Dach } from './components/sections/dach/ContentPage2Dach';
import { ContentPage3Dach } from './components/sections/dach/ContentPage3Dach';
import { ContentPage4Dach } from './components/sections/dach/ContentPage4Dach';
import { DACH_DATA } from './components/proposal/data/dachData';
// Schwarmspeicher (EN:POWER)
import { CoverPageSpeicher } from './components/sections/speicher/CoverPageSpeicher';
import { ContentPage2Speicher } from './components/sections/speicher/ContentPage2Speicher';
import { ContentPage3Speicher } from './components/sections/speicher/ContentPage3Speicher';
import { ContentPage4Speicher } from './components/sections/speicher/ContentPage4Speicher';
import { EnergyFlyerStudio } from './components/sections/flyer/EnergyFlyerStudio';
import {
  ProjectExposeCover,
  ProjectExposePage2,
  ProjectExposePage3,
  ProjectExposePage4,
} from './components/sections/expose/ProjectExposePages';
import {
  VertriebsPraesentation,
  VERTRIEB_ACCENT_IMAGE_PATHS,
  VERTRIEB_LOGO_ON_DARK_PATH,
  VERTRIEB_LOGO_PATH,
  VERTRIEB_PRESENTATION_SLIDE_PATHS,
} from './components/sections/vertrieb/VertriebsPraesentation';
import { DesignSystemPage } from './components/DesignSystemPage';
import { VermittlervertragStudio } from './components/contracts/VermittlervertragStudio';
import VertragsStudio from './components/contracts/VertragsStudio';
import PasswordResetFlow, { AUTH_FORGOT_PASSWORD_PATH, getPasswordAuthRoute } from './components/auth/PasswordResetFlow';
import ToolSettingsPage from './components/settings/ToolSettingsPage';
import AutomationOverviewPage from './components/settings/AutomationOverviewPage';
import ZugangsanfragenPage from './components/settings/ZugangsanfragenPage';
import MailKontenPage from './components/settings/MailKontenPage';
import VertragsAutomationStudio from './components/contracts/VertragsAutomationStudio';
import AbwaegenStudio from './components/contracts/AbwaegenStudio';
import DokumentenAssistent from './components/contracts/dokumentenAssistent/DokumentenAssistent';
import VertragsAmPendingBoard from './components/contracts/VertragsAmPendingBoard';
import FfpvFlyerBoard from './components/flyer/FfpvFlyerBoard';
import FfpvTeaserBoard from './components/flyer/FfpvTeaserBoard';
import VertragsAutomationsPage from './components/contracts/VertragsAutomationsPage';
import ClauseManager from './components/contracts/ClauseManager';
import ExposeAutomationStudio from './components/exposes/ExposeAutomationStudio';
import CockpitExposeList from './components/exposes/CockpitExposeList';
import OfferGeneratorPage from './components/OfferGeneratorPage';
import StandardDocumentsPage from './components/StandardDocumentsPage';
import StandardDocumentsTemplateStudio from './components/StandardDocumentsTemplateStudio';
import EmailSignaturePage from './components/onboarding/EmailSignaturePage';
import MittwaldMailGenerator from './components/MittwaldMailGenerator';
import BitrixUserCreator from './components/BitrixUserCreator';
import EegRechner from './components/EegRechner';
import { resolveContractKeyFromPendingDeal } from './lib/vertragErstellen';
import { pruefeAktualitaet as pruefeEegAktualitaet } from './lib/eegVerguetung';
import { abmelden, getSupabaseClient } from './lib/supabaseClient';
import {
  VERBINDUNG_UNBEKANNT,
  baueVerbindungsadresse,
  leiteZurVerbindungsseite,
  pruefeBitrixVerbindung,
} from './lib/bitrixVerbindung';
import {
  BITRIX_SITZUNG_KOPF, OHNE_DEAL_HINWEIS, adresseImTool, anmeldeHinweis, leseEinbettung, nimmBitrixAnmeldung,
  vergissBitrixSitzung,
} from './lib/bitrixEinbettung';
import { buildExposePlaceholderData } from './lib/exposeAutomation';

// Die Handy-Oberfläche wird nur geladen, wenn sie gebraucht wird - der
// Desktop soll ihren Code nicht mitschleppen.
const MobilHuelle = React.lazy(() => import('./components/mobil/MobilHuelle'));

const IS_LOCALHOST = typeof window !== 'undefined' && /localhost|127\.0\.0\.1/i.test(window.location.hostname);
const DEFAULT_BACKEND_URL = IS_LOCALHOST
  ? 'http://localhost:8001'
  : 'https://backend-pi-red-58.vercel.app';
const BACKEND_URL = String(process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND_URL).trim().replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;
const LAYOUT_API = IS_LOCALHOST ? API : '/api';

const PROPOSAL_DATA = {
  kundenname: 'Mustermann Agrar GmbH',
  kundenadresse: 'Feldweg 7, 34576 Musterregion, Hessen',
  angebotsnummer: 'FF-2025-0118',
  kundennummer: 'KD-2118',
  datum: '15. Januar 2025',
  gueltig_bis: '15. März 2025',
  flaeche_ha: '25',
  mwp_leistung: '25',
  pacht_stufe1: '3.000',
  pacht_stufe2: '3.500',
  pacht_stufe3: '4.000',
  gesamt_pacht_30j: '2.625.000',
  laufzeit: '30',
  ansprechpartner: 'Max Mustermann',
  telefonnummer: '+49 (0) 123 456 789',
  email: 'info@enpower.de',
  placeholder_mode: false,
};

const SPEICHER_PROPOSAL_DATA = {
  kundenname: 'Mustermann Gewerbe GmbH',
  kundenadresse: 'Industriestraße 15, 45678 Musterstadt',
  angebotsnummer: 'SP-2025-0203',
  kundennummer: 'KD-3203',
  datum: '15. Januar 2025',
  gueltig_bis: '15. März 2025',
  leistung_kw: '250',
  einheiten: '2 x 125',
  stellflaeche: '10',
  laufzeit: '20',
  einmalig: '20.000',
  jaehrlich: '1.500',
  gesamt_jaehrlich: '30.000',
  vl_prozent: '5',
  ansprechpartner: 'Max Mustermann',
  telefonnummer: '+49 (0) 123 456 789',
  email: 'info@enpower.de',
};

const EXPOSE_PROPOSAL_DATA = {
  projektname: 'Dachverpachtung Muster-Logistikzentrum',
  turnkeyProjektname: 'Solaranlage Muster-Logistikzentrum',
  kundenname: 'Muster-Logistik GmbH',
  kundenadresse: 'Industriestraße 42, 06484 Quedlinburg, Sachsen-Anhalt',
  angebotsnummer: 'PR-2025-0019',
  turnkeyAngebotsnummer: 'TK-2025-0042',
  kundennummer: 'KD-0001',
  datum: '15. Juni 2025',
  gueltig_bis: '15. Juli 2025',
  flaeche_ha: '0,42',
  mwp_leistung: '0,487',
  dachflaeche: '4.200',
  belegbare_flaeche: '3.640',
  kwp_leistung: '487',
  laufzeit: '25+',
  spezifischertrag: '950',
  jahresertrag: '462.650',
  kaufpreis: '385.000',
  irr: '7,8 %',
  payback: '10,4 Jahre',
  projektstatus: 'Baufreigabe erteilt',
  inbetriebnahme: 'Q3 2025',
  netzstatus: 'Erteilt',
  baurechtstatus: 'Nicht erforderlich',
  projectImage: '',
  ansprechpartner: 'Randolf Kirsten',
  position: 'Projektentwicklung',
  telefonnummer: '+49 151 1234 5678',
  email: 'r.kirsten@enpower.de',
  website: 'www.enpower.de',
};

const TYPE_LABELS = {
  dach: 'Dach-PV',
  sanierung: 'Sanierung',
  freiflaeche: 'Freiflächen-PV',
  speicher: 'Kleinspeicher',
  bess: 'Großspeicher',
  'expose-projektverkauf': 'Exposé Projektrechte',
  'expose-turnkey': 'Exposé Turnkey',
  flyer: 'Flyer',
  'ffpv-flyer': 'FFPV Flyer',
  'ffpv-teaser': 'Projektteaser FFPV',
  vertrieb: 'Vertriebspräsentation',
  generator: 'Offergenerator',
  vermittlervertrag: 'Vermittlervertrag',
  vertraege: 'Verträge - Vorlagen',
  'exposes-automation': 'Exposés erstellen',
  'exposes-cockpit': 'Aus Projekt-Cockpit',
  'vertraege-am': 'Verträge - Erstellen',
  'vertraege-am-bearbeiten': 'Verträge - Bearbeiten',
  'vertraege-am-abwaegen': 'Verträge - Abwägen',
  'vertraege-am-dokumenten-assistent': 'Verträge - Dokumenten-Assistent',
  'vertraege-am-pending': 'Verträge - Deals',
  'vertraege-am-automatisierungen': 'Automatisierung',
  'vertraege-am-klauseln': 'Paragraphen',
  standarddokumente: 'Standarddokumente',
  'standarddokumente-vorlagen': 'Standarddokumente Vorlagen',
  'onboarding-signature': 'On-Boarding E-Mail Signatur',
  'mittwald-mail': 'Mittwald E-Mail',
  'bitrix-user': 'Bitrix Zugang',
  'eeg-rechner': 'EEG-Vergütung berechnen',
  settings: 'Einstellungen',
  'automation-uebersicht': 'Laufende Automatisierungen',
  zugangsanfragen: 'Zugänge freischalten',
  'mail-konten': 'Postfächer',
  noAccess: 'Kein Zugriff',
};

const VIEW_TOOL_KEYS = {
  dach: 'offers',
  sanierung: 'offers',
  freiflaeche: 'offers',
  speicher: 'offers',
  bess: 'offers',
  'expose-projektverkauf': 'exposes',
  'expose-turnkey': 'exposes',
  flyer: 'flyer',
  'ffpv-flyer': 'contractsAmQueue',
  // Eigenes Recht; der Alias auf contractsAmQueue (Server + toolRechte.js)
  // laesst es anfangs genau wie "Verträge - Deals" gelten.
  'ffpv-teaser': 'ffpvTeaser',
  vertrieb: 'vertrieb',
  generator: 'generator',
  designsystem: 'designsystem',
  vermittlervertrag: 'vermittlervertrag',
  vertraege: 'contractTemplates',
  'exposes-automation': 'exposes',
  'exposes-cockpit': 'exposes',
  'vertraege-am': 'contractsAm',
  'vertraege-am-bearbeiten': 'contractsAm',
  // Dasselbe Recht wie Erstellen: Der Server prueft beim Abwaegen ohnehin
  // contractsAm. Ein eigenes Recht haette den Menuepunkt fuer alle ausser
  // Admins unsichtbar gemacht, bis jemand es einzeln freischaltet.
  'vertraege-am-abwaegen': 'contractsAm',
  // Eigenes Recht OHNE Alias (Entscheidung 21.10.2026): wie das alte Tool
  // anfangs nur fuer Admins, per Tool-Rechte freischaltbar.
  'vertraege-am-dokumenten-assistent': 'dokumentenAssistent',
  'vertraege-am-pending': 'contractsAmQueue',
  'vertraege-am-automatisierungen': 'contractsAmAutomations',
  'vertraege-am-klauseln': 'contractsAmClauses',
  standarddokumente: 'standardDocuments',
  'standarddokumente-vorlagen': 'standardDocumentTemplates',
  'onboarding-signature': 'onboarding',
  'mittwald-mail': 'mittwaldMail',
  'bitrix-user': 'bitrixUser',
  'eeg-rechner': 'eegRechner',
};

const MAIN_TOOL_REMOVED_VIEWS = new Set([]);

const OFFER_VIEW_ALIASES = {
  sanierung: 'dach',
  bess: 'speicher',
};

function getBaseOfferView(view) {
  return OFFER_VIEW_ALIASES[view] || view;
}

function isDachTemplateView(view) {
  return getBaseOfferView(view) === 'dach';
}

function isSpeicherTemplateView(view) {
  return getBaseOfferView(view) === 'speicher';
}

function getNavigationLeafItems(items = []) {
  return (Array.isArray(items) ? items : []).flatMap((item) => (
    Array.isArray(item.children) && item.children.length > 0
      ? getNavigationLeafItems(item.children)
      : [item]
  ));
}

function getFirstNavigationLeaf(items = []) {
  return getNavigationLeafItems(items)[0] || null;
}

function navigationItemContainsKey(item, key) {
  if (!item) return false;
  if (item.key === key) return true;
  return Array.isArray(item.children) && item.children.some((child) => navigationItemContainsKey(child, key));
}

const ROUTED_TOOL_VIEWS = {
  '/offer-generator': 'generator',
  '/generator': 'generator',
};

function getInitialActiveView() {
  if (typeof window === 'undefined') {
    return 'dach';
  }

  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  if (ROUTED_TOOL_VIEWS[pathname]) {
    return ROUTED_TOOL_VIEWS[pathname];
  }

  const params = new URLSearchParams(window.location.search);
  const requestedView = String(params.get('view') || params.get('tool') || '').trim().toLowerCase();

  if (MAIN_TOOL_REMOVED_VIEWS.has(requestedView)) {
    return 'dach';
  }

  return TYPE_LABELS[requestedView] ? requestedView : 'dach';
}

function getGeneratedOfferNavigationKey(offer = {}) {
  const offerVariant = String(offer.proposalData?.offerVariant || '').trim().toLowerCase();

  if (offer.view === 'dach' && offerVariant === 'sanierung') {
    return 'sanierung';
  }

  if (offer.view === 'speicher' && offerVariant === 'bess') {
    return 'bess';
  }

  return offer.view || 'dach';
}

const EMPTY_TOOL_ACCESS = { canView: false, canCreate: false, canEdit: false };
const FULL_TOOL_ACCESS = { canView: true, canCreate: true, canEdit: true };
// Gleiche Liste wie api/_lib/supabaseAuth.js und src/lib/toolRechte.js (Test: api/_lib/adminKonten.test.js).
const BUILT_IN_ADMIN_EMAILS = new Set([
  'test@roofshare-solutions.com',
  'r.kirsten@roofshare-solutions.com',
  'r.kirsten@en-power.eu',
  'l.klein@en-power.eu',
]);
const PUBLIC_GENERATED_OFFER_SESSION = { access_token: '', user: { email: 'pdf-render@enpower.local' } };
const PUBLIC_GENERATED_OFFER_ACCESS = {
  offers: { canView: true, canCreate: false, canEdit: false },
  exposes: { canView: true, canCreate: false, canEdit: false },
};
const EXPOSE_IMPORT_NOTICE_KEY = 'enpower.exposes.importNotice.v1';

const isBuiltInAdminEmail = (email) => BUILT_IN_ADMIN_EMAILS.has(String(email || '').trim().toLowerCase());

const normalizeClientToolAccess = (access = EMPTY_TOOL_ACCESS) => {
  const canCreate = access?.canCreate === true;
  const canEdit = access?.canEdit === true;
  const canView = access?.canView === true || canCreate || canEdit;

  return { canView, canCreate, canEdit };
};

const DEFAULT_LAYOUT_POSITION = { x: 0, y: 0 };
const DACH_LAYOUT_STORAGE_KEY = 'enpower-dach-layout-positions-v2';
const DACH_TEXT_STORAGE_KEY = 'enpower-dach-text-content-v1';
const GENERIC_TEXT_OVERRIDES_STORAGE_KEY = 'enpower-generic-text-overrides-v1';
const DACH_TEXT_NODE_OVERRIDES_PATH = '_textNodeOverrides';
const DACH_LAYOUT_DOCUMENT_KEY = `dach-${String(DACH_DATA.angebotsnummer || 'standard').trim().replace(/\s+/g, '-').toLowerCase()}`;
const DACH_LAYOUT_VERSION_LIMIT = 12;
const GENERATED_OFFER_QUERY_KEYS = ['offer', 'offer_id', 'generated_offer'];
const DEFAULT_DACH_LAYOUT_POSITIONS = {
  'S2-Unternehmensprofil-Intro': { x: 0, y: 15 },
  'S2-Stats-Band': { x: 0, y: 41 },
  'S2-Vorteile-Header': { x: 0, y: 38 },
  'S2-Vorteil-1': { x: 0, y: -32 },
  'S2-Vorteil-2': { x: 0, y: -32 },
  'S2-Vorteil-3': { x: 0, y: -32 },
  'S2-Vorteil-4': { x: 0, y: -32 },
  'S2-Vorteil-5': { x: 0, y: -32 },
  'S2-Vorteil-6': { x: 0, y: -32 },
  'S3-Pachtangebot-Adresse': { x: 0, y: 19 },
  'S3-Pachtangebot-Intro': { x: 0, y: 22 },
  'S3-KPI-Dachflaeche': { x: 0, y: 26 },
  'S3-KPI-Leistung': { x: 0, y: 26 },
  'S3-KPI-Laufzeit': { x: 0, y: 26 },
  'S3-Prozess-Band': { x: 0, y: 52 },
  'S3-Preisoptionen-Header': { x: 0, y: 56 },
  'S3-Preisoptionen-Meta': { x: 0, y: 77 },
  'S3-Preisoption-1': { x: 0, y: 86 },
  'S3-Preisoption-2': { x: 0, y: 86 },
  'S3-Preisoptionen-Hinweis': { x: 0, y: -75 },
};
const DEFAULT_SPEICHER_LAYOUT_POSITIONS = {
  'SP-S2-Stats': { x: 0, y: 10 },
  'SP-S3-Address': { x: 0, y: 7 },
  'SP-S3-KPI': { x: 0, y: 13 },
  'SP-S3-Process': { x: 0, y: 22 },
  'SP-S3-Pricing': { x: 0, y: 35 },
  'SP-S3-Pricing-1': { x: 0, y: 20 },
  'SP-S3-Pricing-2': { x: 0, y: 19 },
  'SP-S3-Notice': { x: 0, y: 120 },
  'SP-S4-NextSteps': { x: 0, y: 4 },
  'SP-S4-FAQ': { x: 0, y: -14 },
  'SP-S4-FAQ-1': { x: 0, y: -7 },
  'SP-S4-FAQ-2': { x: 0, y: -7 },
  'SP-S4-FAQ-3': { x: 0, y: -7 },
  'SP-S4-FAQ-4': { x: 0, y: -7 },
  'SP-S4-FAQ-5': { x: 0, y: -7 },
  'SP-S4-FAQ-6': { x: 0, y: -7 },
  'SP-S4-CTA': { x: 0, y: -28 },
  'SP-S4-Signature': { x: 0, y: -16 },
};
const DACH_EDITABLE_TEXT_FIELDS = [
  { path: 'cover.badge', label: 'Deckblatt: Badge' },
  { path: 'cover.eyebrow', label: 'Deckblatt: Eyebrow' },
  { path: 'cover.headline', label: 'Deckblatt: Haupttitel', multiline: true },
  { path: 'cover.sublinePrefix', label: 'Deckblatt: Subline Prefix' },
  { path: 'cover.projectAddressLabel', label: 'Deckblatt: Projektadresse Label' },
  { path: 'cover.confidentialNote', label: 'Deckblatt: Vertraulichkeit', multiline: true },
  { path: 'page2.company.header.eyebrowSuffix', label: 'Seite 2: Unternehmensprofil Eyebrow' },
  { path: 'page2.company.header.headline', label: 'Seite 2: Unternehmensprofil Titel' },
  { path: 'page2.company.intro.paragraph1', label: 'Seite 2: Intro Absatz 1', multiline: true },
  { path: 'page2.company.intro.paragraph2', label: 'Seite 2: Intro Absatz 2', multiline: true },
  { path: 'page2.company.quote.quote', label: 'Seite 2: Zitat', multiline: true },
  { path: 'page2.company.quote.footnote', label: 'Seite 2: Zitat Fußnote', multiline: true },
  { path: 'page2.benefits.header.eyebrow', label: 'Seite 2: Vorteile Eyebrow' },
  { path: 'page2.benefits.header.headline', label: 'Seite 2: Vorteile Titel' },
  { path: 'page3.project.header.eyebrow', label: 'Seite 3: Pachtangebot Eyebrow' },
  { path: 'page3.project.header.headline', label: 'Seite 3: Pachtangebot Titel' },
  { path: 'page3.project.addressLabel', label: 'Seite 3: Projektadresse Label' },
  { path: 'page3.project.addressTextPrefix', label: 'Seite 3: Projektadresse Prefix' },
  { path: 'page3.project.addressTextSuffix', label: 'Seite 3: Projektadresse Suffix' },
  { path: 'page3.project.intro', label: 'Seite 3: Introtext', multiline: true },
  { path: 'page3.project.processLabel', label: 'Seite 3: Prozess Label' },
  // variant: 'standard' = nur im normalen Verpachtungsangebot (zwei Preisboxen),
  // variant: 'sanierung' = nur im Sanierungsangebot (eine Preisbox). Ohne Angabe: immer sichtbar.
  { path: 'pricing.option1.headerLabel', label: 'Preisbox links: Titel', variant: 'standard' },
  { path: 'pricing.option1.headerSub', label: 'Preisbox links: Untertitel', variant: 'standard' },
  { path: 'pricing.option1.rows.auszahlung', label: 'Preisbox links: Zeile 1 Label', variant: 'standard' },
  { path: 'pricing.values.auszahlungVoraus', label: 'Preisbox links: Zeile 1 Wert', variant: 'standard' },
  { path: 'pricing.header.eyebrow', label: 'Preisoptionen: Eyebrow' },
  { path: 'pricing.header.headline', label: 'Preisoptionen: Titel' },
  { path: 'pricing.option2.headerLabel', label: 'Preisbox rechts: Titel', variant: 'standard' },
  { path: 'pricing.option2.headerSub', label: 'Preisbox rechts: Untertitel', variant: 'standard' },
  { path: 'pricing.option2.rows.gesamtePachtzahlung', label: 'Preisbox rechts: Extra-Label', variant: 'standard' },
  { path: 'pricing.sanierung.headerLabel', label: 'Sanierungsbox: Titel', variant: 'sanierung' },
  { path: 'pricing.sanierung.headerSub', label: 'Sanierungsbox: Untertitel', variant: 'sanierung' },
  { path: 'pricing.sanierung.priceLabel', label: 'Sanierungsbox: Label über Betrag', variant: 'sanierung' },
  { path: 'pricing.sanierung.priceFootnote', label: 'Sanierungsbox: Fußnote unter Betrag', variant: 'sanierung' },
  { path: 'pricing.sanierung.values.leistung', label: 'Sanierungsbox: Wert Leistungsumfang', variant: 'sanierung' },
  { path: 'pricing.sanierung.values.zeitpunkt', label: 'Sanierungsbox: Wert Zeitpunkt', variant: 'sanierung' },
  { path: 'pricing.meta.auftraggeberLabel', label: 'Preisoptionen: Label Auftraggeber' },
  { path: 'pricing.meta.angebotsnummerLabel', label: 'Preisoptionen: Label Angebotsnr.' },
  { path: 'pricing.meta.angebotstypLabel', label: 'Preisoptionen: Label Angebotstyp' },
  { path: 'pricing.meta.angebotstypValue', label: 'Preisoptionen: Wert Angebotstyp' },
  { path: 'alternatives.option1.title', label: 'Alternative 1: Titel' },
  { path: 'alternatives.option1.description', label: 'Alternative 1: Beschreibung', multiline: true },
  { path: 'alternatives.option2.title', label: 'Alternative 2: Titel' },
  { path: 'alternatives.option2.description', label: 'Alternative 2: Beschreibung', multiline: true },
  { path: 'alternatives.header.eyebrow', label: 'Alternativen: Eyebrow' },
  { path: 'alternatives.header.headline', label: 'Alternativen: Titel' },
  { path: 'page4.nextSteps.header.eyebrow', label: 'Nächste Schritte: Eyebrow' },
  { path: 'page4.nextSteps.header.headline', label: 'Nächste Schritte: Titel' },
  { path: 'page4.faq.header.eyebrow', label: 'FAQ: Eyebrow' },
  { path: 'page4.faq.header.headline', label: 'FAQ: Titel' },
  { path: 'page4.cta.badge', label: 'CTA: Badge' },
  { path: 'page4.cta.headline', label: 'CTA: Titel' },
  { path: 'page4.cta.sublinePrefix', label: 'CTA: Subline Prefix' },
  { path: 'page4.cta.signatureLead', label: 'CTA: Signatur Einleitung' },
  { path: 'page4.cta.validUntilLabel', label: 'CTA: Gültig-bis Label' },
  { path: 'pricing.notice', label: 'Hinweistext Preisoptionen', multiline: true },
];
const DACH_SECTION_GROUPS = [
  {
    key: 'page-1',
    title: 'Seite 1',
    sections: [{ id: 'S1-Deckblatt', label: 'Deckblatt' }],
  },
  {
    key: 'page-2',
    title: 'Seite 2',
    sections: [
      { id: 'S2-Unternehmensprofil', label: 'Unternehmensprofil' },
      { id: 'S2-Vorteile', label: 'Vorteile' },
    ],
  },
  {
    key: 'page-3',
    title: 'Seite 3',
    sections: [
      { id: 'S3-Pachtangebot', label: 'Pachtangebot' },
      { id: 'S3-Preisoptionen', label: 'Preisoptionen' },
    ],
  },
  {
    key: 'page-4',
    title: 'Seite 4',
    sections: [
      { id: 'S4-Alternativen', label: 'Alternativen' },
      { id: 'S4-Naechste-Schritte', label: 'Nächste Schritte' },
      { id: 'S4-FAQ', label: 'FAQ' },
      { id: 'S4-CTA', label: 'CTA' },
    ],
  },
];
const DACH_SECTION_LABELS = Object.fromEntries(
  DACH_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => [section.id, section.label]))
);
const FREIFLAECHE_SECTION_GROUPS = [
  {
    key: 'page-1',
    title: 'Seite 1',
    sections: [{ id: 'FF-S1-Deckblatt', label: 'Deckblatt' }],
  },
  {
    key: 'page-2',
    title: 'Seite 2',
    sections: [
      { id: 'FF-S2-Heading', label: 'Heading' },
      { id: 'FF-S2-Intro', label: 'Intro' },
      { id: 'FF-S2-Stats', label: 'Stats' },
      { id: 'FF-S2-Benefits-Heading', label: 'Vorteile Heading' },
      { id: 'FF-S2-Benefits', label: 'Vorteile' },
    ],
  },
  {
    key: 'page-3',
    title: 'Seite 3',
    sections: [
      { id: 'FF-S3-Heading', label: 'Heading' },
      { id: 'FF-S3-Address', label: 'Adresse' },
      { id: 'FF-S3-KPI', label: 'KPI' },
      { id: 'FF-S3-Process', label: 'Prozess' },
      { id: 'FF-S3-Pricing', label: 'Pricing' },
    ],
  },
  {
    key: 'page-4',
    title: 'Seite 4',
    sections: [
      { id: 'FF-S4-Alternatives-Heading', label: 'Alternativen Heading' },
      { id: 'FF-S4-Alternatives', label: 'Alternativen' },
      { id: 'FF-S4-NextSteps', label: 'Nächste Schritte' },
      { id: 'FF-S4-FAQ', label: 'FAQ' },
      { id: 'FF-S4-CTA', label: 'CTA' },
      { id: 'FF-S4-Signature', label: 'Signatur' },
    ],
  },
];
const FREIFLAECHE_SECTION_LABELS = Object.fromEntries(
  FREIFLAECHE_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => [section.id, section.label]))
);
const SPEICHER_SECTION_GROUPS = [
  {
    key: 'page-1',
    title: 'Seite 1',
    sections: [{ id: 'SP-S1-Deckblatt', label: 'Deckblatt' }],
  },
  {
    key: 'page-2',
    title: 'Seite 2',
    sections: [
      { id: 'SP-S2-Heading', label: 'Heading' },
      { id: 'SP-S2-Intro', label: 'Intro' },
      { id: 'SP-S2-Stats', label: 'Stats' },
      { id: 'SP-S2-Benefits-Heading', label: 'Vorteile Heading' },
      { id: 'SP-S2-Benefits', label: 'Vorteile' },
    ],
  },
  {
    key: 'page-3',
    title: 'Seite 3',
    sections: [
      { id: 'SP-S3-Heading', label: 'Heading' },
      { id: 'SP-S3-Address', label: 'Adresse' },
      { id: 'SP-S3-KPI', label: 'KPI' },
      { id: 'SP-S3-Process', label: 'Prozess' },
      { id: 'SP-S3-Pricing', label: 'Pricing' },
    ],
  },
  {
    key: 'page-4',
    title: 'Seite 4',
    sections: [
      { id: 'SP-S4-Alternatives-Heading', label: 'Alternativen Heading' },
      { id: 'SP-S4-Alternatives', label: 'Alternativen' },
      { id: 'SP-S4-NextSteps', label: 'Nächste Schritte' },
      { id: 'SP-S4-FAQ', label: 'FAQ' },
      { id: 'SP-S4-CTA', label: 'CTA' },
      { id: 'SP-S4-Signature', label: 'Signatur' },
    ],
  },
];
const SPEICHER_SECTION_LABELS = Object.fromEntries(
  SPEICHER_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => [section.id, section.label]))
);
const buildExposeSectionGroups = (prefix) => [
  {
    key: 'page-1',
    title: 'Seite 1',
    sections: [{ id: `${prefix}-S1-Deckblatt`, label: 'Deckblatt' }],
  },
  {
    key: 'page-2',
    title: 'Seite 2',
    sections: [
      { id: `${prefix}-S2-Heading`, label: 'Heading' },
      { id: `${prefix}-S2-Intro`, label: 'Intro' },
      { id: `${prefix}-S2-Metrics`, label: 'Kennzahlen' },
      { id: `${prefix}-S2-Benefits`, label: 'Highlights' },
    ],
  },
  {
    key: 'page-3',
    title: 'Seite 3',
    sections: [
      { id: `${prefix}-S3-Heading`, label: 'Heading' },
      { id: `${prefix}-S3-Deal`, label: 'Transaktionsprofil' },
      { id: `${prefix}-S3-Commercial`, label: 'Kennzahlen' },
      { id: `${prefix}-S3-Checklist`, label: 'Datenraum' },
    ],
  },
  {
    key: 'page-4',
    title: 'Seite 4',
    sections: [
      { id: `${prefix}-S4-Heading`, label: 'Heading' },
      { id: `${prefix}-S4-Process`, label: 'Prozess' },
      { id: `${prefix}-S4-Contact`, label: 'Kontakt' },
      { id: `${prefix}-S4-Final`, label: 'Hinweise' },
    ],
  },
];
const EXPOSE_PROJEKTVERKAUF_SECTION_GROUPS = [
  {
    key: 'page-1',
    title: 'Seite 1',
    sections: [{ id: 'EXPV-S1-Deckblatt', label: 'Deckblatt' }],
  },
  {
    key: 'page-2',
    title: 'Seite 2',
    sections: [
      { id: 'EXPV-S2-Heading', label: 'Kennzahlen' },
      { id: 'EXPV-S2-Intro', label: 'Wirtschaftliche Eckdaten' },
      { id: 'EXPV-S2-Metrics', label: 'Vertragsdetails' },
      { id: 'EXPV-S3-Heading', label: 'Standort & Gebäudedaten' },
      { id: 'EXPV-S3-Checklist', label: 'Ihr Ansprechpartner' },
      { id: 'EXPV-S3-Deal', label: 'Vertrags- & Genehmigungsstatus' },
      { id: 'EXPV-S4-Process', label: 'Netzanschluss' },
    ],
  },
  {
    key: 'page-3',
    title: 'Seite 3',
    sections: [
      { id: 'EXPV-S4-Heading', label: 'PVSol' },
      { id: 'EXPV-S4-Contact', label: 'Grundbuch' },
      { id: 'EXPV-S4-Final', label: 'Statik' },
      { id: 'EXPV-S2-Benefits', label: 'Lageplan' },
    ],
  },
];
const EXPOSE_TURNKEY_SECTION_GROUPS = [
  {
    key: 'page-1',
    title: 'Seite 1',
    sections: [{ id: 'EXTK-S1-Deckblatt', label: 'Deckblatt' }],
  },
  {
    key: 'page-2',
    title: 'Seite 2',
    sections: [
      { id: 'EXTK-S2-Heading', label: 'Kennzahlen' },
      { id: 'EXTK-S2-Intro', label: 'Wirtschaftliche Eckdaten' },
      { id: 'EXTK-S2-Metrics', label: 'Vertragsdetails' },
      { id: 'EXTK-S3-Heading', label: 'Standort & Gebäudedaten' },
      { id: 'EXTK-S3-Checklist', label: 'Ihr Ansprechpartner' },
      { id: 'EXTK-S3-Deal', label: 'Vertrags- & Genehmigungsstatus' },
      { id: 'EXTK-S4-Process', label: 'Netzanschluss' },
    ],
  },
  {
    key: 'page-3',
    title: 'Seite 3',
    sections: [
      { id: 'EXTK-S4-Heading', label: 'PVSol' },
      { id: 'EXTK-S4-Contact', label: 'Grundbuch' },
      { id: 'EXTK-S4-Final', label: 'Statik' },
      { id: 'EXTK-S2-Benefits', label: 'Lageplan' },
    ],
  },
  {
    key: 'page-4',
    title: 'Seite 4',
    sections: [
      { id: 'EXTK-S5-Heading', label: 'Ertragsprognose & Cashflow' },
      { id: 'EXTK-S5-Process', label: 'OPEX-Aufschlüsselung' },
      { id: 'EXTK-S5-Contact', label: 'Investitions-Highlights' },
    ],
  },
];
const EXPOSE_PROJEKTVERKAUF_SECTION_LABELS = Object.fromEntries(
  EXPOSE_PROJEKTVERKAUF_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => [section.id, section.label]))
);
const EXPOSE_TURNKEY_SECTION_LABELS = Object.fromEntries(
  EXPOSE_TURNKEY_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => [section.id, section.label]))
);
const SPACING_SNAP_THRESHOLD = 6;
const SPACING_GRID_OPTIONS = [0, 4, 8];
const DACH_DETAIL_ELEMENT_IDS = [
  'S1-Deckblatt-Inhalt',
  'S1-Deckblatt-Meta',
  'S2-Unternehmensprofil-Header',
  'S2-Unternehmensprofil-Intro',
  'S2-Vorteile-Header',
  'S2-Vorteil-1',
  'S2-Vorteil-2',
  'S2-Vorteil-3',
  'S2-Vorteil-4',
  'S2-Vorteil-5',
  'S2-Vorteil-6',
  'S2-Stats-Band',
  'S3-Pachtangebot-Header',
  'S3-Pachtangebot-Adresse',
  'S3-Pachtangebot-Intro',
  'S3-Pachtangebot-KPI-Grid',
  'S3-KPI-Dachflaeche',
  'S3-KPI-Leistung',
  'S3-KPI-Laufzeit',
  'S3-Prozess-1',
  'S3-Prozess-2',
  'S3-Prozess-3',
  'S3-Prozess-4',
  'S3-Prozess-5',
  'S3-Prozess-Band',
  'S3-Preisoptionen-Header',
  'S3-Preisoptionen-Meta',
  'S3-Preisoption-1',
  'S3-Preisoption-2',
  'S3-Preisoptionen-Hinweis',
  'S4-Alternativen-Header',
  'S4-Alternative-1',
  'S4-Alternative-2',
  'S4-Kontakt',
  'S4-Naechste-Schritte-Header',
  'S4-Naechste-Schritte-Band',
  'S4-FAQ-Header',
  'S4-CTA-Banner',
  'S4-CTA-Signatur',
];
const FREIFLAECHE_DETAIL_ELEMENT_IDS = [
  'FF-S1-Deckblatt',
  'FF-S2-Heading',
  'FF-S2-Intro',
  'FF-S2-Intro-Text',
  'FF-S2-Intro-Quote',
  'FF-S2-Stats',
  'FF-S2-Stat-1',
  'FF-S2-Stat-2',
  'FF-S2-Stat-3',
  'FF-S2-Stat-4',
  'FF-S2-Benefits-Heading',
  'FF-S2-Benefits',
  'FF-S2-Benefit-1',
  'FF-S2-Benefit-2',
  'FF-S2-Benefit-3',
  'FF-S2-Benefit-4',
  'FF-S2-Benefit-5',
  'FF-S2-Benefit-6',
  'FF-S3-Heading',
  'FF-S3-Address',
  'FF-S3-KPI',
  'FF-S3-KPI-1',
  'FF-S3-KPI-2',
  'FF-S3-KPI-3',
  'FF-S3-Process',
  'FF-S3-Process-1',
  'FF-S3-Process-2',
  'FF-S3-Process-3',
  'FF-S3-Process-4',
  'FF-S3-Process-5',
  'FF-S3-Pricing',
  'FF-S3-Tier-1',
  'FF-S3-Tier-2',
  'FF-S3-Tier-3',
  'FF-S3-Total',
  'FF-S3-Notice',
  'FF-S4-Alternatives-Heading',
  'FF-S4-Alternatives',
  'FF-S4-Alternative-1',
  'FF-S4-Alternative-2',
  'FF-S4-Contact',
  'FF-S4-NextSteps',
  'FF-S4-Step-1',
  'FF-S4-Step-2',
  'FF-S4-FAQ',
  'FF-S4-FAQ-1',
  'FF-S4-FAQ-2',
  'FF-S4-FAQ-3',
  'FF-S4-FAQ-4',
  'FF-S4-FAQ-5',
  'FF-S4-FAQ-6',
  'FF-S4-CTA',
  'FF-S4-Signature',
];
const SPEICHER_DETAIL_ELEMENT_IDS = [
  'SP-S1-Deckblatt',
  'SP-S2-Heading',
  'SP-S2-Intro',
  'SP-S2-Intro-Text',
  'SP-S2-Intro-Image',
  'SP-S2-Stats',
  'SP-S2-Stat-1',
  'SP-S2-Stat-2',
  'SP-S2-Stat-3',
  'SP-S2-Stat-4',
  'SP-S2-Benefits-Heading',
  'SP-S2-Benefits',
  'SP-S2-Benefit-1',
  'SP-S2-Benefit-2',
  'SP-S2-Benefit-3',
  'SP-S2-Benefit-4',
  'SP-S2-Benefit-5',
  'SP-S2-Benefit-6',
  'SP-S3-Heading',
  'SP-S3-Address',
  'SP-S3-KPI',
  'SP-S3-KPI-1',
  'SP-S3-KPI-2',
  'SP-S3-KPI-3',
  'SP-S3-Process',
  'SP-S3-Process-1',
  'SP-S3-Process-2',
  'SP-S3-Process-3',
  'SP-S3-Process-4',
  'SP-S3-Process-5',
  'SP-S3-Pricing',
  'SP-S3-Pricing-1',
  'SP-S3-Pricing-2',
  'SP-S3-Notice',
  'SP-S4-Alternatives-Heading',
  'SP-S4-Alternatives',
  'SP-S4-Alternative-1',
  'SP-S4-Alternative-2',
  'SP-S4-Contact',
  'SP-S4-NextSteps',
  'SP-S4-Step-1',
  'SP-S4-Step-2',
  'SP-S4-FAQ',
  'SP-S4-FAQ-1',
  'SP-S4-FAQ-2',
  'SP-S4-FAQ-3',
  'SP-S4-FAQ-4',
  'SP-S4-FAQ-5',
  'SP-S4-FAQ-6',
  'SP-S4-CTA',
  'SP-S4-Signature',
];
const EXPOSE_DETAIL_ELEMENT_IDS = [
  ...EXPOSE_PROJEKTVERKAUF_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => section.id)),
  ...EXPOSE_TURNKEY_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => section.id)),
];

const getLinkedSectionId = (id) => {
  if (!id) {
    return null;
  }

  if (id === 'S1-Deckblatt' || id.startsWith('S1-Deckblatt-')) {
    return 'S1-Deckblatt';
  }

  if (id === 'S2-Unternehmensprofil' || id.startsWith('S2-Unternehmensprofil-')) {
    return 'S2-Unternehmensprofil';
  }

  if (id === 'S2-Stats-Band') {
    return 'S2-Unternehmensprofil';
  }

  if (id === 'S2-Vorteile' || id.startsWith('S2-Vorteile-') || id.startsWith('S2-Vorteil-')) {
    return 'S2-Vorteile';
  }

  if (
    id === 'S3-Pachtangebot'
    || id.startsWith('S3-Pachtangebot-')
    || id.startsWith('S3-KPI-')
    || id.startsWith('S3-Prozess-')
    || id === 'S3-Prozess-Band'
  ) {
    return 'S3-Pachtangebot';
  }

  if (
    id === 'S3-Preisoptionen'
    || id.startsWith('S3-Preisoptionen-')
    || id.startsWith('S3-Preisoption-')
  ) {
    return 'S3-Preisoptionen';
  }

  if (
    id === 'S4-Alternativen'
    || id.startsWith('S4-Alternativen-')
    || id.startsWith('S4-Alternative-')
    || id === 'S4-Kontakt'
  ) {
    return 'S4-Alternativen';
  }

  if (id === 'S4-Naechste-Schritte' || id.startsWith('S4-Naechste-Schritte-')) {
    return 'S4-Naechste-Schritte';
  }

  if (id === 'S4-Naechste-Schritte-Band') {
    return 'S4-Naechste-Schritte';
  }

  if (id === 'S4-FAQ' || id.startsWith('S4-FAQ-')) {
    return 'S4-FAQ';
  }

  if (id === 'S4-CTA' || id.startsWith('S4-CTA-')) {
    return 'S4-CTA';
  }

  return null;
};

const resolveEffectivePosition = (id, positions = {}) => {
  const sectionId = getLinkedSectionId(id);
  const ownY = Number(positions[id]?.y) || 0;

  if (!sectionId || sectionId === id) {
    return { x: 0, y: ownY };
  }

  return {
    x: 0,
    y: (Number(positions[sectionId]?.y) || 0) + ownY,
  };
};

const buildResolvedPositions = (positions = {}) => {
  const result = {};
  const idsToResolve = new Set([
    ...Object.keys(positions),
    ...DACH_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => section.id)),
    ...DACH_DETAIL_ELEMENT_IDS,
    ...FREIFLAECHE_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => section.id)),
    ...FREIFLAECHE_DETAIL_ELEMENT_IDS,
    ...SPEICHER_SECTION_GROUPS.flatMap((group) => group.sections.map((section) => section.id)),
    ...SPEICHER_DETAIL_ELEMENT_IDS,
    ...EXPOSE_DETAIL_ELEMENT_IDS,
  ]);

  idsToResolve.forEach((id) => {
    result[id] = resolveEffectivePosition(id, positions);
  });

  return result;
};

const getPreferredSpacingGap = (metrics = []) => {
  if (metrics.length === 0) {
    return 0;
  }

  const counts = new Map();

  metrics.forEach((metric) => {
    if (!Number.isFinite(metric.gap)) {
      return;
    }

    const normalizedGap = Math.round(metric.gap);
    counts.set(normalizedGap, (counts.get(normalizedGap) || 0) + 1);
  });

  const rankedGaps = [...counts.entries()].sort((left, right) => right[1] - left[1] || left[0] - right[0]);

  if (rankedGaps[0]?.[1] > 1) {
    return rankedGaps[0][0];
  }

  const totalGap = metrics.reduce((sum, metric) => sum + (Number.isFinite(metric.gap) ? metric.gap : 0), 0);
  return Math.round(totalGap / metrics.length);
};

const getFrequentSpacingGaps = (metrics = [], excludedId) => {
  const counts = new Map();

  metrics.forEach((metric) => {
    if (
      !Number.isFinite(metric.gap)
      || metric.fromId === excludedId
      || metric.toId === excludedId
    ) {
      return;
    }

    const normalizedGap = Math.round(metric.gap);
    counts.set(normalizedGap, (counts.get(normalizedGap) || 0) + 1);
  });

  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1] || left[0] - right[0])
    .map(([gap]) => gap);
};

const getNearestGridGap = (value, gridSize) => {
  if (!gridSize) {
    return null;
  }

  return Math.max(0, Math.round(value / gridSize) * gridSize);
};

const getAdaptiveSnapThreshold = (metrics = [], id) => {
  const relatedGaps = metrics
    .filter((metric) => metric.fromId === id || metric.toId === id)
    .map((metric) => Math.abs(metric.gap))
    .filter((gap) => Number.isFinite(gap) && gap > 0);

  if (relatedGaps.length === 0) {
    return SPACING_SNAP_THRESHOLD;
  }

  const baseGap = Math.min(...relatedGaps);
  return Math.max(3, Math.min(20, Math.round(baseGap * 0.18)));
};

const resolveSpacingAssist = ({ id, deltaY, currentMetrics, snapshotMetrics, gridSize, enableSmartSnap, snapThreshold }) => {
  const previousMetric = currentMetrics.find((metric) => metric.toId === id);
  const nextMetric = currentMetrics.find((metric) => metric.fromId === id);

  if (!previousMetric && !nextMetric) {
    return {
      adjustedDeltaY: deltaY,
      snapTarget: null,
      snapReason: null,
      metricKey: null,
    };
  }

  const candidates = [];
  const stableMetrics = snapshotMetrics.length > 0 ? snapshotMetrics : currentMetrics;

  if (enableSmartSnap) {
    const frequentGaps = getFrequentSpacingGaps(stableMetrics, id).slice(0, 4);

    frequentGaps.forEach((targetGap) => {
      if (previousMetric) {
        const tentativeGap = previousMetric.gap + deltaY;
        candidates.push({
          diff: Math.abs(tentativeGap - targetGap),
          adjustedDeltaY: deltaY + (targetGap - tentativeGap),
          metricKey: previousMetric.key,
          snapReason: 'common',
          snapTarget: targetGap,
        });
      }

      if (nextMetric) {
        const tentativeGap = nextMetric.gap - deltaY;
        candidates.push({
          diff: Math.abs(tentativeGap - targetGap),
          adjustedDeltaY: deltaY + (tentativeGap - targetGap),
          metricKey: nextMetric.key,
          snapReason: 'common',
          snapTarget: targetGap,
        });
      }
    });

    if (previousMetric && nextMetric) {
      const equalizedDelta = Math.round((nextMetric.gap - previousMetric.gap) / 2);
      candidates.push({
        diff: Math.abs(deltaY - equalizedDelta),
        adjustedDeltaY: equalizedDelta,
        metricKey: `${previousMetric.key}__${nextMetric.key}`,
        snapReason: 'equal',
        snapTarget: Math.round((previousMetric.gap + nextMetric.gap) / 2),
      });
    }
  }

  if (gridSize > 0) {
    if (previousMetric) {
      const tentativeGap = previousMetric.gap + deltaY;
      const targetGap = getNearestGridGap(tentativeGap, gridSize);
      candidates.push({
        diff: Math.abs(tentativeGap - targetGap),
        adjustedDeltaY: deltaY + (targetGap - tentativeGap),
        metricKey: previousMetric.key,
        snapReason: 'grid',
        snapTarget: targetGap,
      });
    }

    if (nextMetric) {
      const tentativeGap = nextMetric.gap - deltaY;
      const targetGap = getNearestGridGap(tentativeGap, gridSize);
      candidates.push({
        diff: Math.abs(tentativeGap - targetGap),
        adjustedDeltaY: deltaY + (tentativeGap - targetGap),
        metricKey: nextMetric.key,
        snapReason: 'grid',
        snapTarget: targetGap,
      });
    }
  }

  const priority = { equal: 0, common: 1, grid: 2 };
  const bestCandidate = candidates
    .filter((candidate) => candidate.diff <= snapThreshold)
    .sort((left, right) => left.diff - right.diff || priority[left.snapReason] - priority[right.snapReason])[0];

  if (!bestCandidate) {
    return {
      adjustedDeltaY: deltaY,
      snapTarget: null,
      snapReason: null,
      metricKey: null,
    };
  }

  return bestCandidate;
};

const buildActiveSpacingFeedback = ({ id, appliedDeltaY, currentMetrics, snapTarget, snapReason, snapMetricKey, gridSize }) => {
  const previousMetric = currentMetrics.find((metric) => metric.toId === id);
  const nextMetric = currentMetrics.find((metric) => metric.fromId === id);

  if (!previousMetric && !nextMetric) {
    return null;
  }

  const previousGap = previousMetric ? Math.round(previousMetric.gap + appliedDeltaY) : null;
  const nextGap = nextMetric ? Math.round(nextMetric.gap - appliedDeltaY) : null;
  const equalized = previousGap !== null && nextGap !== null && Math.abs(previousGap - nextGap) <= 1;

  const buildGuide = (metric, startY, endY, gapValue, isSnapTarget) => {
    const labelParts = [`${gapValue}px`];

    if (snapReason === 'grid' && isSnapTarget && gridSize > 0) {
      labelParts.push(`${gridSize}px Grid`);
    } else if (snapReason === 'common' && isSnapTarget) {
      labelParts.push('Snap');
    } else if (snapReason === 'equal' || equalized) {
      labelParts.push('gleich');
    }

    const guideColor = snapReason === 'equal' || equalized
      ? '#16A34A'
      : (isSnapTarget
        ? (snapReason === 'grid' ? '#2563EB' : '#E07000')
        : '#0F766E');

    return {
      key: metric.key,
      color: guideColor,
      label: labelParts.join(' · '),
      x: metric.guideX,
      top: Math.round(Math.min(startY, endY)),
      height: Math.max(12, Math.round(Math.abs(endY - startY))),
    };
  };

  const guides = [];

  if (previousMetric) {
    const startY = previousMetric.fromBottom;
    const endY = previousMetric.toTop + appliedDeltaY;
    guides.push(buildGuide(
      previousMetric,
      startY,
      endY,
      previousGap,
      snapReason === 'equal' || snapMetricKey === previousMetric.key,
    ));
  }

  if (nextMetric) {
    const startY = nextMetric.fromBottom + appliedDeltaY;
    const endY = nextMetric.toTop;
    guides.push(buildGuide(
      nextMetric,
      startY,
      endY,
      nextGap,
      snapReason === 'equal' || snapMetricKey === nextMetric.key,
    ));
  }

  const badges = [];

  if (snapReason === 'common' && snapTarget !== null) {
    badges.push({ label: `Snap ${snapTarget}px`, tone: 'amber' });
  }

  if (snapReason === 'grid' && snapTarget !== null) {
    badges.push({ label: `${gridSize}px Grid`, tone: 'blue' });
  }

  if (snapReason === 'equal' || equalized) {
    badges.push({ label: 'Abstände gleich groß', tone: 'green' });
  }

  return {
    elementId: id,
    guides,
    badges,
    previousGap,
    nextGap,
    summary: equalized
      ? 'Oben und unten liegen die Abstände jetzt auf demselben Wert.'
      : snapReason === 'common'
        ? 'Der Abstand wurde auf einen bereits vorhandenen Systemwert eingerastet.'
        : snapReason === 'grid'
          ? 'Der Abstand folgt dem gewählten Grid-Raster.'
          : 'Vertikalen Abstand per Drag oder Eingabe justieren.',
  };
};

const normalizeDachLayoutPositions = (positions = {}) => {
  const sanitizeLayoutY = (id, rawValue) => {
    const numericValue = Math.round(Number(rawValue) || 0);

    // Keep deckblatt locked to its baseline to prevent persistent page-one drift.
    if (id === 'S1-Deckblatt') {
      return 0;
    }

    // Guardrails for deckblatt detail blocks so saved offsets cannot hide them off-canvas.
    if (id === 'S1-Deckblatt-Inhalt') {
      return Math.max(-24, Math.min(160, numericValue));
    }

    if (id === 'S1-Deckblatt-Meta') {
      return Math.max(-260, Math.min(24, numericValue));
    }

    return numericValue;
  };

  const normalized = Object.fromEntries(
    Object.entries(positions).map(([id, position]) => [
      id,
      {
        x: 0,
        y: sanitizeLayoutY(id, position?.y),
      },
    ])
  );

  const sectionY = Number(normalized['S1-Deckblatt']?.y) || 0;
  const legacyDeckblattOffsets = [
    Number(normalized['S1-Deckblatt-Inhalt']?.y) || 0,
    Number(normalized['S1-Deckblatt-Meta']?.y) || 0,
  ].filter((value) => value !== 0);

  if (sectionY === 0 && legacyDeckblattOffsets.length > 0) {
    normalized['S1-Deckblatt'] = {
      x: 0,
      y: sanitizeLayoutY('S1-Deckblatt', legacyDeckblattOffsets.reduce((sum, value) => sum + value, 0) / legacyDeckblattOffsets.length),
    };
  }

  return normalized;
};

const getDefaultLayoutPositionsForView = (view) => {
  const baseView = getBaseOfferView(view);

  if (baseView === 'dach') {
    return DEFAULT_DACH_LAYOUT_POSITIONS;
  }

  if (baseView === 'speicher') {
    return DEFAULT_SPEICHER_LAYOUT_POSITIONS;
  }

  return {};
};

const resolveGeneratedOfferLayoutPositions = (view, positions = {}) => ({
  ...getDefaultLayoutPositionsForView(view),
  ...(positions || {}),
});

const deepMergeObjects = (baseValue, overrideValue) => {
  if (Array.isArray(baseValue) || Array.isArray(overrideValue)) {
    return overrideValue !== undefined ? overrideValue : baseValue;
  }

  if (baseValue && typeof baseValue === 'object' && overrideValue && typeof overrideValue === 'object') {
    const merged = { ...baseValue };

    Object.keys(overrideValue).forEach((key) => {
      merged[key] = deepMergeObjects(baseValue[key], overrideValue[key]);
    });

    return merged;
  }

  return overrideValue !== undefined ? overrideValue : baseValue;
};

const DACH_TEXT_REPLACEMENTS = [
  ['ausschliesslich', 'ausschließlich'],
  ['Empfaenger', 'Empfänger'],
  ['Dachflaechen', 'Dachflächen'],
  ['fuer', 'für'],
  ['Fuer', 'Für'],
  ['uebernimmt', 'übernimmt'],
  ['Haeufige', 'Häufige'],
  ['Rueckmeldung', 'Rückmeldung'],
  ['Gruessen', 'Grüßen'],
  ['Gueltig', 'Gültig'],
  ['naechsten', 'nächsten'],
  ['zaehlen', 'zählen'],
  ['EN:POWER GmbH', 'EN:POWER'],
];

const sanitizeDachTextContent = (value) => {
  if (typeof value === 'string') {
    return DACH_TEXT_REPLACEMENTS.reduce((text, [from, to]) => text.split(from).join(to), value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeDachTextContent(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, sanitizeDachTextContent(item)]));
  }

  return value;
};

const SANIERUNG_DACH_CONTENT_OVERRIDES = {
  cover: {
    eyebrow: 'Photovoltaik • Dachsanierung • Dachverpachtung',
  },
  page2: {
    company: {
      header: {
        headline: 'DACH SANIEREN UND SOLARENERGIE NUTZEN',
      },
      intro: {
        paragraph1: 'EN:POWER verbindet die wirtschaftliche Entwicklung Ihrer Dachfläche mit der Sanierung der Dachhaut. So entsteht ein belastbares PV-Projekt, ohne dass Sie selbst investieren müssen.',
        paragraph2: 'Wir koordinieren Sanierungsplanung, technische Abstimmung, Netzanschluss und die anschließende PV-Installation aus einer Hand.',
      },
      quote: {
        quote: 'Erst wird die Dachhaut saniert, dann arbeitet das Dach langfristig für Sie.',
        footnote: 'EN:POWER übernimmt Koordination, Umsetzung und Betrieb.',
      },
    },
  },
  page3: {
    project: {
      header: {
        eyebrow: 'Dachsanierung und PV',
        headline: 'IHR SANIERUNGSANGEBOT',
      },
      intro: 'Die Voranalyse Ihrer Dachfläche und der erforderlichen Sanierung ergab, vorbehaltlich einer Vor-Ort-Prüfung, folgende Eckdaten:',
      processLabel: 'Ablauf: Sanierung vor PV-Installation',
      processSteps: [
        { icon: 'search', num: '1', title: 'Dachanalyse', desc: 'Technische Prüfung der Fläche' },
        { icon: 'fileText', num: '2', title: 'Angebot', desc: 'Indikatives Sanierungs- und PV-Angebot' },
        { icon: 'contract', num: '3', title: 'Vertrag', desc: 'Dachnutzungs- und Sanierungsvereinbarung' },
        { icon: 'settings', num: '4', title: 'Genehmigung', desc: 'Netzanschluss und Abstimmung durch uns' },
        { icon: 'building', num: '5', title: 'Sanierung', desc: 'Erneuerung der Dachhaut' },
        { icon: 'hammer', num: '6', title: 'PV-Bau', desc: 'Installation nach der Dachsanierung' },
      ],
    },
  },
  pricing: {
    meta: {
      angebotstypValue: 'Dachsanierung + PV-Pacht - indikativ',
    },
    // Texte der einzelnen Sanierungs-Preisbox. Müssen mit den Defaults in
    // PricingSection.jsx übereinstimmen, damit das Textbearbeitungs-Panel den
    // tatsächlich angezeigten Stand vorbelegt (statt leerer Felder).
    sanierung: {
      headerLabel: 'Dachsanierung',
      headerSub: 'Sanierung Ihres Daches als Gegenleistung für die Dachnutzung',
      priceLabel: 'Wert der Sanierung',
      priceFootnote: 'netto zzgl. MwSt.',
      values: {
        leistung: 'Sanierung der Dachhaut inkl. Montagevorbereitung',
        zeitpunkt: 'Vor dem Bau der PV-Anlage',
      },
    },
    notice: 'Alle Angaben sind indikativ und vorbehaltlich einer Vor-Ort-Besichtigung, eines Sanierungsaufmaßes und der Netzrückmeldung. Sie erhalten die Dachsanierung als Gegenleistung für die Dachnutzung – nicht als Geldbetrag zur Verrechnung. Übersteigen die tatsächlichen Sanierungskosten den ausgewiesenen Wert, entsteht für Sie keine Nachzahlung. Die PV-Installation erfolgt nach Abschluss der Sanierung der Dachhaut. EN:POWER trägt die vereinbarten Kosten für Planung, Sanierung, Installation, Betrieb und Versicherung.',
  },
  alternatives: {
    header: {
      headline: 'OPTIONEN FÜR IHR DACH',
    },
    option1: {
      title: 'Sanierung + Dachnutzung',
      description: 'EN:POWER verbindet die Dachsanierung mit einer langfristigen PV-Nutzung und schafft planbare Einnahmen ohne Eigeninvestition.',
      hint: 'Sanierung vor Installation',
    },
    option2: {
      title: 'Persönliche Abstimmung',
      description: 'Wir klären gemeinsam Dachzustand, Sanierungsumfang, Zeitplan und die passende Vergütungsstruktur für Ihr Objekt.',
      hint: 'Kostenlose Erstberatung',
    },
  },
  page4: {
    nextSteps: {
      header: {
        eyebrow: 'Ihr Weg zu Sanierung und Pachteinnahmen',
      },
      steps: [
        {
          num: '01',
          icon: 'fileSignature',
          active: true,
          title: 'Dachnutzungsvertrag',
          desc: 'Wir halten Sanierungsumfang, Dachnutzung, Vergütung und Zeitplan transparent im Vertrag fest.',
          action: null,
        },
        {
          num: '02',
          icon: 'phoneCall',
          active: false,
          title: 'Netz- und Sanierungsprüfung',
          desc: 'Nach Vertragsabschluss koordinieren wir Netzbetreiber, Vor-Ort-Prüfung und die Sanierung der Dachhaut vor der PV-Installation.',
          action: 'Vollmacht an: info@enpower.de',
        },
      ],
    },
    faq: {
      items: [
        { q: 'Wann wird die PV-Anlage installiert?', a: 'Die Installation erfolgt erst nach Abschluss und Abnahme der Sanierung der Dachhaut. So bleibt die Reihenfolge technisch sauber und planbar.' },
        { q: 'Was beinhaltet die Dachsanierung?', a: 'Der konkrete Umfang wird nach der Vor-Ort-Prüfung festgelegt. Typisch sind Dachhaut, Abdichtung und Montagevorbereitung für die PV-Anlage.' },
        { q: 'Wer koordiniert die Gewerke?', a: 'EN:POWER koordiniert Sanierungsplanung, Netzanschluss, PV-Installation und spätere Betriebsführung aus einer Hand.' },
        { q: 'Entstehen mir Kosten?', a: 'Für den vereinbarten Sanierungs- und PV-Umfang entstehen Ihnen keine eigenen Investitionskosten. Details regelt der Vertrag.' },
        { q: 'Was passiert nach Ablauf der Laufzeit?', a: 'Sie können die PV-Anlage kostenlos übernehmen, den Vertrag verlängern oder die Demontage veranlassen - ganz nach Vereinbarung.' },
        { q: 'Ist das Angebot verbindlich?', a: 'Nein. Das Indikationsangebot ist unverbindlich und wird erst nach Vor-Ort-Prüfung und beidseitiger Vertragsunterzeichnung verbindlich.' },
      ],
    },
    cta: {
      headline: 'Lassen Sie uns Sanierung und PV sauber planen.',
    },
  },
};

const normalizeDachTextContent = (content = {}) => sanitizeDachTextContent(deepMergeObjects(DACH_DATA.text || {}, content || {}));

const getValueByPath = (source, path) => path.split('.').reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), source);

const setValueByPath = (source, path, value) => {
  const keys = path.split('.');
  const nextRoot = { ...(source || {}) };
  let cursor = nextRoot;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }

    const nextValue = cursor[key];
    cursor[key] = nextValue && typeof nextValue === 'object' ? { ...nextValue } : {};
    cursor = cursor[key];
  });

  return nextRoot;
};

const loadStoredDachLayout = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(DACH_LAYOUT_STORAGE_KEY);
    return raw ? normalizeDachLayoutPositions(JSON.parse(raw)) : {};
  } catch (error) {
    console.warn('Failed to load stored Dach layout positions.', error);
    return {};
  }
};

const loadStoredDachTextContent = () => {
  if (typeof window === 'undefined') {
    return normalizeDachTextContent();
  }

  try {
    const raw = window.localStorage.getItem(DACH_TEXT_STORAGE_KEY);
    const normalizedContent = raw ? normalizeDachTextContent(JSON.parse(raw)) : normalizeDachTextContent();

    if (raw) {
      window.localStorage.setItem(DACH_TEXT_STORAGE_KEY, JSON.stringify(normalizedContent));
    }

    return normalizedContent;
  } catch (error) {
    console.warn('Failed to load stored Dach text content.', error);
    return normalizeDachTextContent();
  }
};

const normalizeGenericOverrideText = (value) => String(value || '').replace(/\u00a0/g, ' ').trim();

const STALE_FREIFLAECHE_PROCESS_TEXT_OVERRIDES = {
  'div-11/div-4/div-1/div-1/div-3/p-1': ['Angebot'],
  'div-11/div-4/div-1/div-1/div-4/p-1': ['Vertrag'],
  'div-11/div-4/div-1/div-1/div-5/p-1': ['Bau'],
  'div-11/div-4/div-1/div-1/div-5/p-2': ['B-Plan & Netzanschluss durch uns'],
  'div-11/div-4/div-1/div-1/div-6/p-1': ['Baubeginn', 'Betrieb'],
  'div-11/div-4/div-1/div-1/div-6/p-2': ['Netzanschluss des Solarparks', 'Errichtung des Solarparks', '30+ Jahre Betrieb & Wartung'],
};

const STALE_GENERIC_TEXT_OVERRIDE_VALUES = {
  freiflaeche: [
    'Absichtserklärung unterzeichnen',
    'Wir sichern Ihre Fläche mit einer unverbindlichen Absichtserklärung. Parallel prüfen wir die technische Machbarkeit, Netzkapazität und starten das B-Plan-Verfahren.',
    'Wir melden uns bei Ihnen!',
    'Nach Abschluss der Vorprüfung kontaktieren wir Sie mit einem verbindlichen Nutzungsvertrag. EN:POWER × ILOS übernehmen alle weiteren Schritte bis zum Baubeginn.',
    'Kontakt: info@enpower.de',
  ],
  speicher: [
    'Schwarmspeicher',
    'Schwarmspeicher – indikativ',
    'BESS / Großspeicher',
    'BESS / Großspeicher – indikativ',
    '1.000 kW (8 x 125 kW)',
    '1.000 kW (8 × 125 kW)',
    '8 x 125 kW',
    '8 × 125 kW',
    '2 × 5 J. (5% Ertragsbeteil.)',
    '2 x 5 J. (5% Ertragsbeteil.)',
    'Netzprüfung',
    'Prüfung der Netzkapazität vor Ort',
    'Installation',
    'Aufstellung & Netzanschluss',
    'Betrieb',
    '20+ Jahre Betrieb & Wartung',
    'Netzprüfung beauftragen',
    'Wir prüfen die Netzkapazität an Ihrem Standort. Wir benötigen lediglich Ihre Zustimmung und die Adresse. Ergebnis liegt in ca. 4–6 Wochen vor.',
    'Wir melden uns bei Ihnen!',
    'Nach positiver Netzrückmeldung erstellen wir ein verbindliches Angebot. Die Installation der Speicher erfolgt innerhalb weniger Wochen danach.',
    'Kontakt: info@enpower.de',
  ],
};

const sanitizeGenericTextContentForView = (view, content) => {
  const normalizedContent = content && typeof content === 'object' && !Array.isArray(content) ? content : {};
  const staleTextValues = STALE_GENERIC_TEXT_OVERRIDE_VALUES[view] || [];

  return Object.fromEntries(
    Object.entries(normalizedContent).filter(([textNodeId, value]) => {
      const normalizedValue = normalizeGenericOverrideText(value);

      if (staleTextValues.some((staleValue) => normalizeGenericOverrideText(staleValue) === normalizedValue)) {
        return false;
      }

      const staleValues = view === 'freiflaeche' ? STALE_FREIFLAECHE_PROCESS_TEXT_OVERRIDES[textNodeId] : null;

      if (!staleValues) {
        return true;
      }

      return !staleValues.some((staleValue) => normalizeGenericOverrideText(staleValue) === normalizedValue);
    })
  );
};

const normalizeGenericTextOverrides = (overrides) => ({
  freiflaeche: sanitizeGenericTextContentForView('freiflaeche', overrides?.freiflaeche),
  speicher: sanitizeGenericTextContentForView('speicher', overrides?.speicher),
  bess: sanitizeGenericTextContentForView('bess', overrides?.bess),
  'expose-projektverkauf': sanitizeGenericTextContentForView('expose-projektverkauf', overrides?.['expose-projektverkauf']),
  'expose-turnkey': sanitizeGenericTextContentForView('expose-turnkey', overrides?.['expose-turnkey']),
});

const loadStoredGenericTextOverrides = () => {
  if (typeof window === 'undefined') {
    return { freiflaeche: {}, speicher: {}, bess: {}, 'expose-projektverkauf': {}, 'expose-turnkey': {} };
  }

  try {
    const raw = window.localStorage.getItem(GENERIC_TEXT_OVERRIDES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const normalizedOverrides = normalizeGenericTextOverrides(parsed);

    if (raw) {
      window.localStorage.setItem(GENERIC_TEXT_OVERRIDES_STORAGE_KEY, JSON.stringify(normalizedOverrides));
    }

    return normalizedOverrides;
  } catch (error) {
    console.warn('Failed to load stored generic text overrides.', error);
    return { freiflaeche: {}, speicher: {}, bess: {}, 'expose-projektverkauf': {}, 'expose-turnkey': {} };
  }
};

const hasStoredDachLayout = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(DACH_LAYOUT_STORAGE_KEY) !== null;
};

const getDachElementLabel = (id) => DACH_SECTION_LABELS[id] || id;

const getLayoutStorageModeLabel = (mode) => {
  if (mode === 'supabase' || mode === 'blob') {
    return 'Supabase Storage';
  }

  if (mode === 'file') {
    return 'Projektordner';
  }

  if (mode === 'database') {
    return 'Datenbank';
  }

  return 'Browser-Cache';
};

const formatLayoutVersionDate = (value) => {
  if (!value) {
    return 'Unbekannt';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(parsedDate);
};

const VERTRIEB_PRINT_STYLE_ID = 'vertrieb-print-style';
const VERTRIEB_PRINT_STYLES = `
@page { size: 1280px 720px; margin: 0; }
@media print {
  html, body, #root {
    width: 1280px !important;
    min-width: 1280px !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #FFFFFF !important;
    overflow: visible !important;
  }

  body.printing-vertrieb .top-action-bar,
  body.printing-vertrieb .sidebar-editor,
  body.printing-vertrieb [data-print-hidden="true"] {
    display: none !important;
  }

  body.printing-vertrieb .app-container,
  body.printing-vertrieb .app-body,
  body.printing-vertrieb .proposal-staging,
  body.printing-vertrieb .proposal-pages,
  body.printing-vertrieb .vertrieb-reference-deck {
    display: block !important;
    width: 1280px !important;
    min-width: 1280px !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    gap: 0 !important;
    overflow: visible !important;
    background: #FFFFFF !important;
    box-shadow: none !important;
  }

  body.printing-vertrieb .vertrieb-reference-slide {
    width: 1280px !important;
    height: 720px !important;
    max-width: none !important;
    margin: 0 !important;
    border-radius: 0 !important;
    aspect-ratio: auto !important;
    box-shadow: none !important;
    overflow: hidden !important;
    break-after: page;
    page-break-after: always;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }

  body.printing-vertrieb .vertrieb-reference-slide:last-child {
    break-after: auto;
    page-break-after: auto;
  }

  body.printing-vertrieb .vertrieb-reference-slide-image {
    display: block !important;
    width: 1280px !important;
    height: 720px !important;
  }

  body.printing-vertrieb .vertrieb-editable-field,
  body.printing-vertrieb .vertrieb-number-field {
    border-color: transparent !important;
    box-shadow: none !important;
    outline: none !important;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
`;

const installVertriebPrintStyles = () => {
  document.getElementById(VERTRIEB_PRINT_STYLE_ID)?.remove();
  const style = document.createElement('style');
  style.id = VERTRIEB_PRINT_STYLE_ID;
  style.textContent = VERTRIEB_PRINT_STYLES;
  document.head.appendChild(style);
};

const cleanupVertriebPrintStyles = () => {
  document.body.classList.remove('printing-vertrieb');
  document.getElementById(VERTRIEB_PRINT_STYLE_ID)?.remove();
};

const waitForPrintAssets = async (rootNode) => {
  try {
    await document.fonts?.ready;
  } catch (error) {
    console.warn('Fonts were not fully ready before print.', error);
  }

  const images = Array.from(rootNode?.querySelectorAll('img') || []);
  await Promise.all(images.map((image) => {
    if (image.complete && image.naturalWidth > 0) {
      return Promise.resolve();
    }

    if (typeof image.decode === 'function') {
      return image.decode().catch(() => undefined);
    }

    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }));

  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
};

const cleanupOfferPrintState = () => {
  document.body.classList.remove('printing-offer');
};

const getGeneratedOfferIdFromLocation = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const params = new URLSearchParams(window.location.search);
  return GENERATED_OFFER_QUERY_KEYS.map((key) => params.get(key)).find(Boolean) || '';
};

const isGeneratedOfferPdfRenderRequest = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('pdf') === '1' && GENERATED_OFFER_QUERY_KEYS.some((key) => params.get(key));
};

const authScreenStyles = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
  background: 'linear-gradient(135deg, #EEF3FA 0%, #FFF7ED 32%, #F8FAFC 58%, #EAF3F0 100%)',
  backgroundAttachment: 'fixed',
  fontFamily: 'Manrope, Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
};

const authCardStyles = {
  width: '100%',
  maxWidth: '448px',
  background: 'rgba(255, 255, 255, 0.92)',
  border: '1px solid rgba(226, 232, 240, 0.72)',
  borderRadius: '16px',
  padding: '32px',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.02), 0 4px 8px rgba(15, 23, 42, 0.03), 0 12px 24px rgba(15, 23, 42, 0.05)',
};

const authInputStyles = {
  width: '100%',
  border: '1px solid #E2E8F0',
  borderRadius: '12px',
  padding: '10px 12px',
  fontFamily: 'Manrope, sans-serif',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

function AuthShell({ title, subtitle, children }) {
  return (
    // Die Klasse traegt nur die Regeln fuer die Bedienung mit dem Daumen
    // (siehe App.css) - das Aussehen kommt weiterhin aus den Inline-Stilen.
    <div className="auth-shell" style={authScreenStyles}>
      <Toaster position="top-right" richColors />
      <div style={authCardStyles}>
        <div style={{ textAlign: 'center', display: 'grid', justifyItems: 'center', gap: '10px', marginBottom: children ? '20px' : 0 }}>
          <img
            src="/enpower-logo-transparent.png"
            alt="EN:POWER"
            style={{ width: '210px', maxWidth: '82%', height: 'auto', display: 'block', marginBottom: '8px' }}
          />
          <h1 style={{ margin: 0, fontFamily: 'Outfit, Manrope, sans-serif', color: '#122A5C', fontSize: '24px', lineHeight: 1.18, fontWeight: 700, letterSpacing: '-0.04em' }}>
            {title}
          </h1>
          {subtitle && (
            <p style={{ margin: 0, maxWidth: '300px', color: '#64748B', fontSize: '14px', lineHeight: 1.45 }}>
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

// Bleibt stehen, solange die Verbindung fehlt. Die Umleitung fuehrt nur einmal
// je Browsersitzung hin; wer dort abbricht, soll den Weg zurueck trotzdem
// jederzeit finden, ohne dass ihm das Werkzeug verschlossen wird.
// neuesFenster: im Bitrix-Reiter - die Verbindungsseite laesst sich nicht im Rahmen oeffnen.
function BitrixVerbindungHinweis({ stand, kompakt = false, neuesFenster = false }) {
  if (!stand?.mussVerbinden) {
    return null;
  }

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        right: '16px',
        bottom: kompakt ? '88px' : '16px',
        zIndex: 9000,
        maxWidth: 'min(360px, calc(100vw - 32px))',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        padding: '14px 16px',
        borderRadius: '12px',
        background: '#122A5C',
        color: '#F8FAFC',
        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.32)',
        fontSize: '13px',
        lineHeight: 1.45,
      }}
    >
      <Lock size={18} color="#E07000" style={{ flex: '0 0 auto', marginTop: '1px' }} />
      <div style={{ display: 'grid', gap: '10px' }}>
        <span>
          Ihr Bitrix-Konto ist noch nicht verbunden. Ohne die Verbindung sehen Sie
          hier nicht alle Deals, die Ihnen zugeordnet sind.
        </span>
        <a
          href={baueVerbindungsadresse(typeof window === 'undefined' ? '' : window.location.href)}
          {...(neuesFenster ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          style={{
            justifySelf: 'start',
            padding: '8px 14px',
            borderRadius: '8px',
            background: '#E07000',
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: '13px',
            textDecoration: 'none',
          }}
        >
          Jetzt mit Bitrix verbinden
        </a>
      </div>
    </div>
  );
}

function useMedienAbfrage(abfrageText) {
  const [trifft, setTrifft] = useState(() => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(abfrageText).matches
  ));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const abfrage = window.matchMedia(abfrageText);
    const beiAenderung = (e) => setTrifft(e.matches);
    setTrifft(abfrage.matches);
    // addListener ist veraltet, wird aber von aelteren Android-Browsern noch gebraucht.
    if (abfrage.addEventListener) abfrage.addEventListener('change', beiAenderung);
    else abfrage.addListener(beiAenderung);
    return () => {
      if (abfrage.removeEventListener) abfrage.removeEventListener('change', beiAenderung);
      else abfrage.removeListener(beiAenderung);
    };
  }, [abfrageText]);

  return trifft;
}

// Wer am Handy "Zur Desktop-Ansicht" wählt, bleibt dort, bis er zurückwechselt
// oder die App neu startet - deshalb sessionStorage, nicht localStorage.
const DESKTOP_ERZWUNGEN_KEY = 'enpower.mobil.desktopAnsicht';

function leseDesktopErzwungen() {
  try {
    return window.sessionStorage.getItem(DESKTOP_ERZWUNGEN_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function merkeDesktopErzwungen(an) {
  try {
    if (an) window.sessionStorage.setItem(DESKTOP_ERZWUNGEN_KEY, '1');
    else window.sessionStorage.removeItem(DESKTOP_ERZWUNGEN_KEY);
  } catch (_) {
    // Gesperrter Speicher: dann gilt die Wahl nur bis zum Neuladen.
  }
}

// Tool-Rechte des angemeldeten Kontos. Desktop und Handy fragen dieselbe
// Stelle und rechnen gleich - das Handy blendet nur mehr aus.
function useToolBerechtigungen({ session, publicGeneratedOffer = false }) {
  const accessToken = publicGeneratedOffer ? '' : session?.access_token || '';
  const appAuthHeaders = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);
  const [toolPermissions, setToolPermissions] = useState({
    loading: !publicGeneratedOffer,
    isAdmin: false,
    email: session?.user?.email || '',
    access: publicGeneratedOffer ? PUBLIC_GENERATED_OFFER_ACCESS : {},
  });

  const refreshToolPermissions = useCallback(async () => {
    if (publicGeneratedOffer) {
      setToolPermissions({
        loading: false,
        isAdmin: false,
        email: session?.user?.email || '',
        access: PUBLIC_GENERATED_OFFER_ACCESS,
      });
      return;
    }

    if (!accessToken) {
      setToolPermissions({ loading: false, isAdmin: false, email: '', access: {} });
      return;
    }

    setToolPermissions((current) => ({ ...current, loading: true }));

    try {
      const response = await axios.get(`${LAYOUT_API}/contracts/settings`, { headers: appAuthHeaders });
      const email = response.data?.email || session?.user?.email || '';
      const builtInAdmin = isBuiltInAdminEmail(email);
      setToolPermissions({
        loading: false,
        isAdmin: response.data?.isAdmin === true || builtInAdmin,
        email,
        access: response.data?.toolAccess || {},
      });
    } catch (error) {
      console.error(error);
      const email = session?.user?.email || '';
      const builtInAdmin = isBuiltInAdminEmail(email);
      setToolPermissions({ loading: false, isAdmin: builtInAdmin, email, access: {} });
      toast.error(error?.response?.data?.detail || 'Tool-Rechte konnten nicht geladen werden.');
    }
  }, [accessToken, appAuthHeaders, publicGeneratedOffer, session?.user?.email]);

  useEffect(() => {
    refreshToolPermissions();
  }, [refreshToolPermissions]);

  const isAdminUser = toolPermissions.isAdmin === true;
  const getAccessForView = useCallback((viewKey) => {
    if (viewKey === 'settings') {
      return isAdminUser ? FULL_TOOL_ACCESS : EMPTY_TOOL_ACCESS;
    }

    if (isAdminUser) {
      return FULL_TOOL_ACCESS;
    }

    const toolKey = VIEW_TOOL_KEYS[viewKey];
    if (!toolKey) {
      return EMPTY_TOOL_ACCESS;
    }

    return normalizeClientToolAccess(toolPermissions.access?.[toolKey]);
  }, [isAdminUser, toolPermissions.access]);

  return { appAuthHeaders, toolPermissions, refreshToolPermissions, isAdminUser, getAccessForView };
}

// Scheitert das Nachladen der Handy-Oberfläche (wackliges Netz, oder nach
// einem Deploy fehlt die alte Datei), bliebe die App sonst weiß. Fehlergrenzen
// gibt es in React nur als Klasse.
class MobilFehlergrenze extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fehler: null };
  }

  static getDerivedStateFromError(fehler) {
    return { fehler };
  }

  componentDidCatch(fehler) {
    console.error('Handy-Oberfläche:', fehler);
  }

  render() {
    if (!this.state.fehler) return this.props.children;
    const knopf = { minHeight: 48, padding: '0 18px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' };
    return (
      <div role="alert" style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, background: '#F6F8FB', color: '#475569', fontSize: 15, textAlign: 'center' }}>
        <EnPowerLogoText />
        <span>Die Handy-Ansicht ließ sich nicht laden. Meist hilft Neuladen – etwa nach einem Update oder bei schwachem Netz.</span>
        <button type="button" onClick={() => window.location.reload()} style={{ ...knopf, border: 'none', background: '#E07000', color: '#FFFFFF' }}>Neu laden</button>
        {this.props.onDesktopAnsicht ? (
          <button type="button" onClick={this.props.onDesktopAnsicht} style={{ ...knopf, border: '1px solid #D5DDEA', background: '#FFFFFF', color: '#122A5C' }}>Zur Desktop-Ansicht</button>
        ) : null}
      </div>
    );
  }
}

function MobilLaden() {
  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: '#F6F8FB', color: '#475569', fontSize: 15 }}>
      <EnPowerLogoText />
      <span>Wird geladen …</span>
    </div>
  );
}

// Die mobile Oberfläche lädt nur die Rechte - nicht die Angebots-Layouts,
// Exposés und alles andere, was AuthenticatedApp beim Start holt.
function MobilApp({ session, onLogout, onDesktopAnsicht }) {
  const { appAuthHeaders, toolPermissions, refreshToolPermissions, isAdminUser, getAccessForView } = useToolBerechtigungen({ session });
  const nutzer = useMemo(() => ({
    email: toolPermissions.email || session?.user?.email || '',
    isAdmin: isAdminUser,
  }), [isAdminUser, session?.user?.email, toolPermissions.email]);

  // Beim ersten Laden warten; ein späteres Neuladen der Rechte (nach dem
  // Speichern unter Tool-Rechte) darf die Hülle nicht abbauen.
  const [ersteRechteDa, setErsteRechteDa] = useState(false);
  useEffect(() => {
    if (!toolPermissions.loading) setErsteRechteDa(true);
  }, [toolPermissions.loading]);

  return (
    <>
      <Toaster position="top-center" richColors />
      {ersteRechteDa ? (
        <MobilFehlergrenze onDesktopAnsicht={onDesktopAnsicht}>
          <React.Suspense fallback={<MobilLaden />}>
            <MobilHuelle
              apiBase={LAYOUT_API}
              session={session}
              authHeaders={appAuthHeaders}
              nutzer={nutzer}
              getAccessForView={getAccessForView}
              onLogout={onLogout}
              onDesktopAnsicht={onDesktopAnsicht}
              onRechteGeaendert={refreshToolPermissions}
            />
          </React.Suspense>
        </MobilFehlergrenze>
      ) : <MobilLaden />}
    </>
  );
}

const EINGEBETTET_ANSICHT = 'vertraege-am-dokumenten-assistent';
const EINGEBETTET_KNOPF = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 8,
  border: '1px solid #D5DDEA', background: '#FFFFFF', color: '#122A5C', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', textDecoration: 'none',
};

// Reiter "Dokumenten-Assistent" im Bitrix-Deal mit Anmeldung aus Bitrix (seit 25.10.2026, wie
// beim Lageplan-Tool): kein Supabase-Login, der Server erkennt den Admin am Kopf
// x-bitrix-session (api/_lib/bitrixSitzung.js). Abgelaufen oder kein Admin -> onUngueltig.
function BitrixDokumentenAssistent({ kennwort, dealId, onUngueltig }) {
  const kopf = useMemo(() => ({ [BITRIX_SITZUNG_KOPF]: kennwort }), [kennwort]);
  const [ich, setIch] = useState(null);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    let aktiv = true;
    setIch(null);
    setFehler('');
    axios.post(`${LAYOUT_API}/automation/dokumenten-assistent`, { op: 'ich' }, { headers: kopf, timeout: 20000 })
      .then(({ data }) => { if (aktiv) setIch(data); })
      .catch((antwortFehler) => {
        if (!aktiv) return;
        const status = antwortFehler?.response?.status;
        if (status === 401) onUngueltig('abgelaufen');
        else if (status === 403) onUngueltig('kein_admin');
        else setFehler(antwortFehler?.response?.data?.detail || 'Die Anmeldung über Bitrix ließ sich gerade nicht prüfen.');
      });
    return () => { aktiv = false; };
  }, [kopf, onUngueltig]);

  if (fehler) {
    return (
      <AuthShell title="Gerade nicht erreichbar" subtitle={fehler}>
        <div style={{ display: 'grid', justifyItems: 'center' }}>
          <button type="button" onClick={() => window.location.reload()} style={EINGEBETTET_KNOPF}>
            Neu laden
          </button>
        </div>
      </AuthShell>
    );
  }
  if (!ich) return <MobilLaden />;

  const kopfRechts = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: '#64748B' }}>{ich.email} · über Bitrix angemeldet</span>
      <a href={adresseImTool(EINGEBETTET_ANSICHT)} target="_blank" rel="noopener noreferrer" style={EINGEBETTET_KNOPF}>
        Im Tool öffnen
      </a>
    </span>
  );
  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <Toaster position="top-center" richColors />
      <DokumentenAssistent
        apiBase={LAYOUT_API}
        anmeldeKopf={kopf}
        festeDealId={dealId}
        kopfRechts={kopfRechts}
        hinweis={dealId ? '' : OHNE_DEAL_HINWEIS}
      />
    </div>
  );
}

// Reiter "Dokumenten-Assistent" im Bitrix-Deal mit Supabase-Login (Rueckfall, wenn die Anmeldung
// aus Bitrix nicht ging): nur diese Ansicht, fest auf den Deal des Reiters, nur fuer Admins
// (Nutzer, 25.10.2026). Laedt wie MobilApp nur die Rechte.
function EingebetteterDokumentenAssistent({ session, dealId, onLogout }) {
  const { toolPermissions, isAdminUser } = useToolBerechtigungen({ session });
  const ansicht = EINGEBETTET_ANSICHT;
  const [ersteRechteDa, setErsteRechteDa] = useState(false);
  useEffect(() => {
    if (!toolPermissions.loading) setErsteRechteDa(true);
  }, [toolPermissions.loading]);

  if (!ersteRechteDa) return <MobilLaden />;

  const email = toolPermissions.email || session?.user?.email || '';
  const leiseKnopf = EINGEBETTET_KNOPF;
  const kopfRechts = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {email ? <span style={{ fontSize: 12, color: '#64748B' }}>{email}</span> : null}
      <a href={adresseImTool(ansicht)} target="_blank" rel="noopener noreferrer" style={leiseKnopf}>
        Im Tool öffnen
      </a>
      <button type="button" onClick={onLogout} style={leiseKnopf}>
        <LogOut size={12} /> Abmelden
      </button>
    </span>
  );

  if (!isAdminUser) {
    return (
      <AuthShell title="Nur für Admins" subtitle={`${email || 'Dieses Konto'} ist kein Admin.`}>
        <div style={{ display: 'grid', gap: 14, color: '#475569', fontSize: 13, lineHeight: 1.5 }}>
          <span>Der Dokumenten-Assistent im Bitrix-Deal ist nur für Admins. Im Tool selbst gilt weiter das Häkchen „Verträge – Dokumenten-Assistent“.</span>
          <button type="button" onClick={onLogout} style={{ ...leiseKnopf, justifySelf: 'center' }}>
            <LogOut size={12} /> Mit anderem Konto anmelden
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <Toaster position="top-center" richColors />
      <DokumentenAssistent
        apiBase={LAYOUT_API}
        session={session}
        festeDealId={dealId}
        kopfRechts={kopfRechts}
        hinweis={dealId ? '' : OHNE_DEAL_HINWEIS}
      />
    </div>
  );
}

function App() {
  const supabase = useMemo(() => getSupabaseClient(), []);
  const passwordAuthRoute = getPasswordAuthRoute();
  const publicGeneratedOfferPdf = isGeneratedOfferPdfRenderRequest();
  const isAndroidAppShell = useMemo(() => (
    typeof navigator !== 'undefined'
    && /ENPOWERAndroidApp\/\d/i.test(navigator.userAgent || '')
  ), []);

  // Die kompakte Oberflaeche - Leiste unten statt Seitenleiste - war bisher der
  // Android-App vorbehalten. Sie ist aber genau das, was auch ein Handy-Browser
  // braucht: grosse Schaltflaechen, keine schmale Seitenleiste, alles in
  // Daumenreichweite. Deshalb entscheidet ab hier die Fensterbreite mit.
  const istSchmalerBildschirm = useMedienAbfrage('(max-width: 900px)');
  const nutzeKompakteOberflaeche = isAndroidAppShell || istSchmalerBildschirm;

  // Seit 23.09.2026 bekommt jedes Handy (Android-App oder Fenster < 720 px)
  // die mobile Oberfläche. Tablets zwischen 720 und 900 px behalten die
  // kompakte Desktop-Oberfläche.
  const istHandyBreite = useMedienAbfrage('(max-width: 719px)');
  // Handy im Querformat: breiter als 719 px, aber flach und mit dem Finger bedient.
  const istHandyQuer = useMedienAbfrage('(max-height: 500px) and (pointer: coarse)');
  const istTouch = useMedienAbfrage('(pointer: coarse)');
  const [desktopErzwungen, setDesktopErzwungen] = useState(leseDesktopErzwungen);
  // Einmal Handy, immer Handy (bis zum Neuladen): Drehen darf die mobile
  // Oberfläche samt ungespeicherter Entwürfe nicht abbauen (Review 23.09.2026).
  // Nur bei Touch-Geräten - am Desktop darf ein schmal gezogenes Fenster
  // wieder zur Desktop-Ansicht werden.
  const jetztHandy = isAndroidAppShell || istHandyBreite || istHandyQuer;
  const [bleibtHandy, setBleibtHandy] = useState(false);
  useEffect(() => {
    if (jetztHandy && istTouch) setBleibtHandy(true);
  }, [jetztHandy, istTouch]);
  const mobilMoeglich = jetztHandy || bleibtHandy;
  const nutzeMobil = mobilMoeglich && !desktopErzwungen;
  const zurDesktopAnsicht = useCallback(() => {
    merkeDesktopErzwungen(true);
    setDesktopErzwungen(true);
  }, []);
  const zurHandyAnsicht = useCallback(() => {
    merkeDesktopErzwungen(false);
    setDesktopErzwungen(false);
  }, []);
  // Reiter im Bitrix-Deal: nur der Dokumenten-Assistent (seit 25.10.2026).
  const einbettung = useMemo(() => leseEinbettung(typeof window === 'undefined' ? '' : window.location.search), []);
  // Anmeldung aus Bitrix (#bx=…) gleich beim Start aus der Adresse nehmen - vor allem anderen.
  const [bitrixAnmeldung, setBitrixAnmeldung] = useState(() => (
    einbettung.aktiv && typeof window !== 'undefined' ? nimmBitrixAnmeldung(window) : { kennwort: '', grund: '' }
  ));
  const bitrixAnmeldungUngueltig = useCallback((grund) => {
    vergissBitrixSitzung(window);
    setBitrixAnmeldung({ kennwort: '', grund: grund || 'abgelaufen' });
  }, []);
  const [authLoading, setAuthLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!supabase) {
      setAuthLoading(false);
      return () => {
        isMounted = false;
      };
    }

    supabase.auth.getSession().then(({ data }) => {
      if (isMounted) {
        setSession(data.session || null);
        setAuthLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setSession(currentSession || null);
      setAuthLoading(false);
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, [supabase]);

  // Bitrix-Verbindungspflicht. Erst fragen, wenn die Sitzung wirklich steht -
  // sonst antwortet die Datenbank "nicht angemeldet" und wir schicken jemanden
  // weg, dessen Sitzung sich nur gerade aufbaut.
  const [bitrixVerbindung, setBitrixVerbindung] = useState(VERBINDUNG_UNBEKANNT);
  const angemeldeteKennung = session?.user?.id || '';

  useEffect(() => {
    // Der oeffentliche Angebotsabruf laeuft unter einer Pseudo-Sitzung und
    // gehoert Kunden ohne Konto. Die Ausnahme haengt am Flag, nie an der
    // Adresse - sonst landen Kunden auf einer Bitrix-Verbindungsseite.
    if (publicGeneratedOfferPdf || !supabase || !angemeldeteKennung) {
      setBitrixVerbindung(VERBINDUNG_UNBEKANNT);
      return undefined;
    }

    let nochAktuell = true;

    pruefeBitrixVerbindung(supabase).then((stand) => {
      if (!nochAktuell) return;
      setBitrixVerbindung(stand);
      // Im Bitrix-Reiter nicht umleiten: Die Verbindungsseite (Bitrix-Anmeldung) laesst sich
      // nicht im Rahmen oeffnen. Der Hinweis bleibt stehen und oeffnet sie im neuen Tab.
      if (stand.mussVerbinden && !einbettung.aktiv) {
        leiteZurVerbindungsseite();
      }
    });

    return () => {
      nochAktuell = false;
    };
  }, [supabase, angemeldeteKennung, publicGeneratedOfferPdf, einbettung.aktiv]);

  const handleLogin = useCallback(async (event) => {
    event?.preventDefault();

    if (!supabase) {
      toast.error('Supabase ist nicht konfiguriert.');
      return;
    }

    setLoginBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginData.email.trim(),
        password: loginData.password,
      });

      if (error) {
        throw error;
      }

      toast.success('Login erfolgreich.');
      setLoginData((current) => ({ ...current, password: '' }));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Login fehlgeschlagen.');
    } finally {
      setLoginBusy(false);
    }
  }, [loginData.email, loginData.password, supabase]);

  const handleLogout = useCallback(async () => {
    if (!supabase) {
      return;
    }

    // abmelden() raeumt die Sitzung auch dann weg, wenn der Server sie nicht mehr kennt;
    // dann kommt kein SIGNED_OUT-Ereignis, deshalb die Sitzung hier selbst leeren.
    const { lokalErzwungen, fehler } = await abmelden(supabase);
    if (lokalErzwungen) {
      console.warn('Logout: Supabase kannte die Sitzung nicht mehr, lokal beendet.', fehler);
    }
    setSession(null);
    toast.success('Logout erfolgreich.');
  }, [supabase]);

  if (publicGeneratedOfferPdf) {
    return <AuthenticatedApp session={PUBLIC_GENERATED_OFFER_SESSION} onLogout={() => {}} publicGeneratedOffer isAndroidAppShell={false} />;
  }

  if (!supabase) {
    return (
      <AuthShell title="Login" subtitle="Supabase ist noch nicht vollständig konfiguriert.">
        <div style={{ marginTop: '18px', display: 'flex', gap: '10px', alignItems: 'flex-start', color: '#475569', fontSize: '13px', lineHeight: 1.5 }}>
          <Lock size={18} color="#E07000" style={{ flex: '0 0 auto', marginTop: '1px' }} />
          <span>Bitte setzen Sie REACT_APP_SUPABASE_URL und REACT_APP_SUPABASE_ANON_KEY im Frontend-Projekt.</span>
        </div>
      </AuthShell>
    );
  }

  if (passwordAuthRoute) {
    return <PasswordResetFlow supabase={supabase} initialMode={passwordAuthRoute} />;
  }

  // Reiter im Bitrix-Deal mit Anmeldung aus Bitrix: ohne Supabase-Login (seit 25.10.2026).
  if (einbettung.aktiv && bitrixAnmeldung.kennwort) {
    return (
      <BitrixDokumentenAssistent
        kennwort={bitrixAnmeldung.kennwort}
        dealId={einbettung.dealId}
        onUngueltig={bitrixAnmeldungUngueltig}
      />
    );
  }

  if (authLoading) {
    return (
      <AuthShell title="Zugriff wird geprüft..." subtitle="Supabase Session wird geladen" />
    );
  }

  // Bitrix sagt: kein Admin. Ein Admin mit anderem Bitrix-Konto kann sich trotzdem anmelden.
  if (einbettung.aktiv && bitrixAnmeldung.grund === 'kein_admin' && !session) {
    return (
      <AuthShell title="Nur für Admins" subtitle={anmeldeHinweis('kein_admin')}>
        <div style={{ display: 'grid', justifyItems: 'center' }}>
          <button
            type="button"
            onClick={() => setBitrixAnmeldung({ kennwort: '', grund: 'kein_admin_anmelden' })}
            style={EINGEBETTET_KNOPF}
          >
            Mit Admin-Konto anmelden
          </button>
        </div>
      </AuthShell>
    );
  }

  if (!session) {
    return (
      <AuthShell
        title="Login"
        subtitle={(einbettung.aktiv && bitrixAnmeldung.grund && bitrixAnmeldung.grund !== 'kein_admin_anmelden'
          ? anmeldeHinweis(bitrixAnmeldung.grund)
          : '') || 'Anmelden mit den bestehenden Zugangsdaten Ihres Accounts'}
      >
        <form onSubmit={handleLogin} style={{ display: 'grid', gap: '14px' }}>
          <label style={{ display: 'grid', gap: '6px', color: '#64748B', fontSize: '12px', fontWeight: 600 }}>
            E-Mail
            <input
              type="email"
              placeholder="E-Mail-Adresse"
              value={loginData.email}
              onChange={(event) => setLoginData((current) => ({ ...current, email: event.target.value }))}
              style={authInputStyles}
            />
          </label>
          <label style={{ display: 'grid', gap: '6px', color: '#64748B', fontSize: '12px', fontWeight: 600 }}>
            Passwort
            <input
              type="password"
              placeholder="••••••••"
              value={loginData.password}
              onChange={(event) => setLoginData((current) => ({ ...current, password: event.target.value }))}
              style={authInputStyles}
            />
          </label>
          <button
            type="submit"
            disabled={loginBusy || !loginData.email.trim() || !loginData.password}
            style={{ border: 'none', borderRadius: '12px', padding: '13px 12px', marginTop: '2px', background: 'linear-gradient(90deg, #122A5C 0%, #1F3F7D 72%, #E07000 160%)', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontSize: '16px', fontWeight: 700, cursor: loginBusy ? 'wait' : 'pointer', opacity: loginBusy ? 0.78 : 1, boxShadow: '0 12px 24px rgba(18, 42, 92, 0.16)' }}
          >
            {loginBusy ? 'Anmeldung läuft...' : 'Anmelden'}
          </button>
          <a href={AUTH_FORGOT_PASSWORD_PATH} style={{ justifySelf: 'center', color: '#122A5C', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
            Passwort vergessen?
          </a>
        </form>
      </AuthShell>
    );
  }

  // Vor der Handy-Weiche: Der Rahmen in Bitrix ist oft schmaler als 720 px.
  if (einbettung.aktiv) {
    return (
      <>
        <EingebetteterDokumentenAssistent session={session} dealId={einbettung.dealId} onLogout={handleLogout} />
        <BitrixVerbindungHinweis stand={bitrixVerbindung} neuesFenster />
      </>
    );
  }

  if (nutzeMobil) {
    return (
      <>
        <MobilApp session={session} onLogout={handleLogout} onDesktopAnsicht={zurDesktopAnsicht} />
        <BitrixVerbindungHinweis stand={bitrixVerbindung} kompakt />
      </>
    );
  }

  return (
    <>
      <AuthenticatedApp session={session} onLogout={handleLogout} isAndroidAppShell={nutzeKompakteOberflaeche} />
      <BitrixVerbindungHinweis stand={bitrixVerbindung} kompakt={nutzeKompakteOberflaeche} />
      {mobilMoeglich ? (
        <button
          type="button"
          onClick={zurHandyAnsicht}
          style={{
            position: 'fixed',
            left: '12px',
            bottom: '88px',
            zIndex: 8999,
            minHeight: '44px',
            padding: '0 16px',
            borderRadius: '999px',
            border: '1px solid #D5DDEA',
            background: '#FFFFFF',
            color: '#122A5C',
            fontSize: '14px',
            fontWeight: 700,
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.16)',
          }}
        >
          Handy-Ansicht
        </button>
      ) : null}
    </>
  );
}

function AuthenticatedApp({ session, onLogout, publicGeneratedOffer = false, isAndroidAppShell = false }) {
  const initialActiveView = useMemo(() => (
    isAndroidAppShell && !publicGeneratedOffer ? 'vertraege-am-pending' : getInitialActiveView()
  ), [isAndroidAppShell, publicGeneratedOffer]);
  const [activeView, setActiveView] = useState(() => getBaseOfferView(initialActiveView));
  const [activeNavigationKey, setActiveNavigationKey] = useState(() => initialActiveView);
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [isExportingPng, setIsExportingPng] = useState(false);
  const [isExportingHtml, setIsExportingHtml] = useState(false);
  const [isLayoutEditMode, setIsLayoutEditMode] = useState(false);
  const [editorMode, setEditorMode] = useState('layout');
  const [isTopBarCollapsed, setIsTopBarCollapsed] = useState(false);
  const [showElementOutlines, setShowElementOutlines] = useState(true);
  const [layoutEditLevel, setLayoutEditLevel] = useState('detail');
  const [selectedLayoutElements, setSelectedLayoutElements] = useState([]);
  const [dachLayoutPositions, setDachLayoutPositions] = useState(() => loadStoredDachLayout());
  const [dachTextContent, setDachTextContent] = useState(() => loadStoredDachTextContent());
  const [genericTextOverrides, setGenericTextOverrides] = useState(() => loadStoredGenericTextOverrides());
  const [automationOffer, setAutomationOffer] = useState(null);
  const [selectedContractsDealId, setSelectedContractsDealId] = useState('');
  const [selectedContractsContractKey, setSelectedContractsContractKey] = useState('');
  const [lastLayoutSnapshot, setLastLayoutSnapshot] = useState(null);
  const [hasSavedDachLayout, setHasSavedDachLayout] = useState(() => hasStoredDachLayout());
  const [layoutVersions, setLayoutVersions] = useState([]);
  const [isLoadingStoredLayout, setIsLoadingStoredLayout] = useState(false);
  const [isSavingStoredLayout, setIsSavingStoredLayout] = useState(false);
  const [isRestoringStoredLayout, setIsRestoringStoredLayout] = useState(false);
  const [layoutStorageMode, setLayoutStorageMode] = useState('browser-cache');
  const [layoutStoragePath, setLayoutStoragePath] = useState('');
  const [layoutPersistenceError, setLayoutPersistenceError] = useState('');
  const [hasPendingOnlineLayoutChanges, setHasPendingOnlineLayoutChanges] = useState(false);
  const [isPublishingLayout, setIsPublishingLayout] = useState(false);
  const [publishedLayoutAt, setPublishedLayoutAt] = useState(null);
  const [sectionSpacingMetrics, setSectionSpacingMetrics] = useState([]);
  const [spacingDrafts, setSpacingDrafts] = useState({});
  const [showSpacingGuides, setShowSpacingGuides] = useState(true);
  const [enableSmartSpacingSnap, setEnableSmartSpacingSnap] = useState(true);
  const [spacingGridSize, setSpacingGridSize] = useState(0);
  const [activeSpacingFeedback, setActiveSpacingFeedback] = useState(null);
  const proposalRef = useRef(null);
  const dachLayoutPositionsRef = useRef(dachLayoutPositions);
  const dachTextContentRef = useRef(dachTextContent);
  const pendingLayoutSnapshotRef = useRef(null);
  const persistLayoutOnNextStateRef = useRef(false);
  const layoutSaveRequestIdRef = useRef(0);
  const sectionSpacingMetricsRef = useRef([]);
  const spacingDragMetricsRef = useRef([]);
  const layoutImportInputRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rawNotice = window.sessionStorage.getItem(EXPOSE_IMPORT_NOTICE_KEY);
    if (!rawNotice) return;
    window.sessionStorage.removeItem(EXPOSE_IMPORT_NOTICE_KEY);
    try {
      const notice = JSON.parse(rawNotice);
      if (notice?.type === 'success') {
        toast.success(`Exposé-Vorgang "${notice.name || 'Import'}" gespeichert.`);
      } else {
        toast.error(notice?.message || 'Exposé-Vorgang konnte nicht importiert werden.');
      }
    } catch (_) {
      toast.success('Exposé-Vorgang gespeichert.');
    }
  }, []);

  const { appAuthHeaders, toolPermissions, refreshToolPermissions, isAdminUser, getAccessForView } = useToolBerechtigungen({ session, publicGeneratedOffer });

  const activeToolAccess = getAccessForView(activeView);
  const canViewActiveTool = activeToolAccess.canView === true;
  const canCreateActiveTool = activeToolAccess.canCreate === true;
  const canEditActiveTool = activeToolAccess.canEdit === true;

  const isLayoutDragMode = isLayoutEditMode && editorMode === 'layout';
  const isTextEditMode = isLayoutEditMode && editorMode === 'text';
  const baseOfferView = getBaseOfferView(activeView);
  const isExposeTemplateView = activeView === 'expose-projektverkauf' || activeView === 'expose-turnkey';
  const isOfferTemplateView = baseOfferView === 'dach' || baseOfferView === 'freiflaeche' || baseOfferView === 'speicher' || isExposeTemplateView;
  const isEditableOfferView = isOfferTemplateView && canEditActiveTool;
    const supportsDocxExport = !['vertrieb', 'flyer', 'ffpv-flyer', 'ffpv-teaser', 'designsystem', 'vermittlervertrag', 'vertraege', 'exposes-automation', 'vertraege-am', 'vertraege-am-bearbeiten', 'vertraege-am-pending', 'vertraege-am-automatisierungen', 'vertraege-am-klauseln', 'standarddokumente', 'standarddokumente-vorlagen', 'onboarding-signature', 'expose-projektverkauf', 'expose-turnkey', 'settings', 'automation-uebersicht', 'zugangsanfragen', 'mail-konten', 'generator', 'eeg-rechner', 'noAccess'].includes(activeView);
  const activeSectionGroups = useMemo(() => (
    isDachTemplateView(activeView)
      ? DACH_SECTION_GROUPS
      : (activeView === 'freiflaeche' ? FREIFLAECHE_SECTION_GROUPS : (isSpeicherTemplateView(activeView) ? SPEICHER_SECTION_GROUPS : (activeView === 'expose-projektverkauf' ? EXPOSE_PROJEKTVERKAUF_SECTION_GROUPS : (activeView === 'expose-turnkey' ? EXPOSE_TURNKEY_SECTION_GROUPS : []))))
  ), [activeView]);
  const activeSectionLabels = useMemo(() => (
    isDachTemplateView(activeView)
      ? DACH_SECTION_LABELS
      : (activeView === 'freiflaeche' ? FREIFLAECHE_SECTION_LABELS : (isSpeicherTemplateView(activeView) ? SPEICHER_SECTION_LABELS : (activeView === 'expose-projektverkauf' ? EXPOSE_PROJEKTVERKAUF_SECTION_LABELS : (activeView === 'expose-turnkey' ? EXPOSE_TURNKEY_SECTION_LABELS : {}))))
  ), [activeView]);
  const activeDetailElementIds = useMemo(() => (
    isDachTemplateView(activeView)
      ? DACH_DETAIL_ELEMENT_IDS
      : (activeView === 'freiflaeche' ? FREIFLAECHE_DETAIL_ELEMENT_IDS : (isSpeicherTemplateView(activeView) ? SPEICHER_DETAIL_ELEMENT_IDS : (isExposeTemplateView ? EXPOSE_DETAIL_ELEMENT_IDS : [])))
  ), [activeView, isExposeTemplateView]);

  const dachProposalData = useMemo(() => (
    automationOffer?.view === 'dach'
      ? deepMergeObjects(DACH_DATA, automationOffer.proposalData || {})
      : DACH_DATA
  ), [automationOffer]);

  const freiflaecheProposalData = useMemo(() => (
    automationOffer?.view === 'freiflaeche'
      ? { ...PROPOSAL_DATA, ...(automationOffer.proposalData || {}) }
      : PROPOSAL_DATA
  ), [automationOffer]);

  const speicherProposalData = useMemo(() => (
    automationOffer?.view === 'speicher'
      ? { ...SPEICHER_PROPOSAL_DATA, ...(automationOffer.proposalData || {}) }
      : SPEICHER_PROPOSAL_DATA
  ), [automationOffer]);

  const exposeProposalData = useMemo(() => {
    const storedContent = genericTextOverrides[activeView] || {};
    const storedExposeFields = Object.fromEntries(
      Object.entries(storedContent).filter(([key]) => key && !key.startsWith('_'))
    );
    const storedProjectImage = String(storedContent.__projectImage || '').trim();
    const generatedData = automationOffer?.view === activeView
      ? (automationOffer.proposalData || {})
      : {};
    const baseData = automationOffer?.view === activeView
      ? EXPOSE_PROPOSAL_DATA
      : buildExposePlaceholderData(activeView);

    return {
      ...baseData,
      ...storedExposeFields,
      ...generatedData,
      ...(storedProjectImage ? { projectImage: storedProjectImage } : {}),
      exposeVariant: activeView,
    };
  }, [activeView, automationOffer, genericTextOverrides]);

  const effectiveDachProposalData = useMemo(() => (
    activeNavigationKey === 'sanierung' && automationOffer?.view !== 'dach'
      ? { ...dachProposalData, offerVariant: 'sanierung', offerTypeLabel: 'Sanierung' }
      : dachProposalData
  ), [activeNavigationKey, automationOffer, dachProposalData]);

  const effectiveDachTextContent = useMemo(() => (
    activeNavigationKey === 'sanierung' && automationOffer?.view !== 'dach'
      ? normalizeDachTextContent(SANIERUNG_DACH_CONTENT_OVERRIDES)
      : dachTextContent
  ), [activeNavigationKey, automationOffer, dachTextContent]);

  const effectiveSpeicherProposalData = useMemo(() => (
    activeNavigationKey === 'bess' && automationOffer?.view !== 'speicher'
      ? {
          ...speicherProposalData,
          offerVariant: 'bess',
          offerTypeLabel: 'Großspeicher',
          angebotstyp: 'Großspeicher',
          projektpartner: 'EN:POWER und Solarea',
          leistung_mw: '1',
          leistung_label: 'bis zu 1 MW',
          einheiten: '',
          stellflaeche: '~80',
          einmalig: '240.000',
          jaehrlich: '12.000',
          gesamt_jaehrlich: '240.000',
          laufzeit: '20',
          verlaengerungszahlung: '18.000',
        }
      : speicherProposalData
  ), [activeNavigationKey, automationOffer, speicherProposalData]);

  const dachPageLabels = activeNavigationKey === 'sanierung'
    ? {
        page1: 'Deckblatt Sanierung',
        page2: 'Sanierung + Vorteile',
        page3: 'Sanierungsangebot + Preisoptionen',
        page4: 'Ablauf + Nächste Schritte + FAQ',
      }
    : {
        page1: 'Deckblatt',
        page2: 'Unternehmensprofil + Vorteile',
        page3: 'Pachtangebot + Preisoptionen',
        page4: 'Alternativen + Nächste Schritte + FAQ',
      };

  const speicherPageLabels = activeNavigationKey === 'bess'
    ? {
        page1: 'Deckblatt Großspeicher',
        page2: 'BESS-Großspeicher + Vorteile',
        page3: 'Vergütungsangebot + Genehmigung',
        page4: 'Skalierung + Baugenehmigung + FAQ',
      }
    : {
        page1: 'Deckblatt',
        page2: 'Kompakter Energiespeicher + Vorteile',
        page3: 'Vergütungsangebot + 2 Zahlungsoptionen',
        page4: 'Skalierung + Nächste Schritte + FAQ',
      };

  const activeProposalDataForExport = useMemo(() => {
    if (isDachTemplateView(activeView)) {
      return effectiveDachProposalData;
    }

    if (isSpeicherTemplateView(activeView)) {
      return effectiveSpeicherProposalData;
    }

    if (isExposeTemplateView) {
      return exposeProposalData;
    }

    return freiflaecheProposalData;
  }, [activeView, effectiveDachProposalData, effectiveSpeicherProposalData, exposeProposalData, freiflaecheProposalData, isExposeTemplateView]);

  const getActiveDocumentKey = useCallback((view = activeNavigationKey) => {
    const baseView = getBaseOfferView(view);
    const normalizedView = String(view || '').trim().toLowerCase();
    const automationOfferNavigationKey = automationOffer ? getGeneratedOfferNavigationKey(automationOffer) : null;

    // Only reuse a generated offer key when the exact navigation context matches
    // (e.g. 'bess' must not inherit the generic 'speicher' key and vice versa).
    if (automationOfferNavigationKey === normalizedView && automationOffer?.documentKey) {
      return automationOffer.documentKey;
    }

    if (view === 'sanierung') {
      return 'dach-sanierung-standard';
    }

    if (baseView === 'dach') {
      return DACH_LAYOUT_DOCUMENT_KEY;
    }

    if (baseView === 'freiflaeche') {
      return `freiflaeche-${String(PROPOSAL_DATA.angebotsnummer || 'standard').trim().replace(/\s+/g, '-').toLowerCase()}`;
    }

    if (view === 'bess') {
      return 'speicher-bess-standard';
    }

    if (baseView === 'speicher') {
      return 'speicher-standard';
    }

    if (view === 'expose-projektverkauf') {
      return 'expose-pv-projektverkauf-standard';
    }

    if (view === 'expose-turnkey') {
      return 'expose-pv-turnkey-standard';
    }

    return 'angebot-standard';
  }, [activeNavigationKey, automationOffer]);

  const getCurrentOfferContentForStorage = useCallback((view = activeNavigationKey) => {
    if (view === 'sanierung') {
      return normalizeDachTextContent(SANIERUNG_DACH_CONTENT_OVERRIDES);
    }

    if (getBaseOfferView(view) === 'dach') {
      return normalizeDachTextContent(dachTextContentRef.current);
    }

    return sanitizeGenericTextContentForView(view, genericTextOverrides[view] || {});
  }, [activeNavigationKey, genericTextOverrides]);

  const activeGenericTextOverrideKey = activeView === 'speicher' && activeNavigationKey === 'bess'
    ? 'bess'
    : activeView;

  const getTextNodeOverrides = useCallback((content) => {
    const overrides = content?.[DACH_TEXT_NODE_OVERRIDES_PATH];

    if (!overrides || typeof overrides !== 'object') {
      return {};
    }

    return overrides;
  }, []);

  const buildTextNodeId = useCallback((rootNode, targetNode) => {
    const segments = [];
    let currentNode = targetNode;

    while (currentNode && currentNode !== rootNode && currentNode instanceof Element) {
      const tagName = currentNode.tagName.toLowerCase();
      const siblingElements = currentNode.parentElement
        ? Array.from(currentNode.parentElement.children).filter((sibling) => sibling.tagName === currentNode.tagName)
        : [currentNode];
      const index = Math.max(1, siblingElements.indexOf(currentNode) + 1);
      segments.unshift(`${tagName}-${index}`);
      currentNode = currentNode.parentElement;
    }

    return segments.join('/');
  }, []);

  const persistDachLayout = useCallback((positions, options = {}) => {
    if (typeof window === 'undefined') {
      return;
    }

    const normalizedPositions = normalizeDachLayoutPositions(positions);

    window.localStorage.setItem(DACH_LAYOUT_STORAGE_KEY, JSON.stringify(normalizedPositions));
    setHasSavedDachLayout(true);

    if (options.showToast) {
      toast.success(options.toastMessage || 'Layout gespeichert und aktiv.');
    }
  }, []);

  const persistDachTextContent = useCallback((content) => {
    if (typeof window === 'undefined') {
      return;
    }

    const normalizedContent = normalizeDachTextContent(content);
    window.localStorage.setItem(DACH_TEXT_STORAGE_KEY, JSON.stringify(normalizedContent));
  }, []);

  const persistGenericTextOverrides = useCallback((overrides) => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(GENERIC_TEXT_OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
  }, []);

  const handleGenericSpecialFieldChange = useCallback((fieldKey, value) => {
    const targetKey = activeGenericTextOverrideKey;
    setGenericTextOverrides((currentOverrides) => {
      const nextOverrides = {
        ...currentOverrides,
        [targetKey]: {
          ...(currentOverrides[targetKey] || {}),
          [fieldKey]: value,
        },
      };

      persistGenericTextOverrides(nextOverrides);
      return nextOverrides;
    });
    setHasPendingOnlineLayoutChanges(true);
  }, [activeGenericTextOverrideKey, persistGenericTextOverrides]);

  const applyStoredContentForView = useCallback((view, incomingContent) => {
    if (view === 'dach') {
      const nextContent = normalizeDachTextContent(incomingContent || {});
      setDachTextContent(nextContent);
      dachTextContentRef.current = nextContent;
      persistDachTextContent(nextContent);
      return;
    }

    const nextContent = sanitizeGenericTextContentForView(view, incomingContent);
    setGenericTextOverrides((currentOverrides) => {
      const nextOverrides = {
        ...currentOverrides,
        [view]: nextContent,
      };

      persistGenericTextOverrides(nextOverrides);
      return nextOverrides;
    });
  }, [persistDachTextContent, persistGenericTextOverrides]);

  useEffect(() => {
    const offerId = getGeneratedOfferIdFromLocation();

    if (!offerId || !LAYOUT_API) {
      return undefined;
    }

    let isCancelled = false;

    const loadGeneratedOffer = async () => {
      try {
        const response = await axios.get(`${LAYOUT_API}/offers/${encodeURIComponent(offerId)}`, {
          params: { t: Date.now() },
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (isCancelled) {
          return;
        }

        const offer = response.data || {};
        const offerView = ['dach', 'freiflaeche', 'speicher', 'expose-projektverkauf', 'expose-turnkey'].includes(offer.view) ? offer.view : 'dach';
        const offerNavigationKey = getGeneratedOfferNavigationKey({ ...offer, view: offerView });
        setAutomationOffer({ ...offer, view: offerView });
        setActiveView(offerView);
        setActiveNavigationKey(offerNavigationKey);

        if (offer.content && typeof offer.content === 'object') {
          applyStoredContentForView(offerNavigationKey, offer.content);
        }

        let embeddedPositions = offer.layout?.positions || {};

        if (LAYOUT_API) {
          // Always prefer the current published template so publishing a new version
          // takes effect immediately without re-triggering the webhook.
          const publishedKey = offer.templateVersion?.documentKey;
          if (publishedKey) {
            try {
              const publishedResp = await axios.get(`${LAYOUT_API}/layouts/dach`, {
                params: { document_key: publishedKey, action: 'publish' },
              });
              const publishedPositions = publishedResp.data?.published?.positions;
              if (publishedPositions && Object.keys(publishedPositions).length > 0) {
                embeddedPositions = publishedPositions;
              }
            } catch (_e) {
              // ignore; fall back to baked-in positions
            }
          }

          const hasEmbeddedPositions = Object.keys(embeddedPositions).length > 0;
          if (!hasEmbeddedPositions) {
            try {
              const standardKeyByView = {
                freiflaeche: `freiflaeche-${String(PROPOSAL_DATA.angebotsnummer || 'standard').trim().replace(/\s+/g, '-').toLowerCase()}`,
                dach: DACH_LAYOUT_DOCUMENT_KEY,
                'expose-projektverkauf': 'expose-pv-projektverkauf-standard',
                'expose-turnkey': 'expose-pv-turnkey-standard',
              };
              const fallbackKey = standardKeyByView[offerView];
              if (fallbackKey) {
                const fallbackResp = await axios.get(`${LAYOUT_API}/layouts/dach`, {
                  params: { document_key: fallbackKey, limit: 1 },
                });
                if (fallbackResp.data?.currentVersion?.positions) {
                  embeddedPositions = fallbackResp.data.currentVersion.positions;
                }
              }
            } catch (_e) {
              // ignore; proceed with empty positions
            }
          }
        }

        const offerLayoutPositions = resolveGeneratedOfferLayoutPositions(offerView, embeddedPositions);
        const nextPositions = normalizeDachLayoutPositions(offerLayoutPositions);
        setDachLayoutPositions(nextPositions);
        dachLayoutPositionsRef.current = nextPositions;
        persistDachLayout(nextPositions);

        setSelectedLayoutElements([]);
        toast.success(`Automatisch erzeugtes Angebot geladen${offer.dealId ? ` · Deal ${offer.dealId}` : ''}.`);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.detail || 'Automatisch erzeugtes Angebot konnte nicht geladen werden.');
      }
    };

    loadGeneratedOffer();

    return () => {
      isCancelled = true;
    };
  }, [applyStoredContentForView, persistDachLayout]);

  useEffect(() => {
    dachLayoutPositionsRef.current = dachLayoutPositions;
  }, [dachLayoutPositions]);

  useEffect(() => {
    dachTextContentRef.current = dachTextContent;
  }, [dachTextContent]);

  const resolvedDachLayoutPositions = buildResolvedPositions(dachLayoutPositions);
  const getElementLabel = useCallback((id) => activeSectionLabels[id] || id, [activeSectionLabels]);

  const collectLiveSpacingMetrics = useCallback((targetId) => {
    if (!proposalRef.current) {
      return [];
    }

    const targetNode = proposalRef.current.querySelector(`[data-element-id="${targetId}"]`);

    if (!targetNode) {
      return [];
    }

    const rootRect = proposalRef.current.getBoundingClientRect();
    const pageNode = targetNode.closest('.a4-page');
    const scopedNodes = pageNode
      ? Array.from(pageNode.querySelectorAll('[data-draggable="true"][data-element-id]'))
      : Array.from(proposalRef.current.querySelectorAll('[data-draggable="true"][data-element-id]'));

    const nodes = scopedNodes
      .map((node) => {
        const nodeId = node.getAttribute('data-element-id');

        if (!nodeId) {
          return null;
        }

        const rect = node.getBoundingClientRect();

        return {
          id: nodeId,
          top: Math.round(rect.top - rootRect.top),
          bottom: Math.round(rect.bottom - rootRect.top),
          right: Math.round(rect.right - rootRect.left),
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.top - right.top);

    const metrics = [];

    for (let index = 0; index < nodes.length - 1; index += 1) {
      const currentNode = nodes[index];
      const nextNode = nodes[index + 1];

      metrics.push({
        key: `${currentNode.id}__${nextNode.id}`,
        fromId: currentNode.id,
        toId: nextNode.id,
        gap: Math.round(nextNode.top - currentNode.bottom),
        fromBottom: currentNode.bottom,
        toTop: nextNode.top,
        guideX: Math.max(currentNode.right, nextNode.right) + 10,
      });
    }

    return metrics;
  }, []);

  const applyStoredLayoutState = useCallback((layoutState, options = {}) => {
    const targetView = options.view || activeView;
    const versions = Array.isArray(layoutState?.versions) ? layoutState.versions : [];
    const currentVersion = layoutState?.currentVersion || null;

    setLayoutVersions(versions);
    setLayoutStorageMode(layoutState?.storageMode || 'browser-cache');
    setLayoutStoragePath(layoutState?.storagePath || '');
    setLayoutPersistenceError('');
    setHasPendingOnlineLayoutChanges(false);

    if (options.applyCurrentVersion && currentVersion?.positions) {
      const nextPositions = normalizeDachLayoutPositions(currentVersion.positions);
      setDachLayoutPositions(nextPositions);
      dachLayoutPositionsRef.current = nextPositions;
      persistDachLayout(nextPositions);

      if (currentVersion?.content && typeof currentVersion.content === 'object') {
        applyStoredContentForView(targetView, currentVersion.content);
      }
    }

    if (currentVersion || versions.length > 0) {
      setHasSavedDachLayout(true);
    }
  }, [activeView, applyStoredContentForView, persistDachLayout]);

  const saveLayoutVersionToProject = useCallback(async (positions, options = {}) => {
    const targetView = options.viewOverride || activeNavigationKey;

    if (!LAYOUT_API) {
      const message = 'Kein Backend für die Online-Speicherung konfiguriert.';
      setLayoutPersistenceError(message);

      if (options.showToast) {
        toast.error(message);
      }

      return;
    }

    const requestId = layoutSaveRequestIdRef.current + 1;
    layoutSaveRequestIdRef.current = requestId;
    setIsSavingStoredLayout(true);

    try {
      const response = await axios.post(`${LAYOUT_API}/layouts/dach`, {
        document_key: getActiveDocumentKey(targetView),
        positions: normalizeDachLayoutPositions(positions),
        content: getCurrentOfferContentForStorage(targetView),
        label: options.label,
        source: options.source || 'manual',
      });

      if (requestId !== layoutSaveRequestIdRef.current) {
        return;
      }

      applyStoredLayoutState(response.data, { view: targetView });

      if (options.showToast) {
        toast.success(options.toastMessage || 'Änderungen online gespeichert.');
      }
    } catch (error) {
      if (requestId !== layoutSaveRequestIdRef.current) {
        return;
      }

      console.error(error);
      const message = error?.response?.data?.detail || 'Änderungen konnten nicht online gespeichert werden.';
      setLayoutPersistenceError(message);

      if (options.showToast) {
        toast.error(message);
      }
    } finally {
      if (requestId === layoutSaveRequestIdRef.current) {
        setIsSavingStoredLayout(false);
      }
    }
  }, [activeNavigationKey, applyStoredLayoutState, getActiveDocumentKey, getCurrentOfferContentForStorage]);

  const loadStoredProjectLayout = useCallback(async () => {
    if (getGeneratedOfferIdFromLocation() || automationOffer?.view === getBaseOfferView(activeView)) {
      setLayoutVersions([]);
      setLayoutStorageMode('generated-offer');
      setLayoutStoragePath('');
      setLayoutPersistenceError('');
      setHasPendingOnlineLayoutChanges(false);
      return;
    }

    if (!LAYOUT_API) {
      setLayoutStorageMode('browser-cache');
      return;
    }

    setIsLoadingStoredLayout(true);

    try {
      const response = await axios.get(`${LAYOUT_API}/layouts/dach`, {
        params: {
          document_key: getActiveDocumentKey(activeNavigationKey),
          limit: DACH_LAYOUT_VERSION_LIMIT,
        },
      });

      applyStoredLayoutState(response.data, {
        view: activeView,
        applyCurrentVersion: Boolean(response.data?.currentVersion),
      });
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.detail || 'Online-Speicherung konnte nicht geladen werden.';
      setLayoutPersistenceError(message);
      setLayoutStorageMode('browser-cache');
    } finally {
      setIsLoadingStoredLayout(false);
    }
  }, [activeNavigationKey, activeView, applyStoredLayoutState, automationOffer, getActiveDocumentKey]);

  const loadPublishedLayoutStatus = useCallback(async () => {
    if (!LAYOUT_API || getGeneratedOfferIdFromLocation()) {
      return;
    }

    setPublishedLayoutAt(null);

    try {
      const response = await axios.get(`${LAYOUT_API}/layouts/dach`, {
        params: { document_key: getActiveDocumentKey(activeNavigationKey), action: 'publish' },
      });
      setPublishedLayoutAt(response.data?.published?.published_at || null);
    } catch {
      setPublishedLayoutAt(null);
    }
  }, [activeNavigationKey, getActiveDocumentKey]);

  const restoreLayoutVersionFromProject = useCallback(async (versionId) => {
    if (!LAYOUT_API) {
      toast.error('Kein Backend für den Versionsverlauf konfiguriert.');
      return;
    }

    const previousSnapshot = normalizeDachLayoutPositions(dachLayoutPositionsRef.current);
    setIsRestoringStoredLayout(true);

    try {
      const response = await axios.post(`${LAYOUT_API}/layouts/dach?action=restore`, {
        document_key: getActiveDocumentKey(activeNavigationKey),
        version_id: versionId,
      });

      applyStoredLayoutState(response.data, {
        view: activeView,
        applyCurrentVersion: Boolean(response.data?.currentVersion),
      });
      setLastLayoutSnapshot(previousSnapshot);
      setSelectedLayoutElements([]);
      toast.success('Version wiederhergestellt.');
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.detail || 'Version konnte nicht wiederhergestellt werden.';
      setLayoutPersistenceError(message);
      toast.error(message);
    } finally {
      setIsRestoringStoredLayout(false);
    }
  }, [activeNavigationKey, activeView, applyStoredLayoutState, getActiveDocumentKey]);

  const queueProjectLayoutSave = useCallback(() => {
    persistLayoutOnNextStateRef.current = true;
    setHasPendingOnlineLayoutChanges(true);
  }, []);

  useEffect(() => {
    if (!hasPendingOnlineLayoutChanges) {
      return undefined;
    }

    if (!isOfferTemplateView || getGeneratedOfferIdFromLocation()) {
      return undefined;
    }

    if (isLoadingStoredLayout || isRestoringStoredLayout) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      saveLayoutVersionToProject(dachLayoutPositionsRef.current, {
        label: 'Auto-Sync',
        source: 'autosave',
        showToast: false,
      });
    }, 900);

    return () => window.clearTimeout(timeoutId);
  }, [
    dachLayoutPositions,
    hasPendingOnlineLayoutChanges,
    isLoadingStoredLayout,
    isOfferTemplateView,
    isRestoringStoredLayout,
    saveLayoutVersionToProject,
  ]);

  useEffect(() => {
    if (!persistLayoutOnNextStateRef.current) {
      return;
    }

    persistLayoutOnNextStateRef.current = false;
    persistDachLayout(dachLayoutPositions);
  }, [dachLayoutPositions, persistDachLayout]);

  useEffect(() => {
    if (!isOfferTemplateView) {
      return;
    }

    loadStoredProjectLayout();
    loadPublishedLayoutStatus();
  }, [activeView, isOfferTemplateView, loadStoredProjectLayout, loadPublishedLayoutStatus]);

  useEffect(() => {
    if (!isOfferTemplateView || !proposalRef.current) {
      return;
    }

    const rootNode = proposalRef.current;
    const textNodeOverrides = activeView === 'dach'
      ? getTextNodeOverrides(dachTextContent)
      : (genericTextOverrides[activeGenericTextOverrideKey] || {});
    const editableTags = 'h1,h2,h3,h4,h5,h6,p,span,li,strong,em,a,label,blockquote,div';
    const candidateNodes = Array.from(rootNode.querySelectorAll(editableTags));

    candidateNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) {
        return;
      }

      if (node.closest('[data-print-hidden="true"]') || node.closest('button') || node.closest('[data-draggable="true"] [data-element-id]')) {
        return;
      }

      if (node.closest('[data-no-override]')) {
        return;
      }

      if (node.childElementCount > 0) {
        return;
      }

      const baseText = node.innerText?.trim();

      if (!baseText) {
        return;
      }

      const textNodeId = buildTextNodeId(rootNode, node);

      if (!textNodeId) {
        return;
      }

      node.dataset.textNodeId = textNodeId;

      const overrideValue = textNodeOverrides[textNodeId];
      const resolvedText = overrideValue !== undefined ? String(overrideValue) : baseText;

      if (node.innerText !== resolvedText) {
        node.innerText = resolvedText;
      }

      if (!isTextEditMode) {
        node.contentEditable = 'false';
        node.style.cursor = '';
        node.style.outline = '';
        node.style.outlineOffset = '';
        node.style.boxShadow = '';
        node.oninput = null;
        node.onmousedown = null;
        node.onfocus = null;
        node.onblur = null;
        return;
      }

      node.contentEditable = 'true';
      node.spellcheck = false;
      node.style.cursor = 'text';
      node.onmousedown = (event) => event.stopPropagation();
      node.onfocus = () => {
        node.style.outline = '1px dashed rgba(224,112,0,0.7)';
        node.style.outlineOffset = '1px';
        node.style.boxShadow = '0 0 0 2px rgba(224,112,0,0.12)';
      };
      node.onblur = () => {
        node.style.outline = '';
        node.style.outlineOffset = '';
        node.style.boxShadow = '';
      };
      node.oninput = () => {
        const nextText = node.innerText;

        if (activeView === 'dach') {
          setDachTextContent((currentContent) => {
            const nextContent = normalizeDachTextContent(setValueByPath(currentContent, `${DACH_TEXT_NODE_OVERRIDES_PATH}.${textNodeId}`, nextText));
            persistDachTextContent(nextContent);
            dachTextContentRef.current = nextContent;
            return nextContent;
          });

          setHasPendingOnlineLayoutChanges(true);
          return;
        }

        setGenericTextOverrides((currentOverrides) => {
          const nextOverrides = {
            ...currentOverrides,
            [activeGenericTextOverrideKey]: {
              ...(currentOverrides[activeGenericTextOverrideKey] || {}),
              [textNodeId]: nextText,
            },
          };

          persistGenericTextOverrides(nextOverrides);
          return nextOverrides;
        });
      };
    });
  }, [activeGenericTextOverrideKey, activeView, buildTextNodeId, dachTextContent, genericTextOverrides, getTextNodeOverrides, isOfferTemplateView, isTextEditMode, persistDachTextContent, persistGenericTextOverrides]);

  useEffect(() => {
    if (typeof window === 'undefined' || !isOfferTemplateView || !isLayoutDragMode) {
      setSectionSpacingMetrics([]);
      return;
    }

    const measureSectionSpacing = () => {
      if (!proposalRef.current) {
        return;
      }

      const rootRect = proposalRef.current.getBoundingClientRect();
      const nextMetrics = [];

      activeSectionGroups.forEach((group) => {
        group.sections.forEach((section, index) => {
          const nextSection = group.sections[index + 1];

          if (!nextSection) {
            return;
          }

          const currentElement = proposalRef.current.querySelector(`[data-element-id="${section.id}"]`);
          const nextElement = proposalRef.current.querySelector(`[data-element-id="${nextSection.id}"]`);

          if (!currentElement || !nextElement) {
            return;
          }

          const currentRect = currentElement.getBoundingClientRect();
          const nextRect = nextElement.getBoundingClientRect();

          nextMetrics.push({
            key: `${section.id}__${nextSection.id}`,
            groupKey: group.key,
            groupTitle: group.title,
            fromId: section.id,
            fromLabel: section.label,
            toId: nextSection.id,
            toLabel: nextSection.label,
            toIndex: index + 1,
            gap: Math.round(nextRect.top - currentRect.bottom),
            fromTop: Math.round(currentRect.top - rootRect.top),
            fromBottom: Math.round(currentRect.bottom - rootRect.top),
            toTop: Math.round(nextRect.top - rootRect.top),
            toBottom: Math.round(nextRect.bottom - rootRect.top),
            guideX: Math.round(Math.max(currentRect.right, nextRect.right) - rootRect.left - 28),
          });
        });
      });

      setSectionSpacingMetrics(nextMetrics);
    };

    const frameId = window.requestAnimationFrame(measureSectionSpacing);
    window.addEventListener('resize', measureSectionSpacing);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', measureSectionSpacing);
    };
  }, [activeSectionGroups, activeView, dachLayoutPositions, isLayoutDragMode, isOfferTemplateView]);

  useEffect(() => {
    sectionSpacingMetricsRef.current = sectionSpacingMetrics;
  }, [sectionSpacingMetrics]);

  useEffect(() => {
    if (!isOfferTemplateView || !isLayoutDragMode || !showSpacingGuides) {
      spacingDragMetricsRef.current = [];
      setActiveSpacingFeedback(null);
    }
  }, [activeView, isLayoutDragMode, isOfferTemplateView, showSpacingGuides]);

  // PDF Export
  const handleExportPdf = useCallback(async () => {
    if (!canCreateActiveTool) {
      toast.error('Erstellung ist für diesen Bereich nicht freigeschaltet.');
      return;
    }

    if (activeView === 'vertrieb') {
      installVertriebPrintStyles();
      document.body.classList.add('printing-vertrieb');
      await waitForPrintAssets(proposalRef.current);
      window.addEventListener('afterprint', cleanupVertriebPrintStyles, { once: true });
      window.print();
      window.setTimeout(cleanupVertriebPrintStyles, 1500);
      return;
    }

    document.body.classList.add('printing-offer');
    await waitForPrintAssets(proposalRef.current);
    window.addEventListener('afterprint', cleanupOfferPrintState, { once: true });
    window.print();
    window.setTimeout(cleanupOfferPrintState, 1500);
  }, [activeView, canCreateActiveTool]);

  // DOCX Export
  const handleExportDocx = useCallback(async () => {
    if (!canCreateActiveTool) {
      toast.error('Erstellung ist für diesen Bereich nicht freigeschaltet.');
      return;
    }

    if (activeView === 'vertrieb' || activeView === 'flyer') {
      toast.info(`Für ${TYPE_LABELS[activeView] || 'diese Vorlage'} sind PDF, HTML-Code und PNG verfügbar.`);
      return;
    }

    setIsExportingDocx(true);
    try {
      const type = getBaseOfferView(activeView);
      const response = await axios.post(`${API}/docx?type=${type}`, activeProposalDataForExport, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Angebot_EN-POWER_${TYPE_LABELS[activeNavigationKey] || TYPE_LABELS[type] || 'Angebot'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('DOCX erfolgreich heruntergeladen!');
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Erstellen der DOCX-Datei.');
    } finally {
      setIsExportingDocx(false);
    }
  }, [activeNavigationKey, activeProposalDataForExport, activeView, canCreateActiveTool]);

  // PNG Export (for Canva)
  const handleExportPng = useCallback(async () => {
    if (!canCreateActiveTool) {
      toast.error('Erstellung ist für diesen Bereich nicht freigeschaltet.');
      return;
    }

    setIsExportingPng(true);
    toast.info('PNG-Export wird vorbereitet...');
    try {
      if (activeView === 'vertrieb') {
        const slides = Array.from(proposalRef.current?.querySelectorAll('.vertrieb-reference-slide') || []);

        if (slides.length === 0) {
          toast.error('Keine Vertrieb-Folien gefunden.');
          return;
        }

        for (let i = 0; i < slides.length; i++) {
          const canvas = await html2canvas(slides[i], {
            scale: 1.5,
            useCORS: true,
            backgroundColor: '#FFFFFF',
            width: 1280,
            height: 720,
            logging: false,
          });
          const link = document.createElement('a');
          link.download = `ENPOWER_Vertriebspraesentation_Folie_${String(i + 1).padStart(2, '0')}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
          await new Promise(r => setTimeout(r, 500));
        }

        toast.success(`${slides.length} PNG-Folien mit Änderungen heruntergeladen!`);
        return;
      }

      const pages = Array.from(proposalRef.current?.querySelectorAll('.a4-page') || []);
      if (!pages || pages.length === 0) {
        toast.error('Keine Seiten gefunden.');
        return;
      }

      const prefix = TYPE_LABELS[activeNavigationKey] || TYPE_LABELS[activeView] || 'Angebot';
      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], {
          scale: 2,
          useCORS: true,
          backgroundColor: '#FFFFFF',
          width: 794,
          height: 1123,
          logging: false,
        });
        const link = document.createElement('a');
        link.download = `Angebot_EN-POWER_${prefix}_Seite_${i + 1}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        await new Promise(r => setTimeout(r, 500));
      }
      toast.success(`${pages.length} PNG-Dateien heruntergeladen! (Für Canva-Import geeignet)`);
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim PNG-Export.');
    } finally {
      setIsExportingPng(false);
    }
  }, [activeNavigationKey, activeView, canCreateActiveTool]);

  // HTML Code Export
  const handleExportHtml = useCallback(async () => {
    if (!canCreateActiveTool) {
      toast.error('Erstellung ist für diesen Bereich nicht freigeschaltet.');
      return;
    }

    setIsExportingHtml(true);
    try {
      const type = getBaseOfferView(activeView);

      if (type === 'vertrieb') {
        const deck = proposalRef.current?.querySelector('[data-testid="vertrieb-reference-deck"]');

        if (!deck) {
          toast.error('Keine Vertriebspräsentation gefunden.');
          return;
        }

        const assetToDataUrl = async (assetPath, errorLabel) => {
          const response = await fetch(assetPath, { cache: 'no-store' });

          if (!response.ok) {
            throw new Error(`${errorLabel} konnte nicht geladen werden.`);
          }

          const blob = await response.blob();

          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          });
        };

        const slideDataUrls = await Promise.all(
          VERTRIEB_PRESENTATION_SLIDE_PATHS.map((slidePath, index) => assetToDataUrl(slidePath, `Folie ${index + 1}`)),
        );
        const logoDataUrl = await assetToDataUrl(VERTRIEB_LOGO_PATH, 'EN:POWER Logo');
        const logoOnDarkDataUrl = await assetToDataUrl(VERTRIEB_LOGO_ON_DARK_PATH, 'EN:POWER Logo hell');
        const accentDataUrls = await Promise.all(
          VERTRIEB_ACCENT_IMAGE_PATHS.map((accentPath, index) => assetToDataUrl(accentPath, `Akzentbild ${index + 1}`)),
        );
        const accentDataUrlMap = Object.fromEntries(
          VERTRIEB_ACCENT_IMAGE_PATHS.map((accentPath, index) => [accentPath, accentDataUrls[index]]),
        );

        const clonedDeck = deck.cloneNode(true);
        const sourceFields = Array.from(deck.querySelectorAll('[data-vertrieb-editable-field="true"], [data-vertrieb-number-field="true"]'));
        const clonedFields = Array.from(clonedDeck.querySelectorAll('[data-vertrieb-editable-field="true"], [data-vertrieb-number-field="true"]'));
        const clonedImages = Array.from(clonedDeck.querySelectorAll('.vertrieb-reference-slide-image'));
        const clonedLogoImages = Array.from(clonedDeck.querySelectorAll('[data-vertrieb-logo-image="true"]'));
        const clonedAccentImages = Array.from(clonedDeck.querySelectorAll('[data-vertrieb-accent-image="true"]'));

        clonedImages.forEach((image, index) => {
          image.src = slideDataUrls[index] || image.src;
          image.loading = 'eager';
        });

        clonedLogoImages.forEach((image) => {
          image.src = image.getAttribute('data-vertrieb-logo-variant') === 'on-dark' ? logoOnDarkDataUrl : logoDataUrl;
          image.loading = 'eager';
        });

        clonedAccentImages.forEach((image) => {
          const sourcePath = image.getAttribute('src');
          image.src = accentDataUrlMap[sourcePath] || image.src;
          image.loading = 'eager';
        });

        clonedFields.forEach((field, index) => {
          const value = sourceFields[index]?.value ?? field.value ?? field.textContent ?? '';
          field.value = value;
          field.textContent = value;
          field.setAttribute('value', value);
          field.setAttribute('readonly', 'readonly');
        });

        const htmlDocument = `<!doctype html>
<html lang="de">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>EN:POWER Vertriebspräsentation</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&family=Outfit:wght@400;600;700;800&display=swap" rel="stylesheet">
    <style>
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #E5EAF2; }
      body { display: flex; flex-direction: column; align-items: center; gap: 24px; padding: 32px 16px; }
      .vertrieb-reference-deck { display: flex; flex-direction: column; align-items: center; gap: 24px; width: 100%; }
      .vertrieb-reference-slide { position: relative; width: 1280px; max-width: none; aspect-ratio: 16 / 9; margin: 0; background: #fff; overflow: hidden; box-shadow: 0 18px 50px rgba(15,23,42,.12); }
      .vertrieb-reference-slide-image { display: block; width: 100%; height: 100%; object-fit: contain; user-select: none; }
      .vertrieb-image-accent { position: absolute; z-index: 2; display: block; margin: 0; overflow: hidden; pointer-events: none; isolation: isolate; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .vertrieb-image-accent::after { content: ''; position: absolute; inset: 0; z-index: 2; background: var(--vertrieb-accent-veil, transparent); pointer-events: none; }
      .vertrieb-image-accent-image { display: block; width: 100%; height: 100%; object-fit: cover; object-position: var(--vertrieb-accent-position, center); filter: saturate(0.96) contrast(1.08) brightness(1.02); user-select: none; }
      .vertrieb-logo-overlay { position: absolute; z-index: 6; display: flex; align-items: center; justify-content: flex-start; margin: 0; box-sizing: border-box; overflow: hidden; pointer-events: none; }
      .vertrieb-logo-image { display: block; width: 100%; height: 100%; object-fit: contain; object-position: left center; user-select: none; }
      .vertrieb-editable-field, .vertrieb-number-field { position: absolute; z-index: 5; display: block; margin: 0; padding: 0 2px; border: 1px solid transparent; border-radius: 3px; outline: none; resize: none; overflow: hidden; box-sizing: border-box; appearance: none; -webkit-appearance: none; letter-spacing: 0; white-space: pre-wrap; }
      @page { size: 1280px 720px; margin: 0; }
      @media print { html, body { width: 1280px; background: #fff; } body { padding: 0; gap: 0; } .vertrieb-reference-deck { gap: 0; } .vertrieb-reference-slide { width: 1280px; height: 720px; max-width: none; box-shadow: none; break-after: page; page-break-after: always; } .vertrieb-reference-slide:last-child { break-after: auto; page-break-after: auto; } .vertrieb-editable-field, .vertrieb-number-field { border-color: transparent; box-shadow: none; print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
    </style>
  </head>
  <body>
    ${clonedDeck.outerHTML}
  </body>
</html>`;
        const blob = new Blob([htmlDocument], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ENPOWER_Vertriebspraesentation.html';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('HTML-Code erfolgreich heruntergeladen!');
        return;
      }

      const canvasRoot = proposalRef.current;

      if (!canvasRoot) {
        toast.error('Kein Vorschau-Canvas gefunden.');
        return;
      }

      const clonedCanvas = canvasRoot.cloneNode(true);
      clonedCanvas.querySelectorAll('[data-print-hidden="true"]').forEach((node) => node.remove());

      const styleMarkup = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((node) => node.outerHTML)
        .join('\n');

      const htmlDocument = `<!doctype html>
<html lang="de">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Angebot EN:POWER ${TYPE_LABELS[type] || 'Angebot'}</title>
    ${styleMarkup}
    <style>
      body { margin: 0; padding: 24px; background: #e2e8f0; }
      .proposal-pages { margin: 0 auto; }
    </style>
  </head>
  <body>
    ${clonedCanvas.outerHTML}
  </body>
</html>`;

      const blob = new Blob([htmlDocument], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Angebot_EN-POWER_${TYPE_LABELS[type] || 'Angebot'}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('HTML-Code erfolgreich heruntergeladen!');
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim HTML-Export.');
    } finally {
      setIsExportingHtml(false);
    }
  }, [activeView, canCreateActiveTool]);

  const handleExportLayoutProject = useCallback(() => {
    if (!isOfferTemplateView || !canEditActiveTool) {
      toast.error('Projekt-Export ist in dieser Ansicht nicht verfügbar.');
      return;
    }

    const activeDocumentKey = getActiveDocumentKey(activeNavigationKey);
    try {
      const payload = {
        schema: 'enpower-layout-project/v1',
        exportedAt: new Date().toISOString(),
        view: activeNavigationKey,
        documentKey: activeDocumentKey,
        layout: {
          positions: normalizeDachLayoutPositions(dachLayoutPositionsRef.current),
        },
        content: getCurrentOfferContentForStorage(activeNavigationKey),
        editor: {
          showElementOutlines,
          showSpacingGuides,
          enableSmartSpacingSnap,
          spacingGridSize,
        },
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ENPOWER_${activeNavigationKey}_Projekt_${activeDocumentKey}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Projektdatei exportiert.');
    } catch (error) {
      console.error(error);
      toast.error('Projektdatei konnte nicht exportiert werden.');
    }
  }, [activeNavigationKey, canEditActiveTool, enableSmartSpacingSnap, getActiveDocumentKey, getCurrentOfferContentForStorage, isOfferTemplateView, showElementOutlines, showSpacingGuides, spacingGridSize]);

  const handleImportLayoutProjectClick = useCallback(() => {
    if (!isOfferTemplateView || !canEditActiveTool) {
      toast.error('Projekt-Import ist in dieser Ansicht nicht verfügbar.');
      return;
    }

    layoutImportInputRef.current?.click();
  }, [canEditActiveTool, isOfferTemplateView]);

  const handleImportLayoutProjectFile = useCallback(async (event) => {
    const inputElement = event?.target;
    const selectedFile = inputElement?.files?.[0];

    if (!isOfferTemplateView || !canEditActiveTool) {
      toast.error('Projekt-Import ist für diesen Bereich nicht freigeschaltet.');
      if (inputElement) {
        inputElement.value = '';
      }
      return;
    }

    if (!selectedFile) {
      return;
    }

    try {
      const rawContent = await selectedFile.text();
      const parsed = JSON.parse(rawContent);
      const importedView = parsed?.view === 'dach' || parsed?.view === 'freiflaeche' || parsed?.view === 'speicher'
        ? parsed.view
        : 'dach';
      const rawPositions = parsed?.layout?.positions;

      if (!rawPositions || typeof rawPositions !== 'object') {
        throw new Error('invalid-project-file');
      }

      const nextPositions = normalizeDachLayoutPositions(rawPositions);
      const nextContent = parsed?.content && typeof parsed.content === 'object' ? parsed.content : {};
      const previousSnapshot = normalizeDachLayoutPositions(dachLayoutPositionsRef.current);

      pendingLayoutSnapshotRef.current = null;
      spacingDragMetricsRef.current = [];
      setActiveSpacingFeedback(null);
      setLastLayoutSnapshot(previousSnapshot);
      setDachLayoutPositions(nextPositions);
      dachLayoutPositionsRef.current = nextPositions;
      persistDachLayout(nextPositions);
      applyStoredContentForView(importedView, nextContent);
      setSelectedLayoutElements([]);
      setActiveView(getBaseOfferView(importedView));
      setActiveNavigationKey(importedView);

      if (typeof parsed?.editor?.showElementOutlines === 'boolean') {
        setShowElementOutlines(parsed.editor.showElementOutlines);
      }

      if (typeof parsed?.editor?.showSpacingGuides === 'boolean') {
        setShowSpacingGuides(parsed.editor.showSpacingGuides);
      }

      if (typeof parsed?.editor?.enableSmartSpacingSnap === 'boolean') {
        setEnableSmartSpacingSnap(parsed.editor.enableSmartSpacingSnap);
      }

      if (Number.isFinite(parsed?.editor?.spacingGridSize)) {
        const importedGrid = Number(parsed.editor.spacingGridSize);

        if (SPACING_GRID_OPTIONS.includes(importedGrid)) {
          setSpacingGridSize(importedGrid);
        }
      }

      await saveLayoutVersionToProject(nextPositions, {
        label: 'Projektdatei importiert',
        source: 'import',
        viewOverride: importedView,
        showToast: false,
      });

      toast.success('Projektdatei geladen. Sie können direkt weiterbearbeiten.');
    } catch (error) {
      console.error(error);
      toast.error('Die Projektdatei ist ungültig oder konnte nicht geladen werden.');
    } finally {
      if (inputElement) {
        inputElement.value = '';
      }
    }
  }, [applyStoredContentForView, canEditActiveTool, isOfferTemplateView, persistDachLayout, saveLayoutVersionToProject]);

  const handleLayoutPositionChange = useCallback((id, nextPosition) => {
    const isDeckblattDetailElement = id === 'S1-Deckblatt-Inhalt' || id === 'S1-Deckblatt-Meta';

    if (nextPosition.isDragging && pendingLayoutSnapshotRef.current === null) {
      pendingLayoutSnapshotRef.current = normalizeDachLayoutPositions(dachLayoutPositionsRef.current);
      spacingDragMetricsRef.current = collectLiveSpacingMetrics(id);
    }

    if (nextPosition.isDragging === false && pendingLayoutSnapshotRef.current !== null) {
      setLastLayoutSnapshot(pendingLayoutSnapshotRef.current);
      pendingLayoutSnapshotRef.current = null;
      queueProjectLayoutSave();
    }

    if (nextPosition.isDragging === false) {
      spacingDragMetricsRef.current = [];
      setActiveSpacingFeedback(null);
    }

    const targetIds = selectedLayoutElements.includes(id) && selectedLayoutElements.length > 1
      ? selectedLayoutElements
      : [id];
    const currentPosition = resolveEffectivePosition(id, dachLayoutPositionsRef.current);
    let deltaY = typeof nextPosition.deltaY === 'number'
      ? nextPosition.deltaY
      : ((typeof nextPosition.y === 'number' ? nextPosition.y : currentPosition.y) - currentPosition.y);

    if (nextPosition.isDragging && targetIds.length === 1 && !isDeckblattDetailElement) {
      const currentMetrics = collectLiveSpacingMetrics(id);
      const snapThreshold = getAdaptiveSnapThreshold(currentMetrics, id);
      const assist = resolveSpacingAssist({
        id,
        deltaY,
        currentMetrics,
        snapshotMetrics: spacingDragMetricsRef.current,
        gridSize: spacingGridSize,
        enableSmartSnap: enableSmartSpacingSnap,
        snapThreshold,
      });

      deltaY = assist.adjustedDeltaY;

      if (showSpacingGuides) {
        setActiveSpacingFeedback(buildActiveSpacingFeedback({
          id,
          appliedDeltaY: deltaY,
          currentMetrics,
          snapTarget: assist.snapTarget,
          snapReason: assist.snapReason,
          snapMetricKey: assist.metricKey,
          gridSize: spacingGridSize,
        }));
      }
    } else if (nextPosition.isDragging) {
      setActiveSpacingFeedback(null);
    }

    setDachLayoutPositions((currentPositions) => {
      const nextPositions = { ...currentPositions };

      targetIds.forEach((targetId) => {
        const linkedSectionId = getLinkedSectionId(targetId);

        if (layoutEditLevel === 'detail' && linkedSectionId && linkedSectionId !== targetId) {
          const baseDetailPosition = nextPositions[targetId] || DEFAULT_LAYOUT_POSITION;
          const nextDetailY = Math.round(baseDetailPosition.y + deltaY);
          nextPositions[targetId] = {
            x: 0,
            y: targetId === 'S1-Deckblatt' ? 0 : nextDetailY,
          };
        } else {
          const baseSectionPosition = nextPositions[targetId] || DEFAULT_LAYOUT_POSITION;
          const nextSectionY = Math.round(baseSectionPosition.y + deltaY);
          nextPositions[targetId] = {
            x: 0,
            y: targetId === 'S1-Deckblatt' ? 0 : nextSectionY,
          };
        }
      });

      return nextPositions;
    });
  }, [collectLiveSpacingMetrics, enableSmartSpacingSnap, layoutEditLevel, queueProjectLayoutSave, selectedLayoutElements, showSpacingGuides, spacingGridSize]);

  const handleSaveDachLayout = useCallback(() => {
    pendingLayoutSnapshotRef.current = null;
    persistDachLayout(dachLayoutPositionsRef.current);
    saveLayoutVersionToProject(dachLayoutPositionsRef.current, {
      label: 'Manuell gespeichert',
      source: 'manual',
      showToast: true,
      toastMessage: 'Änderungen online gespeichert.',
    });
  }, [persistDachLayout, saveLayoutVersionToProject]);

  const handlePublishLayout = useCallback(async () => {
    if (!LAYOUT_API) {
      toast.error('Kein Backend für die Veröffentlichung konfiguriert.');
      return;
    }

    const targetView = activeNavigationKey;
    const positions = normalizeDachLayoutPositions(dachLayoutPositionsRef.current);
    const content = getCurrentOfferContentForStorage(targetView);
    const documentKey = getActiveDocumentKey(targetView);

    persistDachLayout(positions);
    setIsPublishingLayout(true);

    try {
      await Promise.all([
        axios.post(`${LAYOUT_API}/layouts/dach`, {
          document_key: documentKey,
          positions,
          content,
          label: 'Für Automation veröffentlicht',
          source: 'published',
        }),
        axios.post(`${LAYOUT_API}/layouts/dach?action=publish`, {
          document_key: documentKey,
          positions,
          content,
          label: 'Für Automation veröffentlicht',
        }),
      ]);

      setPublishedLayoutAt(new Date().toISOString());
      toast.success(`Vorlage "${documentKey}" für Automation veröffentlicht.`);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Veröffentlichung fehlgeschlagen.');
    } finally {
      setIsPublishingLayout(false);
    }
  }, [activeNavigationKey, getActiveDocumentKey, getCurrentOfferContentForStorage, persistDachLayout]);

  const handleUndoLastLayoutChange = useCallback(() => {
    if (lastLayoutSnapshot === null) {
      return;
    }

    pendingLayoutSnapshotRef.current = null;
    queueProjectLayoutSave();
    setDachLayoutPositions(lastLayoutSnapshot);
    setSelectedLayoutElements([]);
    setLastLayoutSnapshot(null);
    toast.success('Letzte Änderung rückgängig gemacht.');
  }, [lastLayoutSnapshot, queueProjectLayoutSave]);

  const handleLayoutElementSelect = useCallback((id, event) => {
    const isMultiSelect = event?.ctrlKey || event?.metaKey || event?.shiftKey;

    setSelectedLayoutElements((currentSelection) => {
      if (!isMultiSelect) {
        return [id];
      }

      if (currentSelection.includes(id)) {
        return currentSelection.filter((selectedId) => selectedId !== id);
      }

      return [...currentSelection, id];
    });
  }, []);

  const handleToggleLayoutEditMode = useCallback(() => {
    if (!canEditActiveTool) {
      toast.error('Bearbeitung ist für diesen Bereich nicht freigeschaltet.');
      return;
    }

    setIsLayoutEditMode((currentValue) => {
      if (currentValue) {
        setSelectedLayoutElements([]);
        setEditorMode('layout');
      }

      return !currentValue;
    });
  }, [canEditActiveTool]);

  const handleClearLayoutSelection = useCallback(() => {
    setSelectedLayoutElements([]);
  }, []);

  const handleDachTextFieldChange = useCallback((path, value) => {
    setDachTextContent((currentContent) => {
      const nextContent = normalizeDachTextContent(setValueByPath(currentContent, path, value));
      persistDachTextContent(nextContent);
      dachTextContentRef.current = nextContent;
      return nextContent;
    });

    setHasPendingOnlineLayoutChanges(true);
  }, [persistDachTextContent]);

  const handleExposeInlineFieldChange = useCallback((field, value) => {
    const targetView = isExposeTemplateView ? activeView : 'expose-projektverkauf';
    const fieldKey = String(field || '').trim();
    if (!fieldKey) return;

    setAutomationOffer((currentOffer) => {
      if (currentOffer?.view !== targetView) return currentOffer;
      return {
        ...currentOffer,
        proposalData: {
          ...(currentOffer.proposalData || {}),
          [fieldKey]: value,
        },
      };
    });

    setGenericTextOverrides((currentOverrides) => {
      const nextOverrides = {
        ...currentOverrides,
        [targetView]: {
          ...(currentOverrides[targetView] || {}),
          [fieldKey]: value,
        },
      };

      persistGenericTextOverrides(nextOverrides);
      return nextOverrides;
    });

    setHasPendingOnlineLayoutChanges(true);
  }, [activeView, isExposeTemplateView, persistGenericTextOverrides]);

  const handleLayoutCanvasMouseDown = useCallback((event) => {
    if (!isLayoutDragMode || !(event.target instanceof Element)) {
      return;
    }

    if (event.target.closest('[data-draggable="true"]')) {
      return;
    }

    setSelectedLayoutElements([]);
  }, [isLayoutDragMode]);

  const handleResetDachLayout = useCallback(() => {
    if (Object.keys(dachLayoutPositionsRef.current).length > 0) {
      setLastLayoutSnapshot(normalizeDachLayoutPositions(dachLayoutPositionsRef.current));
    }

    pendingLayoutSnapshotRef.current = null;
    queueProjectLayoutSave();
    setDachLayoutPositions({});
    setSelectedLayoutElements([]);
    toast.success('Bereichspositionen zurückgesetzt.');
  }, [queueProjectLayoutSave]);

  const handleSpacingDraftChange = useCallback((key, value) => {
    setSpacingDrafts((currentDrafts) => ({
      ...currentDrafts,
      [key]: value,
    }));
  }, []);

  const handleApplySpacingValue = useCallback((metric) => {
    const rawGapValue = spacingDrafts[metric.key] ?? String(metric.gap);
    const nextGap = Number(rawGapValue);

    if (!Number.isFinite(nextGap)) {
      toast.error('Bitte einen gültigen Pixelwert eingeben.');
      return;
    }

    const deltaY = Math.round(nextGap - metric.gap);

    setSpacingDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[metric.key];
      return nextDrafts;
    });

    if (deltaY === 0) {
      return;
    }

    const previousSnapshot = normalizeDachLayoutPositions(dachLayoutPositionsRef.current);
    setLastLayoutSnapshot(previousSnapshot);
    pendingLayoutSnapshotRef.current = null;
    queueProjectLayoutSave();

    setDachLayoutPositions((currentPositions) => {
      const group = DACH_SECTION_GROUPS.find((entry) => entry.key === metric.groupKey);

      if (!group) {
        return currentPositions;
      }

      const nextPositions = { ...currentPositions };

      group.sections.slice(metric.toIndex).forEach((section) => {
        const basePosition = nextPositions[section.id] || DEFAULT_LAYOUT_POSITION;
        nextPositions[section.id] = {
          x: 0,
          y: basePosition.y + deltaY,
        };
      });

      return nextPositions;
    });

    toast.success('Abschnittsabstand aktualisiert.');
  }, [queueProjectLayoutSave, spacingDrafts]);

  const handleDistributeSpacingGroup = useCallback((group) => {
    if (!group?.metrics?.length) {
      return;
    }

    const previousSnapshot = normalizeDachLayoutPositions(dachLayoutPositionsRef.current);
    const targetGap = Math.max(0, group.preferredGap);

    setLastLayoutSnapshot(previousSnapshot);
    pendingLayoutSnapshotRef.current = null;
    queueProjectLayoutSave();

    setDachLayoutPositions((currentPositions) => {
      const nextPositions = { ...currentPositions };
      let cumulativeDelta = 0;

      group.metrics.forEach((metric) => {
        cumulativeDelta += targetGap - metric.gap;
        const basePosition = nextPositions[metric.toId] || DEFAULT_LAYOUT_POSITION;

        nextPositions[metric.toId] = {
          x: 0,
          y: basePosition.y + cumulativeDelta,
        };
      });

      return nextPositions;
    });

    toast.success(`${group.title}: Abstände auf ${targetGap}px verteilt.`);
  }, [queueProjectLayoutSave]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isLayoutDragMode || lastLayoutSnapshot === null) {
        return;
      }

      if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.key.toLowerCase() !== 'z') {
        return;
      }

      if (event.target instanceof HTMLElement) {
        const interactiveTagNames = ['INPUT', 'TEXTAREA', 'SELECT'];

        if (event.target.isContentEditable || interactiveTagNames.includes(event.target.tagName)) {
          return;
        }
      }

      event.preventDefault();
      handleUndoLastLayoutChange();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUndoLastLayoutChange, isLayoutDragMode, lastLayoutSnapshot]);

  const sectionSpacingGroups = activeSectionGroups.map((group) => {
    const metrics = sectionSpacingMetrics.filter((metric) => metric.groupKey === group.key);
    const preferredGap = getPreferredSpacingGap(metrics);
    const numericGaps = metrics.map((metric) => metric.gap);
    const variation = numericGaps.length > 1 ? Math.max(...numericGaps) - Math.min(...numericGaps) : 0;

    return {
      ...group,
      preferredGap,
      variation,
      isBalanced: metrics.length > 1 && metrics.every((metric) => Math.abs(metric.gap - preferredGap) <= 1),
      metrics: metrics.map((metric) => ({
        ...metric,
        isPreferredGap: Math.abs(metric.gap - preferredGap) <= 1,
      })),
    };
  }).filter((group) => group.metrics.length > 0);
  const spacingDistributionSuggestions = sectionSpacingGroups.filter((group) => group.metrics.length > 1 && !group.isBalanced);

  const activeDocumentKey = getActiveDocumentKey(activeNavigationKey);

  const layoutStatusLabel = isPublishingLayout
    ? 'Wird für Automation veröffentlicht...'
    : isLoadingStoredLayout
      ? 'Layout wird geladen...'
      : isRestoringStoredLayout
        ? 'Version wird wiederhergestellt...'
        : isSavingStoredLayout
          ? 'Entwurf wird gespeichert...'
          : hasPendingOnlineLayoutChanges
            ? 'Online-Speicherung ausstehend'
            : publishedLayoutAt
              ? `Veröffentlicht · ${new Date(publishedLayoutAt).toLocaleString('de-DE')}`
              : layoutStoragePath
                ? `${getLayoutStorageModeLabel(layoutStorageMode)} (Entwurf)`
                : hasSavedDachLayout
                  ? 'Browser-Cache aktiv'
                  : 'Noch nicht gespeichert';

  const latestLayoutVersions = layoutVersions.slice(0, DACH_LAYOUT_VERSION_LIMIT);

  // Gueltigkeit der EEG-Saetze: einmal pro Sitzung ermittelt, das Datum
  // aendert sich waehrend der Nutzung nicht.
  const eegStand = useMemo(() => pruefeEegAktualitaet(), []);

  const navigationGroups = useMemo(() => ([
    {
      key: 'angebote-am',
      label: 'Angebote',
      icon: Briefcase,
      children: [
        { key: 'dach', label: 'Dach PV', icon: Building2 },
        { key: 'sanierung', label: 'Sanierung', icon: Building2 },
        { key: 'freiflaeche', label: 'Freiflächen PV', icon: Trees },
        { key: 'speicher', label: 'Kleinspeicher', icon: Battery },
        { key: 'bess', label: 'Großspeicher', icon: Battery },
        { key: 'flyer', label: 'Flyer', icon: Image },
        { key: 'ffpv-flyer', label: 'FFPV Flyer', icon: Trees },
        { key: 'ffpv-teaser', label: 'Projektteaser FFPV', icon: Handshake },
        { key: 'vertrieb', label: 'Vertriebspräsentation', icon: FileText },
        { key: 'generator', label: 'Generator', icon: FileDown },
      ],
    },
    {
      key: 'exposes-group',
      label: 'Exposés',
      icon: FileText,
      children: [
        { key: 'exposes-automation', label: 'Erstellen', icon: Workflow },
        { key: 'exposes-cockpit', label: 'Aus Projekt-Cockpit', icon: FileDown },
        { key: 'expose-projektverkauf', label: 'Projektrechte Vorlage', icon: FileText },
        { key: 'expose-turnkey', label: 'Turnkey Vorlage', icon: FileText },
      ],
    },
    {
      key: 'vertraege-am-group',
      label: 'Verträge',
      icon: FileText,
      children: [
        {
          key: 'contracts-workflow',
          label: 'Bearbeitung',
          icon: Briefcase,
          children: [
            { key: 'vertraege-am-pending', label: 'Deals', icon: Briefcase },
            { key: 'vertraege-am', label: 'Erstellen', icon: FileText },
            { key: 'vertraege-am-bearbeiten', label: 'Bearbeiten', icon: Upload },
            { key: 'vertraege-am-abwaegen', label: 'Abwägen', icon: Scale },
            { key: 'vertraege-am-dokumenten-assistent', label: 'Dokumenten-Assistent', icon: FileStack },
          ],
        },
        {
          key: 'contracts-templates',
          label: 'Vorlagen',
          icon: FileText,
          children: [
            { key: 'vertraege', label: 'Vertragsvorlagen', icon: FileText },
          ],
        },
        { key: 'vertraege-am-klauseln', label: 'Paragraphen', icon: FileText },
      ],
    },
    {
      key: 'vermittlervertrag-group',
      label: 'Vermittlervertrag',
      icon: FileText,
      children: [
        { key: 'vermittlervertrag', label: 'Vermittlervertrag', icon: FileText },
      ],
    },
    {
      key: 'standarddokumente-group',
      label: 'Standarddokumente',
      icon: FileText,
      children: [
        { key: 'standarddokumente-vorlagen', label: 'Vorlagen', icon: FileText },
        { key: 'standarddokumente', label: 'Erstellen', icon: Workflow },
      ],
    },
    {
      key: 'onboarding-group',
      label: 'On-Boarding',
      icon: Mail,
      children: [
        { key: 'onboarding-signature', label: 'E-Mail Signatur', icon: Mail },
        { key: 'mittwald-mail', label: 'Mittwald Postfach', icon: Mail },
        { key: 'bitrix-user', label: 'Bitrix Zugang', icon: UserPlus },
      ],
    },
    {
      key: 'werkzeuge-group',
      label: 'Werkzeuge',
      icon: Wrench,
      children: [
        {
          key: 'eeg-rechner',
          // Laufen die EEG-Saetze ab, faellt das schon im Menue auf. Sonst
          // stuende die Warnung nur im Rechner - und wer ihn gerade nicht
          // oeffnet, rechnet weiter mit ueberholten Werten.
          label: eegStand.zustand === 'gueltig'
            ? 'EEG-Vergütung'
            : `EEG-Vergütung (${eegStand.zustand === 'abgelaufen' ? 'veraltet' : 'läuft aus'})`,
          icon: eegStand.zustand === 'gueltig' ? Calculator : AlertTriangle,
        },
      ],
    },
    {
      key: 'einstellungen-group',
      label: 'Einstellungen',
      icon: Settings,
      children: [
        { key: 'vertraege-am-automatisierungen', label: 'Automatisierung', icon: Workflow },
        { key: 'designsystem', label: 'Design System', icon: Palette },
        { key: 'automation-uebersicht', label: 'Laufende Automatisierungen', icon: Activity, adminOnly: true },
        { key: 'zugangsanfragen', label: 'Zugänge freischalten', icon: ShieldCheck, adminOnly: true },
        { key: 'mail-konten', label: 'Postfächer', icon: Mail, adminOnly: true },
        { key: 'settings', label: 'Tool-Rechte', icon: Settings, adminOnly: true },
      ],
    },
  ]), [eegStand.zustand]);

  const visibleNavigationGroups = useMemo(() => {
    if (toolPermissions.loading) return [];
    const filterNavigationItems = (items = []) => items
      .map((item) => {
        if (Array.isArray(item.children) && item.children.length > 0) {
          const children = filterNavigationItems(item.children);
          return children.length > 0 ? { ...item, children } : null;
        }

        if (item.adminOnly) return isAdminUser ? item : null;
        return getAccessForView(item.key).canView === true ? item : null;
      })
      .filter(Boolean);

    return navigationGroups
      .map((group) => ({
        ...group,
        children: filterNavigationItems(group.children),
      }))
      .filter((group) => group.children.length > 0);
  }, [getAccessForView, isAdminUser, navigationGroups, toolPermissions.loading]);

  const activeGroup = useMemo(() => (
    visibleNavigationGroups.find((group) => navigationItemContainsKey(group, activeNavigationKey))
      || visibleNavigationGroups[0]
      || null
  ), [visibleNavigationGroups, activeNavigationKey]);

  const activeNavigationItem = useMemo(() => (
    getNavigationLeafItems(visibleNavigationGroups).find((item) => item.key === activeNavigationKey)
      || getFirstNavigationLeaf(activeGroup?.children)
      || null
  ), [visibleNavigationGroups, activeNavigationKey, activeGroup]);

  const androidMainNavigationItems = useMemo(() => ([
    { key: 'vertraege-am-pending', label: 'Deals', icon: Briefcase },
    { key: 'vertraege-am', label: 'Erstellen', icon: FileText },
    { key: 'vertraege-am-bearbeiten', label: 'Bearbeiten', icon: Upload },
    { key: 'vertraege-am-abwaegen', label: 'Abwägen', icon: Scale },
    { key: 'vertraege', label: 'Vorlagen', icon: FileText },
  ]), []);

  const androidContextToolItems = useMemo(() => ([
    { key: 'vertraege-am-automatisierungen', label: 'Automatisierung', icon: Workflow },
    { key: 'vertraege-am-klauseln', label: 'Paragraphen', icon: FileText },
    { key: 'standarddokumente', label: 'Standarddokumente', icon: Workflow },
    { key: 'standarddokumente-vorlagen', label: 'Dokumentvorlagen', icon: FileText },
    { key: 'vermittlervertrag', label: 'Vermittlervertrag', icon: FileText },
    { key: 'onboarding-signature', label: 'E-Mail Signatur', icon: Mail },
    { key: 'settings', label: 'Tool-Rechte', icon: Settings, adminOnly: true },
    { key: 'automation-uebersicht', label: 'Laufende Automatisierungen', icon: Activity, adminOnly: true },
    { key: 'zugangsanfragen', label: 'Zugänge freischalten', icon: ShieldCheck, adminOnly: true },
    { key: 'mail-konten', label: 'Postfächer', icon: Mail, adminOnly: true },
  ]), []);

  const getVisibleAndroidItems = useCallback((items = []) => items.filter((item) => {
    if (item.adminOnly && !isAdminUser) return false;
    return getAccessForView(item.key).canView === true;
  }), [getAccessForView, isAdminUser]);

  const visibleAndroidMainNavigationItems = useMemo(
    () => getVisibleAndroidItems(androidMainNavigationItems),
    [androidMainNavigationItems, getVisibleAndroidItems]
  );

  const visibleAndroidContextToolItems = useMemo(
    () => getVisibleAndroidItems(androidContextToolItems),
    [androidContextToolItems, getVisibleAndroidItems]
  );

  useEffect(() => {
    if (toolPermissions.loading) {
      return;
    }

    if (canViewActiveTool) {
      return;
    }

    const fallbackView = getFirstNavigationLeaf(visibleNavigationGroups[0]?.children)?.key || (isAdminUser ? 'settings' : 'noAccess');

    if (activeView !== fallbackView) {
      setActiveView(getBaseOfferView(fallbackView));
      setActiveNavigationKey(fallbackView);
    }
  }, [activeView, canViewActiveTool, isAdminUser, toolPermissions.loading, visibleNavigationGroups]);

  useEffect(() => {
    if (canEditActiveTool || !isLayoutEditMode) {
      return;
    }

    setIsLayoutEditMode(false);
    setEditorMode('layout');
    setSelectedLayoutElements([]);
  }, [canEditActiveTool, isLayoutEditMode]);

  const handleNavigationSelect = useCallback((nextView) => {
    const nextBaseView = getBaseOfferView(nextView);
    setActiveView(nextBaseView);
    setActiveNavigationKey(nextView);

    if (typeof window === 'undefined' || publicGeneratedOffer) {
      return;
    }

    const normalizedPathname = window.location.pathname.replace(/\/+$/, '') || '/';
    if (nextBaseView === 'generator' && normalizedPathname !== '/offer-generator') {
      window.history.pushState(null, '', '/offer-generator');
      return;
    }

    if (ROUTED_TOOL_VIEWS[normalizedPathname] && nextBaseView !== 'generator') {
      window.history.pushState(null, '', '/');
    }
  }, [publicGeneratedOffer]);

  const handleSelectContractPendingDeal = useCallback((item) => {
    const nextDealId = String(item?.dealId || '').trim();
    if (!nextDealId) {
      toast.error('Deal-ID fehlt und konnte nicht übernommen werden.');
      return;
    }

    const nextContractKey = resolveContractKeyFromPendingDeal(item);
    setSelectedContractsDealId(nextDealId);
    setSelectedContractsContractKey(nextContractKey);
    setActiveView('vertraege-am');
    setActiveNavigationKey('vertraege-am');
    if (nextContractKey) {
      toast.success(`Deal #${nextDealId} übernommen. Vertragsart wurde automatisch vorausgewählt.`);
      return;
    }

    toast.success(`Deal #${nextDealId} wurde für die Vertragserstellung übernommen.`);
  }, []);

  const handleContractManualUploadQueued = useCallback(() => {
    setActiveView('vertraege-am-pending');
    setActiveNavigationKey('vertraege-am-pending');
  }, []);

  const handleOpenExposeInTemplateEditor = useCallback((payload = {}) => {
    const targetView = payload.variantKey === 'expose-turnkey'
      ? 'expose-turnkey'
      : 'expose-projektverkauf';
    const sourceData = payload.data && typeof payload.data === 'object'
      ? payload.data
      : buildExposePlaceholderData(targetView);
    const project = payload.project && typeof payload.project === 'object'
      ? payload.project
      : {};
    const projectId = String(project.id || sourceData.id || sourceData.projekt_id || sourceData.dealId || 'manuell').trim();

    setAutomationOffer({
      view: targetView,
      proposalData: sourceData,
      documentKey: `${targetView}-${projectId}`,
      dealId: String(project.dealId || sourceData.dealId || sourceData.deal_id || '').trim(),
    });
    setActiveView(targetView);
    setActiveNavigationKey(targetView);
    setIsLayoutEditMode(true);
    setEditorMode('text');
    setSelectedLayoutElements([]);
    toast.success('Exposé im ursprünglichen Editor geöffnet.');
  }, []);

  const handleGroupSelect = useCallback((groupKey) => {
    const group = visibleNavigationGroups.find((candidate) => candidate.key === groupKey);
    if (!group || group.children.length === 0) return;
    const shouldResetContractsGroup = groupKey === 'vertraege-am-group' && activeNavigationKey !== 'vertraege-am-pending';
    if (navigationItemContainsKey(group, activeNavigationKey) && !shouldResetContractsGroup) return;
    const firstLeaf = getFirstNavigationLeaf(group.children);
    if (!firstLeaf?.key) return;
    handleNavigationSelect(firstLeaf.key);
  }, [visibleNavigationGroups, activeNavigationKey, handleNavigationSelect]);

  const handleNavigationMenuSelect = useCallback((nextView, event) => {
    handleNavigationSelect(nextView);
    event?.currentTarget?.closest?.('details')?.removeAttribute('open');
  }, [handleNavigationSelect]);

  const isOfferWorkspace = activeGroup?.key === 'angebote-am' || isExposeTemplateView;
  const shouldShowActiveSubNavigation = !toolPermissions.loading
    && activeGroup
    && (activeGroup.children.length > 1 || getNavigationLeafItems(activeGroup.children).length > 1);
  const showOfferEditorToolbar = isOfferWorkspace
    && !['generator', 'noAccess'].includes(activeView)
    && canViewActiveTool;
  const showOfferExports = showOfferEditorToolbar && canCreateActiveTool;
  const showOfferEditorSidebar = isEditableOfferView && isLayoutEditMode && !isTopBarCollapsed;
  const showOfferLayoutTopPanel = isEditableOfferView && isLayoutDragMode && !isTopBarCollapsed;
  const compactToolbarButtonStyle = {
    minHeight: '34px',
    padding: '7px 12px',
    fontSize: '11.5px',
    whiteSpace: 'nowrap',
  };
  const dropdownSummaryStyle = {
    ...compactToolbarButtonStyle,
    listStyle: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };
  const toolbarMenuItemStyle = {
    width: '100%',
    justifyContent: 'flex-start',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '11.5px',
  };

  const renderSubNavigationLeaf = (child, isNested = false) => {
    const Icon = child.icon;
    const isActive = activeNavigationKey === child.key;
    const isPrimaryContractEntry = child.key === 'vertraege-am-pending';
    return (
      <button
        key={child.key}
        onClick={() => handleNavigationSelect(child.key)}
        aria-current={isActive ? 'page' : undefined}
        style={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: isNested ? '11px' : '11.5px',
          fontWeight: isActive ? 700 : 500,
          padding: isNested ? '6px 10px' : '6px 12px',
          borderRadius: '9px',
          border: '1px solid',
          borderColor: isActive ? '#122A5C' : (isNested ? (isPrimaryContractEntry ? '#FED7AA' : '#E2E8F0') : 'transparent'),
          background: isActive ? '#122A5C' : (isNested ? (isPrimaryContractEntry ? '#FFF7ED' : '#FFFFFF') : 'transparent'),
          color: isActive ? '#FFFFFF' : (isPrimaryContractEntry && isNested ? '#9A3412' : '#475569'),
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          whiteSpace: 'nowrap',
          transition: 'all 0.15s ease',
          flexShrink: 0,
          boxShadow: isActive ? '0 6px 14px rgba(18, 42, 92, 0.16)' : 'none',
        }}
      >
        <Icon size={13} />
        {child.label}
      </button>
    );
  };

  const renderSubNavigationItem = (child) => {
    if (!Array.isArray(child.children) || child.children.length === 0) {
      return renderSubNavigationLeaf(child);
    }

    const Icon = child.icon;
    const leaves = getNavigationLeafItems(child.children);
    const firstLeaf = leaves[0];
    const isActive = navigationItemContainsKey(child, activeNavigationKey);
    return (
      <div
        key={child.key}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '3px',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: isActive ? '#CBD5E1' : 'transparent',
          background: isActive ? '#F8FAFC' : 'transparent',
          boxShadow: isActive ? '0 1px 2px rgba(15, 23, 42, 0.05)' : 'none',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => firstLeaf?.key && handleNavigationSelect(firstLeaf.key)}
          aria-current={isActive && leaves.length === 1 ? 'page' : undefined}
          style={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: '11px',
            fontWeight: isActive ? 700 : 600,
            padding: '6px 10px',
            borderRadius: '9px',
            border: '1px solid',
            borderColor: isActive ? '#E2E8F0' : 'transparent',
            background: isActive ? '#EAF1F8' : 'transparent',
            color: isActive ? '#122A5C' : '#475569',
            cursor: firstLeaf?.key ? 'pointer' : 'default',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s ease',
            flexShrink: 0,
          }}
        >
          <Icon size={13} />
          {child.label}
        </button>
        {isActive && leaves.map((leaf) => renderSubNavigationLeaf(leaf, true))}
      </div>
    );
  };

  const renderAndroidNavigationItems = (items = [], depth = 0) => items.map((item) => {
    const Icon = item.icon;

    if (Array.isArray(item.children) && item.children.length > 0) {
      return (
        <div className="android-tool-cluster" key={item.key}>
          <div className="android-tool-cluster-title">
            <Icon size={14} />
            <span>{item.label}</span>
          </div>
          <div className="android-tool-cluster-items">
            {renderAndroidNavigationItems(item.children, depth + 1)}
          </div>
        </div>
      );
    }

    const isActive = activeNavigationKey === item.key;
    return (
      <button
        key={item.key}
        type="button"
        className={`android-tool-item${depth > 0 ? ' android-tool-item--nested' : ''}${isActive ? ' android-tool-item--active' : ''}`}
        aria-current={isActive ? 'page' : undefined}
        onClick={(event) => handleNavigationMenuSelect(item.key, event)}
      >
        <span className="android-tool-item-icon">
          <Icon size={16} />
        </span>
        <span className="android-tool-item-label">{item.label}</span>
        {isActive && <span className="android-tool-item-state">Aktiv</span>}
      </button>
    );
  });

  const renderAndroidBottomNavigation = () => {
    if (!isAndroidAppShell || toolPermissions.loading || visibleAndroidMainNavigationItems.length === 0) {
      return null;
    }

    const moreIsActive = visibleAndroidContextToolItems.some((item) => item.key === activeNavigationKey);

    return (
      <nav className="android-bottom-navigation" data-print-hidden="true" aria-label="Vertragsnavigation">
        {visibleAndroidMainNavigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeNavigationKey === item.key;
          return (
            <button
              key={item.key}
              type="button"
              className={`android-bottom-tab${isActive ? ' android-bottom-tab--active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => handleNavigationSelect(item.key)}
            >
              <Icon size={22} />
              <span>{item.label}</span>
            </button>
          );
        })}

        {visibleAndroidContextToolItems.length > 0 && (
          <details className={`android-bottom-more${moreIsActive ? ' android-bottom-more--active' : ''}`}>
            <summary className="android-bottom-tab">
              <Menu size={22} />
              <span>Mehr</span>
            </summary>
            <div className="android-sheet android-bottom-sheet">
              <div className="android-sheet-handle" aria-hidden />
              <div className="android-sheet-heading">
                <span>Weitere Werkzeuge</span>
                <strong>Verträge & Dokumente</strong>
              </div>
              <div className="android-tool-list">
                {renderAndroidNavigationItems(visibleAndroidContextToolItems)}
              </div>
            </div>
          </details>
        )}
      </nav>
    );
  };

  const renderToolbarDropdown = (label, children, title = '') => (
    <details className="offer-toolbar-dropdown">
      <summary className="btn-docx offer-toolbar-summary" title={title || label} style={dropdownSummaryStyle}>
        {label}
        <ChevronDown size={13} />
      </summary>
      <div className="offer-toolbar-menu">
        {children}
      </div>
    </details>
  );

  const renderOfferLayoutScrollPanel = () => {
    if (!showOfferLayoutTopPanel) return null;

    return (
      <div className="offer-layout-scroll-panel" data-print-hidden="true">
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '15px', fontWeight: 700, color: '#122A5C', margin: 0 }}>Abstände</h3>
          </div>

          {sectionSpacingGroups.length === 0 ? (
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B', margin: 0 }}>
              Es konnten noch keine Sektionsabstände gemessen werden.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '220px', overflowY: 'auto' }}>
              {sectionSpacingGroups.map((group) => (
                <div key={group.key} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                      {group.title}
                    </p>
                    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: group.isBalanced ? '#15803D' : '#64748B' }}>
                      {group.isBalanced ? `gleichmäßig · ${group.preferredGap}px` : `Ziel ${group.preferredGap}px · Varianz ${group.variation}px`}
                    </span>
                  </div>

                  {group.metrics.map((metric) => (
                    <div
                      key={metric.key}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 92px 96px',
                        gap: '8px',
                        alignItems: 'center',
                        border: metric.isPreferredGap ? '1px solid #BBF7D0' : '1px solid transparent',
                        background: metric.isPreferredGap ? '#F0FDF4' : 'transparent',
                        borderRadius: '12px',
                        padding: '8px 10px',
                      }}
                    >
                      <div>
                        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', margin: 0 }}>
                          {metric.fromLabel} -> {metric.toLabel}
                        </p>
                        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: '#64748B', margin: '4px 0 0' }}>
                          Aktuell: {metric.gap}px{metric.isPreferredGap ? ' · im System' : ` · Ziel ${group.preferredGap}px`}
                        </p>
                      </div>

                      <input
                        type="number"
                        value={spacingDrafts[metric.key] ?? String(metric.gap)}
                        onChange={(event) => handleSpacingDraftChange(metric.key, event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            handleApplySpacingValue(metric);
                          }
                        }}
                        style={{
                          width: '100%',
                          border: '1px solid #CBD5E1',
                          borderRadius: '10px',
                          padding: '8px 10px',
                          fontFamily: 'Manrope, sans-serif',
                          fontSize: '12px',
                          color: '#122A5C',
                          background: '#FFFFFF',
                        }}
                      />

                      <button
                        onClick={() => handleApplySpacingValue(metric)}
                        className="btn-docx"
                        title="Diesen Abstand auf den eingegebenen Pixelwert setzen"
                      >
                        <span className="hidden sm:inline">Setzen</span>
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Spacing Assist</p>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 700, color: '#122A5C', margin: '4px 0 0' }}>Guides, Snap und Verteilung</h3>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button
              onClick={() => setShowSpacingGuides((currentValue) => !currentValue)}
              className="btn-docx"
              style={{
                background: showSpacingGuides ? '#ECFDF5' : '#FFFFFF',
                borderColor: showSpacingGuides ? '#86EFAC' : '#E2E8F0',
                color: showSpacingGuides ? '#166534' : '#475569',
              }}
              title="Messlinien und Distanz-Overlays im Canvas ein- oder ausblenden"
            >
              <span className="hidden sm:inline">Guides {showSpacingGuides ? 'an' : 'aus'}</span>
            </button>

            <button
              onClick={() => setEnableSmartSpacingSnap((currentValue) => !currentValue)}
              className="btn-docx"
              style={{
                background: enableSmartSpacingSnap ? '#FFF7ED' : '#FFFFFF',
                borderColor: enableSmartSpacingSnap ? '#FDBA74' : '#E2E8F0',
                color: enableSmartSpacingSnap ? '#C2410C' : '#475569',
              }}
              title="Häufige oder gleichmäßige Abstände beim Ziehen automatisch einrasten"
            >
              <span className="hidden sm:inline">Smart Snap {enableSmartSpacingSnap ? 'an' : 'aus'}</span>
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#475569', margin: 0 }}>
              Grid-System
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {SPACING_GRID_OPTIONS.map((gridOption) => {
                const isActive = spacingGridSize === gridOption;
                const label = gridOption === 0 ? 'Kein Grid' : `${gridOption}px`;

                return (
                  <button
                    key={gridOption}
                    onClick={() => setSpacingGridSize(gridOption)}
                    className="btn-docx"
                    style={{
                      background: isActive ? '#EFF6FF' : '#FFFFFF',
                      borderColor: isActive ? '#93C5FD' : '#E2E8F0',
                      color: isActive ? '#1D4ED8' : '#475569',
                    }}
                    title={gridOption === 0 ? 'Kein Raster für Abstände verwenden' : `Abstände auf ${gridOption}px Raster ausrichten`}
                  >
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', background: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#475569', margin: 0 }}>
              Live-Feedback
            </p>

            {activeSpacingFeedback ? (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {activeSpacingFeedback.badges.map((badge) => (
                    <span
                      key={badge.label}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        borderRadius: '999px',
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '10px',
                        fontWeight: 700,
                        background: badge.tone === 'green' ? '#DCFCE7' : (badge.tone === 'blue' ? '#DBEAFE' : '#FFEDD5'),
                        color: badge.tone === 'green' ? '#166534' : (badge.tone === 'blue' ? '#1D4ED8' : '#C2410C'),
                      }}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>

                <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', margin: 0 }}>
                  {getElementLabel(activeSpacingFeedback.elementId)}
                </p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#475569', margin: 0 }}>
                  {activeSpacingFeedback.previousGap !== null ? `Oben ${activeSpacingFeedback.previousGap}px` : 'Kein oberer Abstand'}
                  {' · '}
                  {activeSpacingFeedback.nextGap !== null ? `Unten ${activeSpacingFeedback.nextGap}px` : 'Kein unterer Abstand'}
                </p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B', margin: 0 }}>
                  {activeSpacingFeedback.summary}
                </p>
              </>
            ) : null}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#475569', margin: 0 }}>
              Vorschläge zur gleichmäßigen Verteilung
            </p>

            {spacingDistributionSuggestions.length === 0 ? (
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#15803D', margin: 0 }}>
                Alle gemessenen Bereichsabstände sind aktuell konsistent.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                {spacingDistributionSuggestions.map((group) => (
                  <div key={group.key} style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px 12px', background: '#FFFFFF', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <div>
                      <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', margin: 0 }}>
                        {group.title}
                      </p>
                      <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: '#64748B', margin: '4px 0 0' }}>
                        Vorschlag: alle Lücken auf {group.preferredGap}px setzen · aktuelle Varianz {group.variation}px
                      </p>
                    </div>

                    <button
                      onClick={() => handleDistributeSpacingGroup(group)}
                      className="btn-docx"
                      title="Alle gemessenen Abstände dieser Seite gleichmäßig verteilen"
                    >
                      <span className="hidden sm:inline">Verteilen</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`app-container${isAndroidAppShell ? ' app-container--android-shell' : ''}${showOfferEditorSidebar ? ' app-container--offer-editor-sidebar' : ''}${showOfferLayoutTopPanel ? ' app-container--offer-layout-top-panel' : ''}`}>
      <Toaster position="top-right" richColors />
      <input
        ref={layoutImportInputRef}
        type="file"
        accept="application/json,.json"
        onChange={handleImportLayoutProjectFile}
        style={{ display: 'none' }}
      />

      {/* TOP BAR */}
      <div className="top-action-bar" data-print-hidden="true">
        <div className="top-action-left flex items-center gap-4">
          {activeView === 'freiflaeche' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <EnPowerLogoText size="sm" variant="dark" />
              <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 300, color: '#CBD5E1' }}>&times;</span>
              <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '15px', color: '#122A5C', letterSpacing: '-0.02em' }}>ILOS</span>
            </div>
          ) : (
            <EnPowerLogoText size="sm" variant="dark" />
          )}
          {isAndroidAppShell ? (
            <div className="android-app-navigation">
              <div className="android-current-context" aria-live="polite">
                <span className="android-workspace-icon">
                  {activeNavigationItem?.icon ? React.createElement(activeNavigationItem.icon, { size: 17 }) : <Briefcase size={17} />}
                </span>
                <span className="android-workspace-copy">
                  <span>Aktuell</span>
                  <strong>{activeNavigationItem?.label || TYPE_LABELS[activeView] || activeView}</strong>
                </span>
              </div>

              <details className="android-global-menu">
                <summary className="workspace-menu-summary" aria-label="Menü öffnen">
                  <Menu size={18} />
                  <span>Menü</span>
                </summary>
                <div className="android-sheet android-global-sheet">
                  <div className="android-sheet-handle" aria-hidden />
                  <div className="android-sheet-heading">
                    <span>Weitere Werkzeuge</span>
                    <strong>Vertraege & Dokumente</strong>
                  </div>
                  <div className="android-tool-list">
                    {toolPermissions.loading ? (
                      <div className="workspace-menu-loading">Rechte werden geladen...</div>
                    ) : renderAndroidNavigationItems(visibleAndroidContextToolItems)}
                  </div>
                </div>
              </details>
            </div>
          ) : (
            <>
              <span className="text-slate-300 text-lg font-light" aria-hidden>|</span>

              <div className="top-navigation-pills" style={{ display: 'flex', gap: '3px', background: '#F1F5F9', borderRadius: '999px', padding: '3px' }}>
                {toolPermissions.loading ? (
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11.5px', fontWeight: 700, padding: '6px 14px', color: '#64748B' }}>Rechte werden geladen...</span>
                ) : visibleNavigationGroups.map((group) => {
                  const Icon = group.icon;
                  const isActive = activeGroup?.key === group.key;
                  return (
                    <button
                      key={group.key}
                      onClick={() => handleGroupSelect(group.key)}
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '11px',
                        fontWeight: isActive ? 700 : 500,
                        padding: '6px 12px',
                        borderRadius: '999px',
                        border: 'none',
                        background: isActive ? 'white' : 'transparent',
                        color: isActive ? '#122A5C' : '#64748B',
                        cursor: 'pointer',
                        boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <Icon size={13} />
                      {group.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Collapse Button + Downloads – nur bei Angebotsansichten */}
        <div className="top-action-right flex items-center gap-2">
          {false && !['designsystem', 'vermittlervertrag', 'vertraege', 'exposes-automation', 'vertraege-am', 'vertraege-am-bearbeiten', 'vertraege-am-pending', 'vertraege-am-automatisierungen', 'vertraege-am-klauseln', 'standarddokumente', 'standarddokumente-vorlagen', 'settings', 'generator', 'noAccess'].includes(activeView) && canViewActiveTool && (
            <>
            {isEditableOfferView && isLayoutEditMode && (
              <button
                onClick={() => setIsTopBarCollapsed(!isTopBarCollapsed)}
                className="btn-docx"
                title={isTopBarCollapsed ? 'Bereich ausklappen' : 'Bereich einklappen'}
                style={{
                  padding: '8px 10px',
                  minWidth: 'auto',
                }}
              >
                {isTopBarCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              </button>
            )}

            {isEditableOfferView && (
              <button
                onClick={handleToggleLayoutEditMode}
                className="btn-docx"
                title={isLayoutEditMode ? 'Bereiche verschieben deaktivieren' : 'Bereiche verschieben aktivieren'}
                style={{
                  background: isLayoutEditMode ? '#FEF3C7' : '#FFFFFF',
                  borderColor: isLayoutEditMode ? '#FDE68A' : '#E2E8F0',
                  color: isLayoutEditMode ? '#92400E' : '#475569',
                }}
              >
                <Building2 size={14} />
                <span className="hidden sm:inline">{isLayoutEditMode ? 'Bearbeitung aktiv' : 'Bearbeitung starten'}</span>
              </button>
            )}

            {isEditableOfferView && isLayoutEditMode && (
              <div style={{ display: 'inline-flex', gap: '6px', padding: '4px', borderRadius: '10px', border: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                <button
                  onClick={() => setEditorMode('layout')}
                  className="btn-docx"
                  title="Layout-Modus: Bereiche verschieben"
                  style={{
                    background: isLayoutDragMode ? '#DBEAFE' : '#FFFFFF',
                    borderColor: isLayoutDragMode ? '#93C5FD' : '#E2E8F0',
                    color: isLayoutDragMode ? '#1E40AF' : '#475569',
                  }}
                >
                  <span className="hidden sm:inline">Layout</span>
                </button>

                <button
                  onClick={() => setEditorMode('text')}
                  className="btn-docx"
                  title="Schrift-Modus: Texte direkt bearbeiten"
                  style={{
                    background: isTextEditMode ? '#FFEDD5' : '#FFFFFF',
                    borderColor: isTextEditMode ? '#FDBA74' : '#E2E8F0',
                    color: isTextEditMode ? '#9A3412' : '#475569',
                  }}
                >
                  <span className="hidden sm:inline">Schrift</span>
                </button>
              </div>
            )}

            {isEditableOfferView && (
              <>
                {automationOffer?.view === getBaseOfferView(activeView) && (
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#166534', background: '#DCFCE7', border: '1px solid #86EFAC', borderRadius: '999px', padding: '5px 10px', whiteSpace: 'nowrap' }}>
                    Automatisch geladen{automationOffer.dealId ? ` · Deal ${automationOffer.dealId}` : ''}
                  </span>
                )}

                <button
                  onClick={handleResetDachLayout}
                  disabled={Object.keys(dachLayoutPositions).length === 0}
                  className="btn-docx"
                  title="Bereichspositionen zurücksetzen"
                >
                  <span className="hidden sm:inline">Layout zurücksetzen</span>
                </button>

                <button
                  onClick={handleSaveDachLayout}
                  disabled={isLoadingStoredLayout || isSavingStoredLayout || isRestoringStoredLayout || isPublishingLayout}
                  className="btn-docx"
                  title="Aktuelles Layout online als Entwurf speichern (wird noch nicht von der Automation genutzt)"
                >
                  <span className="hidden sm:inline">Entwurf speichern</span>
                </button>

                <button
                  onClick={handlePublishLayout}
                  disabled={isLoadingStoredLayout || isSavingStoredLayout || isRestoringStoredLayout || isPublishingLayout}
                  className="btn-docx"
                  title={`Vorlage "${activeDocumentKey}" für Bitrix-Automation freigeben – nur diese Version wird für neue Angebote genutzt${publishedLayoutAt ? ` · Zuletzt veröffentlicht: ${new Date(publishedLayoutAt).toLocaleString('de-DE')}` : ''}`}
                  style={{
                    background: '#EFF6FF',
                    borderColor: '#93C5FD',
                    color: '#1D4ED8',
                    fontWeight: 600,
                  }}
                >
                  <span className="hidden sm:inline">
                    {isPublishingLayout ? 'Wird veröffentlicht…' : publishedLayoutAt ? 'Neu veröffentlichen' : 'Für Automation veröffentlichen'}
                  </span>
                </button>

                <button
                  onClick={handleExportLayoutProject}
                  className="btn-docx"
                  title="Projektdatei mit allen Layout-Anpassungen exportieren"
                >
                  <span className="hidden sm:inline">Projekt export</span>
                </button>

                <button
                  onClick={handleImportLayoutProjectClick}
                  className="btn-docx"
                  title="Projektdatei laden und nahtlos weiterbearbeiten"
                >
                  <Upload size={14} />
                  <span className="hidden sm:inline">Projekt laden</span>
                </button>

                <button
                  onClick={handleUndoLastLayoutChange}
                  disabled={lastLayoutSnapshot === null}
                  className="btn-docx"
                  title="Letzte Änderung rückgängig machen (Strg+Z)"
                >
                  <Undo size={14} />
                  <span className="hidden sm:inline">Letzte Änderung</span>
                </button>

                <button
                  onClick={() => setShowElementOutlines((v) => !v)}
                  className="btn-docx"
                  title={showElementOutlines ? 'Element-Rahmen ausblenden' : 'Element-Rahmen einblenden'}
                  style={{
                    background: showElementOutlines ? '#EFF6FF' : '#FFFFFF',
                    borderColor: showElementOutlines ? '#93C5FD' : '#E2E8F0',
                    color: showElementOutlines ? '#1D4ED8' : '#475569',
                  }}
                >
                  <span className="hidden sm:inline">Rahmen {showElementOutlines ? 'an' : 'aus'}</span>
                </button>

                <button
                  onClick={() => setSelectedLayoutElements([...activeDetailElementIds])}
                  className="btn-docx"
                  title="Alle Detail-Elemente markieren und gemeinsam verschieben"
                >
                  <span className="hidden sm:inline">Alle markieren</span>
                </button>

                <button
                  onClick={handleClearLayoutSelection}
                  disabled={selectedLayoutElements.length === 0}
                  className="btn-docx"
                  title="Ausgewählte Elemente oder Gruppe abwählen"
                >
                  <span className="hidden sm:inline">Auswahl leeren</span>
                </button>

                {isLayoutDragMode && (
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 600, color: '#64748B', whiteSpace: 'nowrap' }}>
                    {(selectedLayoutElements.length > 1 ? `${selectedLayoutElements.length} Elemente gruppiert` : 'Strg/Shift + Klick = Gruppe')}
                    {' • '}
                    {layoutStatusLabel}
                    {' • Strg+Z = letzte Änderung'}
                  </span>
                )}
              </>
            )}

            {canCreateActiveTool && (
              <>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginRight: '4px' }}>Downloads</span>

            <button onClick={handleExportPdf} data-testid="export-pdf-button" className="btn-pdf" title={activeView === 'vertrieb' ? 'Bearbeitete Präsentation als PDF drucken / speichern' : 'Als PDF drucken / speichern'}>
              <FileDown size={14} />
              <span className="hidden sm:inline">PDF</span>
            </button>

            <button onClick={handleExportDocx} disabled={isExportingDocx || !supportsDocxExport} data-testid="export-docx-button" className="btn-docx" title={supportsDocxExport ? 'Als DOCX herunterladen' : 'DOCX ist für diese Vorlage nicht verfügbar'}>
              <FileText size={14} />
              <span className="hidden sm:inline">{supportsDocxExport ? (isExportingDocx ? '...' : 'DOCX') : 'DOCX'}</span>
            </button>

            <button onClick={handleExportHtml} disabled={isExportingHtml} className="btn-docx" title="Als HTML-Code herunterladen" data-testid="export-html-button">
              <Code size={14} />
              <span className="hidden sm:inline">{isExportingHtml ? '...' : 'Code'}</span>
            </button>

            <button onClick={handleExportPng} disabled={isExportingPng} className="btn-docx" title="Als PNG für Canva herunterladen" data-testid="export-png-button">
              <Image size={14} />
              <span className="hidden sm:inline">{isExportingPng ? '...' : 'PNG'}</span>
            </button>
              </>
            )}
            </>
          )}

          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#64748B', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '999px', padding: '6px 10px', whiteSpace: 'nowrap' }}>
            {session?.user?.email || 'Eingeloggt'}
          </span>
          <button onClick={onLogout} className="btn-docx" title="Aus dem Tool ausloggen">
            <LogOut size={14} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {/* Sub-Navigation: Kinder der aktiven Gruppe */}
      {((shouldShowActiveSubNavigation && !isAndroidAppShell) || (showOfferEditorToolbar && isAndroidAppShell)) && (
        <div
          className={`sub-navigation-bar${showOfferEditorToolbar ? ' sub-navigation-bar--with-toolbar' : ''}${isAndroidAppShell ? ' android-action-strip' : ''}`}
          data-print-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 24px',
            background: '#FFFFFF',
            borderBottom: '1px solid #E2E8F0',
            flexWrap: isOfferWorkspace ? 'wrap' : 'nowrap',
            overflowX: isOfferWorkspace ? 'visible' : 'auto',
            overflowY: 'visible',
            position: 'relative',
            zIndex: 210,
          }}
        >
          {!isAndroidAppShell && (
            <div
              className="sub-navigation-items"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flexWrap: isOfferWorkspace ? 'wrap' : 'nowrap',
                overflowX: isOfferWorkspace ? 'visible' : 'auto',
                overflowY: 'visible',
                minWidth: 0,
              }}
            >
              {activeGroup.children.map(renderSubNavigationItem)}
            </div>
          )}

          {showOfferEditorToolbar && (
            <div
              className="offer-editor-toolbar"
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '6px',
                flexWrap: 'wrap',
                minWidth: 0,
              }}
            >
              {isEditableOfferView && !isLayoutEditMode && (
                <>
                  <button
                    onClick={handleToggleLayoutEditMode}
                    className="btn-docx"
                    title="Bereiche verschieben aktivieren"
                    style={compactToolbarButtonStyle}
                  >
                    <Building2 size={13} />
                    <span>Bearbeitung starten</span>
                  </button>
                  <button
                    onClick={handleImportLayoutProjectClick}
                    className="btn-docx"
                    title="Projektdatei laden und nahtlos weiterbearbeiten"
                    style={compactToolbarButtonStyle}
                  >
                    <Upload size={13} />
                    <span>Projekt laden</span>
                  </button>
                </>
              )}

              {isEditableOfferView && isLayoutEditMode && (
                <>
                  <button
                    onClick={handleSaveDachLayout}
                    disabled={isLoadingStoredLayout || isSavingStoredLayout || isRestoringStoredLayout || isPublishingLayout}
                    className="btn-docx"
                    title="Aktuelles Layout online als Entwurf speichern"
                    style={compactToolbarButtonStyle}
                  >
                    Entwurf speichern
                  </button>
                  <button
                    onClick={handlePublishLayout}
                    disabled={isLoadingStoredLayout || isSavingStoredLayout || isRestoringStoredLayout || isPublishingLayout}
                    className="btn-docx"
                    title={`Vorlage "${activeDocumentKey}" f\u00fcr Bitrix-Automation freigeben${publishedLayoutAt ? ` - Zuletzt ver\u00f6ffentlicht: ${new Date(publishedLayoutAt).toLocaleString('de-DE')}` : ''}`}
                    style={{
                      ...compactToolbarButtonStyle,
                      background: '#EFF6FF',
                      borderColor: '#93C5FD',
                      color: '#1D4ED8',
                      fontWeight: 700,
                    }}
                  >
                    {isPublishingLayout ? 'Wird ver\u00f6ffentlicht...' : publishedLayoutAt ? 'Neu ver\u00f6ffentlichen' : 'F\u00fcr Automation ver\u00f6ffentlichen'}
                  </button>
                </>
              )}

              {isEditableOfferView && isLayoutEditMode && renderToolbarDropdown('Ansicht', (
                <>
                  <button
                    onClick={() => setEditorMode('layout')}
                    className="btn-docx"
                    title="Layout-Modus: Bereiche verschieben"
                    style={{
                      ...toolbarMenuItemStyle,
                      background: isLayoutDragMode ? '#DBEAFE' : '#FFFFFF',
                      borderColor: isLayoutDragMode ? '#93C5FD' : '#E2E8F0',
                      color: isLayoutDragMode ? '#1E40AF' : '#475569',
                    }}
                  >
                    Layout-Modus
                  </button>
                  <button
                    onClick={() => setEditorMode('text')}
                    className="btn-docx"
                    title="Schrift-Modus: Texte direkt bearbeiten"
                    style={{
                      ...toolbarMenuItemStyle,
                      background: isTextEditMode ? '#FFEDD5' : '#FFFFFF',
                      borderColor: isTextEditMode ? '#FDBA74' : '#E2E8F0',
                      color: isTextEditMode ? '#9A3412' : '#475569',
                    }}
                  >
                    Schrift-Modus
                  </button>
                  <button
                    onClick={handleResetDachLayout}
                    disabled={Object.keys(dachLayoutPositions).length === 0}
                    className="btn-docx"
                    title="Bereichspositionen zurücksetzen"
                    style={toolbarMenuItemStyle}
                  >
                    Layout zurücksetzen
                  </button>
                  <button
                    onClick={() => setShowElementOutlines((v) => !v)}
                    className="btn-docx"
                    title={showElementOutlines ? 'Element-Rahmen ausblenden' : 'Element-Rahmen einblenden'}
                    style={{
                      ...toolbarMenuItemStyle,
                      background: showElementOutlines ? '#EFF6FF' : '#FFFFFF',
                      borderColor: showElementOutlines ? '#93C5FD' : '#E2E8F0',
                      color: showElementOutlines ? '#1D4ED8' : '#475569',
                    }}
                  >
                    Rahmen {showElementOutlines ? 'an' : 'aus'}
                  </button>
                  <button
                    onClick={() => setIsTopBarCollapsed(!isTopBarCollapsed)}
                    className="btn-docx"
                    title={isTopBarCollapsed ? 'Bereich ausklappen' : 'Bereich einklappen'}
                    style={toolbarMenuItemStyle}
                  >
                    {isTopBarCollapsed ? 'Bereich ausklappen' : 'Bereich einklappen'}
                  </button>
                </>
              ), 'Ansicht und Bearbeitungsmodus')}

              {isEditableOfferView && isLayoutEditMode && renderToolbarDropdown('Projekt', (
                <>
                  <button
                    onClick={handleImportLayoutProjectClick}
                    className="btn-docx"
                    title="Projektdatei laden und nahtlos weiterbearbeiten"
                    style={toolbarMenuItemStyle}
                  >
                    <Upload size={13} />
                    Projekt laden
                  </button>
                  <button
                    onClick={handleExportLayoutProject}
                    className="btn-docx"
                    title="Projektdatei mit allen Layout-Anpassungen exportieren"
                    style={toolbarMenuItemStyle}
                  >
                    Projekt exportieren
                  </button>
                  <button
                    onClick={handleUndoLastLayoutChange}
                    disabled={lastLayoutSnapshot === null}
                    className="btn-docx"
                    title="Letzte Änderung rückgängig machen (Strg+Z)"
                    style={toolbarMenuItemStyle}
                  >
                    <Undo size={13} />
                    Letzte Änderung
                  </button>
                </>
              ), 'Projekt laden, exportieren oder Änderung rückgängig machen')}

              {isEditableOfferView && isLayoutEditMode && renderToolbarDropdown('Auswahl', (
                <>
                  <button
                    onClick={() => setSelectedLayoutElements([...activeDetailElementIds])}
                    className="btn-docx"
                    title="Alle Detail-Elemente markieren und gemeinsam verschieben"
                    style={toolbarMenuItemStyle}
                  >
                    Alle markieren
                  </button>
                  <button
                    onClick={handleClearLayoutSelection}
                    disabled={selectedLayoutElements.length === 0}
                    className="btn-docx"
                    title="Ausgewählte Elemente oder Gruppe abwählen"
                    style={toolbarMenuItemStyle}
                  >
                    Auswahl leeren
                  </button>
                </>
              ), 'Auswahl-Werkzeuge')}

              {showOfferExports && renderToolbarDropdown('Export', (
                <>
                  <button
                    onClick={handleExportPdf}
                    data-testid="export-pdf-button"
                    className="btn-pdf"
                    title={activeView === 'vertrieb' ? 'Bearbeitete Präsentation als PDF drucken / speichern' : 'Als PDF drucken / speichern'}
                    style={toolbarMenuItemStyle}
                  >
                    <FileDown size={13} />
                    PDF
                  </button>
                  <button
                    onClick={handleExportDocx}
                    disabled={isExportingDocx || !supportsDocxExport}
                    data-testid="export-docx-button"
                    className="btn-docx"
                    title={supportsDocxExport ? 'Als DOCX herunterladen' : 'DOCX ist für diese Vorlage nicht verfügbar'}
                    style={toolbarMenuItemStyle}
                  >
                    <FileText size={13} />
                    {supportsDocxExport ? (isExportingDocx ? '...' : 'DOCX') : 'DOCX'}
                  </button>
                  <button
                    onClick={handleExportHtml}
                    disabled={isExportingHtml}
                    className="btn-docx"
                    title="Als HTML-Code herunterladen"
                    data-testid="export-html-button"
                    style={toolbarMenuItemStyle}
                  >
                    <Code size={13} />
                    {isExportingHtml ? '...' : 'Code'}
                  </button>
                  <button
                    onClick={handleExportPng}
                    disabled={isExportingPng}
                    className="btn-docx"
                    title="Als PNG für Canva herunterladen"
                    data-testid="export-png-button"
                    style={toolbarMenuItemStyle}
                  >
                    <Image size={13} />
                    {isExportingPng ? '...' : 'PNG'}
                  </button>
                </>
              ), 'Export und Downloads')}

              {isEditableOfferView && isLayoutDragMode && (
                <span className="offer-toolbar-status">
                  {selectedLayoutElements.length > 1 ? `${selectedLayoutElements.length} Elemente gruppiert` : layoutStatusLabel}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {isEditableOfferView && isLayoutEditMode && !isTopBarCollapsed && (
        <div
          className="top-bar-collapsible"
          data-print-hidden="true"
          style={{
            padding: '14px 24px 18px',
            background: '#F8FAFC',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
            {isLayoutDragMode && (
            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' }}>
                <div>
                  <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '15px', fontWeight: 700, color: '#122A5C', margin: 0 }} title={[`Vorlage: ${activeDocumentKey}`, layoutStoragePath ? `Speicherort: ${layoutStoragePath}` : ''].filter(Boolean).join('\n')}>Gespeicherte Versionen</h3>
                </div>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {layoutStatusLabel}
                </span>
              </div>

              {layoutPersistenceError && (
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: '#B91C1C', margin: 0 }}>
                  {layoutPersistenceError}
                </p>
              )}

              {latestLayoutVersions.length === 0 ? (
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B', margin: 0 }}>
                  Noch keine online gespeicherten Versionen vorhanden.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                  {latestLayoutVersions.map((version) => (
                    <div
                      key={version.versionId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        borderRadius: '12px',
                        border: version.isActive ? '1px solid #F59E0B' : '1px solid #E2E8F0',
                        background: version.isActive ? '#FFFBF5' : '#F8FAFC',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', margin: 0 }}>
                          {version.label || 'Layout gespeichert'}
                        </p>
                        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: '#64748B', margin: '4px 0 0' }}>
                          {formatLayoutVersionDate(version.createdAt)}{version.source && version.source !== 'manual' ? ` • ${version.source}` : ''}
                        </p>
                      </div>

                      <button
                        onClick={() => restoreLayoutVersionFromProject(version.versionId)}
                        disabled={version.isActive || isRestoringStoredLayout}
                        className="btn-docx"
                        title={version.isActive ? 'Dies ist bereits die aktive Version' : 'Diese Version wiederherstellen'}
                      >
                        <span className="hidden sm:inline">{version.isActive ? 'Aktiv' : 'Wiederherstellen'}</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '15px', fontWeight: 700, color: '#122A5C', margin: 0 }}>Texte</h3>
              </div>

              {activeView === 'dach' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                {DACH_EDITABLE_TEXT_FIELDS
                  .filter((field) => !field.variant || field.variant === (
                    effectiveDachProposalData?.offerVariant === 'sanierung' ? 'sanierung' : 'standard'
                  ))
                  .map((field) => {
                  const fieldValue = String(getValueByPath(dachTextContent, field.path) || '');

                  return (
                    <label key={field.path} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#475569' }}>{field.label}</span>
                      {field.multiline ? (
                        <textarea
                          value={fieldValue}
                          onChange={(event) => handleDachTextFieldChange(field.path, event.target.value)}
                          rows={3}
                          style={{ width: '100%', border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 10px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#122A5C', background: '#FFFFFF', resize: 'vertical' }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={fieldValue}
                          onChange={(event) => handleDachTextFieldChange(field.path, event.target.value)}
                          style={{ width: '100%', border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 10px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#122A5C', background: '#FFFFFF' }}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
              ) : (
              <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', background: '#F8FAFC', display: 'grid', gap: '10px' }}>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B', margin: 0 }}>
                  Im Schrift-Modus Texte direkt im Canvas anklicken.
                </p>
                {isExposeTemplateView && (
                  <label style={{ display: 'grid', gap: '4px' }}>
                    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#475569' }}>Deckblattbild aus Bitrix / URL / Data-URI</span>
                    <input
                      type="text"
                      value={String((genericTextOverrides[activeGenericTextOverrideKey] || {}).__projectImage || '')}
                      onChange={(event) => handleGenericSpecialFieldChange('__projectImage', event.target.value)}
                      placeholder="z. B. data:image/... oder https://..."
                      style={{ width: '100%', border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 10px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#122A5C', background: '#FFFFFF' }}
                    />
                  </label>
                )}
              </div>
              )}
            </div>

            {isLayoutDragMode && (
            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '15px', fontWeight: 700, color: '#122A5C', margin: 0 }}>Abstände</h3>
              </div>

              {sectionSpacingGroups.length === 0 ? (
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B', margin: 0 }}>
                  Es konnten noch keine Sektionsabstände gemessen werden.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '220px', overflowY: 'auto' }}>
                  {sectionSpacingGroups.map((group) => (
                    <div key={group.key} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                          {group.title}
                        </p>
                        <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: group.isBalanced ? '#15803D' : '#64748B' }}>
                          {group.isBalanced ? `gleichmäßig · ${group.preferredGap}px` : `Ziel ${group.preferredGap}px · Varianz ${group.variation}px`}
                        </span>
                      </div>

                      {group.metrics.map((metric) => (
                        <div
                          key={metric.key}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 92px 96px',
                            gap: '8px',
                            alignItems: 'center',
                            border: metric.isPreferredGap ? '1px solid #BBF7D0' : '1px solid transparent',
                            background: metric.isPreferredGap ? '#F0FDF4' : 'transparent',
                            borderRadius: '12px',
                            padding: '8px 10px',
                          }}
                        >
                          <div>
                            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', margin: 0 }}>
                              {metric.fromLabel} -> {metric.toLabel}
                            </p>
                            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: '#64748B', margin: '4px 0 0' }}>
                              Aktuell: {metric.gap}px{metric.isPreferredGap ? ' · im System' : ` · Ziel ${group.preferredGap}px`}
                            </p>
                          </div>

                          <input
                            type="number"
                            value={spacingDrafts[metric.key] ?? String(metric.gap)}
                            onChange={(event) => handleSpacingDraftChange(metric.key, event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                handleApplySpacingValue(metric);
                              }
                            }}
                            style={{
                              width: '100%',
                              border: '1px solid #CBD5E1',
                              borderRadius: '10px',
                              padding: '8px 10px',
                              fontFamily: 'Manrope, sans-serif',
                              fontSize: '12px',
                              color: '#122A5C',
                              background: '#FFFFFF',
                            }}
                          />

                          <button
                            onClick={() => handleApplySpacingValue(metric)}
                            className="btn-docx"
                            title="Diesen Abstand auf den eingegebenen Pixelwert setzen"
                          >
                            <span className="hidden sm:inline">Setzen</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {isLayoutDragMode && (
            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Spacing Assist</p>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 700, color: '#122A5C', margin: '4px 0 0' }}>Guides, Snap und Verteilung</h3>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <button
                  onClick={() => setShowSpacingGuides((currentValue) => !currentValue)}
                  className="btn-docx"
                  style={{
                    background: showSpacingGuides ? '#ECFDF5' : '#FFFFFF',
                    borderColor: showSpacingGuides ? '#86EFAC' : '#E2E8F0',
                    color: showSpacingGuides ? '#166534' : '#475569',
                  }}
                  title="Messlinien und Distanz-Overlays im Canvas ein- oder ausblenden"
                >
                  <span className="hidden sm:inline">Guides {showSpacingGuides ? 'an' : 'aus'}</span>
                </button>

                <button
                  onClick={() => setEnableSmartSpacingSnap((currentValue) => !currentValue)}
                  className="btn-docx"
                  style={{
                    background: enableSmartSpacingSnap ? '#FFF7ED' : '#FFFFFF',
                    borderColor: enableSmartSpacingSnap ? '#FDBA74' : '#E2E8F0',
                    color: enableSmartSpacingSnap ? '#C2410C' : '#475569',
                  }}
                  title="Häufige oder gleichmäßige Abstände beim Ziehen automatisch einrasten"
                >
                  <span className="hidden sm:inline">Smart Snap {enableSmartSpacingSnap ? 'an' : 'aus'}</span>
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#475569', margin: 0 }}>
                  Grid-System
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {SPACING_GRID_OPTIONS.map((gridOption) => {
                    const isActive = spacingGridSize === gridOption;
                    const label = gridOption === 0 ? 'Kein Grid' : `${gridOption}px`;

                    return (
                      <button
                        key={gridOption}
                        onClick={() => setSpacingGridSize(gridOption)}
                        className="btn-docx"
                        style={{
                          background: isActive ? '#EFF6FF' : '#FFFFFF',
                          borderColor: isActive ? '#93C5FD' : '#E2E8F0',
                          color: isActive ? '#1D4ED8' : '#475569',
                        }}
                        title={gridOption === 0 ? 'Kein Raster für Abstände verwenden' : `Abstände auf ${gridOption}px Raster ausrichten`}
                      >
                        <span className="hidden sm:inline">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {(
                <>
                  <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', background: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#475569', margin: 0 }}>
                      Live-Feedback
                    </p>

                    {activeSpacingFeedback ? (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {activeSpacingFeedback.badges.map((badge) => (
                            <span
                              key={badge.label}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: '999px',
                                fontFamily: 'Manrope, sans-serif',
                                fontSize: '10px',
                                fontWeight: 700,
                                background: badge.tone === 'green' ? '#DCFCE7' : (badge.tone === 'blue' ? '#DBEAFE' : '#FFEDD5'),
                                color: badge.tone === 'green' ? '#166534' : (badge.tone === 'blue' ? '#1D4ED8' : '#C2410C'),
                              }}
                            >
                              {badge.label}
                            </span>
                          ))}
                        </div>

                        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', margin: 0 }}>
                          {getElementLabel(activeSpacingFeedback.elementId)}
                        </p>
                        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#475569', margin: 0 }}>
                          {activeSpacingFeedback.previousGap !== null ? `Oben ${activeSpacingFeedback.previousGap}px` : 'Kein oberer Abstand'}
                          {' • '}
                          {activeSpacingFeedback.nextGap !== null ? `Unten ${activeSpacingFeedback.nextGap}px` : 'Kein unterer Abstand'}
                        </p>
                        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B', margin: 0 }}>
                          {activeSpacingFeedback.summary}
                        </p>
                      </>
                    ) : null}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#475569', margin: 0 }}>
                      Vorschläge zur gleichmäßigen Verteilung
                    </p>

                    {spacingDistributionSuggestions.length === 0 ? (
                      <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#15803D', margin: 0 }}>
                        Alle gemessenen Bereichsabstände sind aktuell konsistent.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                        {spacingDistributionSuggestions.map((group) => (
                          <div key={group.key} style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px 12px', background: '#FFFFFF', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                            <div>
                              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', margin: 0 }}>
                                {group.title}
                              </p>
                              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: '#64748B', margin: '4px 0 0' }}>
                                Vorschlag: alle Lücken auf {group.preferredGap}px setzen · aktuelle Varianz {group.variation}px
                              </p>
                            </div>

                            <button
                              onClick={() => handleDistributeSpacingGroup(group)}
                              className="btn-docx"
                              title="Alle gemessenen Abstände dieser Seite gleichmäßig verteilen"
                            >
                              <span className="hidden sm:inline">Verteilen</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div className="app-body">
        {toolPermissions.loading ? (
          <div style={{ flex: 1, display: 'grid', placeItems: 'center', background: '#F8FAFC', color: '#64748B', fontFamily: 'Manrope, sans-serif' }}>
            Rechte werden geladen...
          </div>
        ) : activeView === 'noAccess' ? (
          <div style={{ flex: 1, display: 'grid', placeItems: 'center', background: '#F8FAFC', padding: '24px' }}>
            <div style={{ maxWidth: '560px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '22px', display: 'grid', gap: '8px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Kein Tool-Zugriff freigeschaltet</h2>
              <p style={{ margin: 0, fontFamily: 'Manrope, sans-serif', color: '#64748B', fontSize: '13px' }}>
                Für diesen Login sind aktuell keine Bereiche aktiviert. Ein Admin kann die Rechte in den allgemeinen Einstellungen freigeben.
              </p>
            </div>
          </div>
        ) : activeView === 'mail-konten' ? (
          <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
            <MailKontenPage apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'zugangsanfragen' ? (
          <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
            <ZugangsanfragenPage apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'automation-uebersicht' ? (
          <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
            <AutomationOverviewPage apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'settings' ? (
          <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
            <ToolSettingsPage apiBase={LAYOUT_API} session={session} onPermissionsChanged={refreshToolPermissions} />
          </div>
        ) : activeView === 'generator' ? (
          <OfferGeneratorPage apiBase="/api" authHeaders={appAuthHeaders} />
        ) : activeView === 'standarddokumente-vorlagen' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC' }}>
            <StandardDocumentsTemplateStudio apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'standarddokumente' ? (
          <StandardDocumentsPage apiBase={LAYOUT_API} session={session} />
        ) : activeView === 'onboarding-signature' ? (
          <EmailSignaturePage apiBase={LAYOUT_API} session={session} />
        ) : activeView === 'mittwald-mail' ? (
          <MittwaldMailGenerator apiBase={LAYOUT_API} session={session} />
        ) : activeView === 'bitrix-user' ? (
          <BitrixUserCreator apiBase={LAYOUT_API} session={session} />
        ) : activeView === 'eeg-rechner' ? (
          <div style={{ padding: '24px 28px' }}>
            <EegRechner />
          </div>
        ) : isDachTemplateView(activeView) ? (
          <div className="proposal-staging" data-proposal-canvas="true" onMouseDown={handleLayoutCanvasMouseDown}>
            {renderOfferLayoutScrollPanel()}
            <div className="proposal-pages" ref={proposalRef} data-testid="proposal-canvas-dach">
              {isLayoutDragMode && (
                <div className="spacing-guide-overlay" data-print-hidden="true">
                  {spacingGridSize > 0 && (
                    <div
                      className="spacing-guide-grid"
                      style={{ backgroundSize: `100% ${spacingGridSize}px` }}
                    />
                  )}

                  {showSpacingGuides && activeSpacingFeedback?.guides?.map((guide) => (
                    <div
                      key={guide.key}
                      className="spacing-guide-connector"
                      style={{
                        top: `${guide.top}px`,
                        left: `${guide.x}px`,
                        height: `${guide.height}px`,
                        color: guide.color,
                      }}
                    >
                      <div className="spacing-guide-cap" style={{ top: 0 }} />
                      <div className="spacing-guide-cap" style={{ bottom: 0 }} />
                      <div className="spacing-guide-label">
                        {guide.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <PageLabel num={1} title={dachPageLabels.page1} />
              <div className="page-gap" />
              <CoverPageDach
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                layoutEditLevel={layoutEditLevel}
                proposalData={effectiveDachProposalData}
                textContent={effectiveDachTextContent}
                onTextChange={handleDachTextFieldChange}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={2} title={dachPageLabels.page2} />
              <div className="page-gap" />
              <ContentPage2Dach
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                layoutEditLevel={layoutEditLevel}
                proposalData={effectiveDachProposalData}
                textContent={effectiveDachTextContent}
                onTextChange={handleDachTextFieldChange}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={3} title={dachPageLabels.page3} />
              <div className="page-gap" />
              <ContentPage3Dach
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                layoutEditLevel={layoutEditLevel}
                proposalData={effectiveDachProposalData}
                textContent={effectiveDachTextContent}
                onTextChange={handleDachTextFieldChange}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={4} title={dachPageLabels.page4} />
              <div className="page-gap" />
              <ContentPage4Dach
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                layoutEditLevel={layoutEditLevel}
                proposalData={effectiveDachProposalData}
                textContent={effectiveDachTextContent}
                onTextChange={handleDachTextFieldChange}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div style={{ height: '48px' }} data-print-hidden="true" />
            </div>
          </div>
        ) : activeView === 'freiflaeche' ? (
          <div className="proposal-staging" data-proposal-canvas="true" onMouseDown={handleLayoutCanvasMouseDown}>
            {renderOfferLayoutScrollPanel()}
            <div className="proposal-pages" ref={proposalRef} data-testid="proposal-canvas-ff">
              {isLayoutDragMode && (
                <div className="spacing-guide-overlay" data-print-hidden="true">
                  {spacingGridSize > 0 && (
                    <div
                      className="spacing-guide-grid"
                      style={{ backgroundSize: `100% ${spacingGridSize}px` }}
                    />
                  )}

                  {showSpacingGuides && activeSpacingFeedback?.guides?.map((guide) => (
                    <div
                      key={guide.key}
                      className="spacing-guide-connector"
                      style={{
                        top: `${guide.top}px`,
                        left: `${guide.x}px`,
                        height: `${guide.height}px`,
                        color: guide.color,
                      }}
                    >
                      <div className="spacing-guide-cap" style={{ top: 0 }} />
                      <div className="spacing-guide-cap" style={{ bottom: 0 }} />
                      <div className="spacing-guide-label">
                        {guide.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <PageLabel num={1} title="Deckblatt" />
              <div className="page-gap" />
              <CoverPageStatic
                data={freiflaecheProposalData}
                editMode={isLayoutDragMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={2} title="Partnerschaft + Vorteile" />
              <div className="page-gap" />
              <ContentPage2Static
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={3} title="Pachtangebot + 3-Stufen-Konditionen" />
              <div className="page-gap" />
              <ContentPage3Static
                data={freiflaecheProposalData}
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={4} title="Alternativen + Nächste Schritte + FAQ" />
              <div className="page-gap" />
              <ContentPage4Static
                data={freiflaecheProposalData}
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div style={{ height: '48px' }} data-print-hidden="true" />
            </div>
          </div>
        ) : isSpeicherTemplateView(activeView) ? (
          <div className="proposal-staging" data-proposal-canvas="true" onMouseDown={handleLayoutCanvasMouseDown}>
            {renderOfferLayoutScrollPanel()}
            <div className="proposal-pages" ref={proposalRef} data-testid="proposal-canvas-sp">
              {isLayoutDragMode && (
                <div className="spacing-guide-overlay" data-print-hidden="true">
                  {spacingGridSize > 0 && (
                    <div
                      className="spacing-guide-grid"
                      style={{ backgroundSize: `100% ${spacingGridSize}px` }}
                    />
                  )}

                  {showSpacingGuides && activeSpacingFeedback?.guides?.map((guide) => (
                    <div
                      key={guide.key}
                      className="spacing-guide-connector"
                      style={{
                        top: `${guide.top}px`,
                        left: `${guide.x}px`,
                        height: `${guide.height}px`,
                        color: guide.color,
                      }}
                    >
                      <div className="spacing-guide-cap" style={{ top: 0 }} />
                      <div className="spacing-guide-cap" style={{ bottom: 0 }} />
                      <div className="spacing-guide-label">
                        {guide.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <PageLabel num={1} title={speicherPageLabels.page1} />
              <div className="page-gap" />
              <CoverPageSpeicher
                data={effectiveSpeicherProposalData}
                editMode={isLayoutDragMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={2} title={speicherPageLabels.page2} />
              <div className="page-gap" />
              <ContentPage2Speicher
                data={effectiveSpeicherProposalData}
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={3} title={speicherPageLabels.page3} />
              <div className="page-gap" />
              <ContentPage3Speicher
                data={effectiveSpeicherProposalData}
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={4} title={speicherPageLabels.page4} />
              <div className="page-gap" />
              <ContentPage4Speicher
                data={effectiveSpeicherProposalData}
                editMode={isLayoutDragMode}
                textEditMode={isTextEditMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
              />

              <div style={{ height: '48px' }} data-print-hidden="true" />
            </div>
          </div>
        ) : isExposeTemplateView ? (
          <div className="proposal-staging" data-proposal-canvas="true" onMouseDown={handleLayoutCanvasMouseDown}>
            {renderOfferLayoutScrollPanel()}
            <div className="proposal-pages" ref={proposalRef} data-testid={activeView === 'expose-turnkey' ? 'proposal-canvas-expose-turnkey' : 'proposal-canvas-expose-projektverkauf'}>
              {isLayoutDragMode && (
                <div className="spacing-guide-overlay" data-print-hidden="true">
                  {spacingGridSize > 0 && (
                    <div
                      className="spacing-guide-grid"
                      style={{ backgroundSize: `100% ${spacingGridSize}px` }}
                    />
                  )}

                  {showSpacingGuides && activeSpacingFeedback?.guides?.map((guide) => (
                    <div
                      key={guide.key}
                      className="spacing-guide-connector"
                      style={{
                        top: `${guide.top}px`,
                        left: `${guide.x}px`,
                        height: `${guide.height}px`,
                        color: guide.color,
                      }}
                    >
                      <div className="spacing-guide-cap" style={{ top: 0 }} />
                      <div className="spacing-guide-cap" style={{ bottom: 0 }} />
                      <div className="spacing-guide-label">
                        {guide.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <PageLabel num={1} title={activeView === 'expose-turnkey' ? 'Deckblatt Turnkey' : 'Deckblatt Projektrechte'} />
              <div className="page-gap" />
              <ProjectExposeCover
                data={exposeProposalData}
                variant={activeView}
                editMode={isLayoutDragMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
                inlineEditMode={isTextEditMode}
                onFieldChange={handleExposeInlineFieldChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={2} title="Wirtschaft + Standort + Netzanschluss" />
              <div className="page-gap" />
              <ProjectExposePage2
                data={exposeProposalData}
                variant={activeView}
                editMode={isLayoutDragMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
                inlineEditMode={isTextEditMode}
                onFieldChange={handleExposeInlineFieldChange}
              />

              <div className="page-gap" style={{ height: '32px' }} />
              <PageLabel num={3} title="PVSol + Prüfungen + Lageplan" />
              <div className="page-gap" />
              <ProjectExposePage3
                data={exposeProposalData}
                variant={activeView}
                editMode={isLayoutDragMode}
                showMarkers={showElementOutlines && isLayoutDragMode}
                selectedElements={selectedLayoutElements}
                elementPositions={resolvedDachLayoutPositions}
                onSelectElement={handleLayoutElementSelect}
                onPositionChange={handleLayoutPositionChange}
                inlineEditMode={isTextEditMode}
                onFieldChange={handleExposeInlineFieldChange}
              />

              {activeView === 'expose-turnkey' ? (
                <>
                  <div className="page-gap" style={{ height: '32px' }} />
                  <PageLabel num={4} title="Ertragsprognose + Cashflow + OPEX" />
                  <div className="page-gap" />
                  <ProjectExposePage4
                    data={exposeProposalData}
                    variant={activeView}
                    editMode={isLayoutDragMode}
                    showMarkers={showElementOutlines && isLayoutDragMode}
                    selectedElements={selectedLayoutElements}
                    elementPositions={resolvedDachLayoutPositions}
                    onSelectElement={handleLayoutElementSelect}
                    onPositionChange={handleLayoutPositionChange}
                    inlineEditMode={isTextEditMode}
                    onFieldChange={handleExposeInlineFieldChange}
                  />
                </>
              ) : null}

              <div style={{ height: '48px' }} data-print-hidden="true" />
            </div>
          </div>
        ) : activeView === 'vertrieb' ? (
          <div className="proposal-staging" data-proposal-canvas="true">
            {renderOfferLayoutScrollPanel()}
            <div className="proposal-pages" ref={proposalRef} data-testid="proposal-canvas-vertrieb">
              <VertriebsPraesentation readOnly={!canEditActiveTool} />
              <div style={{ height: '48px' }} data-print-hidden="true" />
            </div>
          </div>
        ) : activeView === 'flyer' ? (
          <EnergyFlyerStudio pagesRef={proposalRef} canEdit={canEditActiveTool} />
        ) : activeView === 'ffpv-flyer' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC' }}>
            <FfpvFlyerBoard apiBase={`${LAYOUT_API}/contracts/usage`} session={session} canEdit={canEditActiveTool} />
          </div>
        ) : activeView === 'ffpv-teaser' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC' }}>
            <FfpvTeaserBoard
              apiBase={`${LAYOUT_API}/contracts/usage`}
              session={session}
              canCreate={canCreateActiveTool}
              canEdit={canEditActiveTool}
            />
          </div>
        ) : activeView === 'vermittlervertrag' ? (
          <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
            <VermittlervertragStudio apiBase={LAYOUT_API} session={session} toolAccess={activeToolAccess} />
          </div>
        ) : activeView === 'vertraege' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC' }}>
            <VertragsStudio apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'exposes-automation' ? (
          <ExposeAutomationStudio
            apiBase={LAYOUT_API}
            session={session}
            toolAccess={activeToolAccess}
            onOpenInTemplateEditor={handleOpenExposeInTemplateEditor}
          />
        ) : activeView === 'exposes-cockpit' ? (
          <CockpitExposeList apiBase={LAYOUT_API} session={session} />
        ) : activeView === 'vertraege-am' ? (
          <div className="vertraege-studio-rahmen" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC', overflow: 'hidden' }}>
            <VertragsAutomationStudio
              apiBase={LAYOUT_API}
              session={session}
              initialDealId={selectedContractsDealId}
              initialContractKey={selectedContractsContractKey}
              onManualUploadQueued={handleContractManualUploadQueued}
            />
          </div>
        ) : activeView === 'vertraege-am-bearbeiten' ? (
          <div className="vertraege-studio-rahmen" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC', overflow: 'hidden' }}>
            <VertragsAutomationStudio
              apiBase={LAYOUT_API}
              session={session}
              mode="edit-existing"
              onManualUploadQueued={handleContractManualUploadQueued}
            />
          </div>
        ) : activeView === 'vertraege-am-abwaegen' ? (
          // Eigener Bereich seit 23.09.2026 - vorher ein Reiter im Gespraechs-Panel.
          // Der Rahmen scrollt nicht selbst; die beiden Spalten darin tun es.
          <div className="abwaegen-rahmen" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <AbwaegenStudio apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'vertraege-am-dokumenten-assistent' ? (
          // Seit 21.10.2026 - ersetzt das eigene Tool "Bitrix Doku Assistant".
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC', overflowY: 'auto' }}>
            <DokumentenAssistent apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'vertraege-am-pending' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC' }}>
            <VertragsAmPendingBoard apiBase={LAYOUT_API} session={session} onSelectDeal={handleSelectContractPendingDeal} />
          </div>
        ) : activeView === 'vertraege-am-automatisierungen' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC' }}>
            <VertragsAutomationsPage apiBase={LAYOUT_API} session={session} />
          </div>
        ) : activeView === 'vertraege-am-klauseln' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#F8FAFC', overflowY: 'auto' }}>
            <ClauseManager apiBase={LAYOUT_API} session={session} />
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
            <DesignSystemPage />
          </div>
        )}
      </div>
      {renderAndroidBottomNavigation()}
    </div>
  );
}

const PageLabel = ({ num, title }) => (
  <div className="page-indicator" data-print-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{
      fontFamily: 'Outfit, sans-serif', fontSize: '10px', fontWeight: 700, color: '#94A3B8',
      background: 'rgba(255,255,255,0.7)', padding: '3px 10px', borderRadius: '99px',
      border: '1px solid rgba(203, 213, 225, 0.8)', backdropFilter: 'blur(4px)',
    }}>
      Seite {num} &bull; {title}
    </span>
  </div>
);

export default App;
